import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import ChatFrame from './chat-frame';
import KnowFrame from './know-frame';
import HomeView from './home-view';
import ChatFloatingButton from './chat-floating-button';
import ChatTeaserBubble from './chat-teaser-bubble';
import { openLink as bridgeOpenLink } from './native-bridge.js';
import LauncherLabel from './launcher-label';
import QuickButtons from './quick-buttons';
import offlineStatusLine from './offline-status';
import {
    labelStorageKey,
    quickButtonsDismissKey,
    resolveLabelSession,
    shouldRenderLabel,
    labelLines,
    visibleQuickButtons,
    resolveServiceTitle,
} from './launcher-engagement';
import ChatTitleMsg from './chat-title-msg';
import ArrowIcon from './arrow-icon';
import LcIcon from './lc-icon.js';
import ApiService from '../services/api';
import { t, getLang, setLang, ensureLocale, onLocaleLoaded, SUPPORTED_LANGS, LANG_NATIVE_NAMES } from '../i18n';
import { flagInnerHtml, FLAG_VIEWBOX } from '../i18n/flags';
import { widgetConfigToThemeUpdate, computeEarlyThemeUpdate, resolveHeaderGradient, colorToRgbTriplet, resolveHomeViewAction, resolveFirstVisitLandingView, resolveAutoOpen, isExplicitlySet, resolveColorKnown, neutralLauncherColor, parseCachedAppConfig, WIDGET_CONFIG_CACHE_SCHEMA_VERSION, resolveInlineMinHeightFallback, resolveInitErrorKind, resolveThemeMode, resolveEffectiveTheme, resolveDarkPalette, darkPaletteToCssVars, lightPaletteToCssVars, shadowTokensToCssVars } from './default-configuration';
import { NAV_HOME, NAV_CHAT, NAV_CHAT_LIST, NAV_KNOWLEDGE, navInit, navGo, navBack, navBackTarget, navSwitchTab, cornerButtonTarget } from './chat-navigation';
import nativeBridge, { hasNativeTransport } from './native-bridge';

// 这些HTML函数已不再需要 - 现在直接渲染React组件
import {
    chatHeaderStyle,
    chatHeaderSubStyle,
    chatHeaderBeforeStyle,
    getHeaderPatternStyle,
    homeHeroBackgroundStyle,
    chatBackStyle,
    chatSmallStyle,
    chatIconStyle,
    chatIconSvgStyle,
    closeButtonStyle,
    serviceTitleStyle,
    guideTextStyle,
    serviceInfoWrapperStyle,
    fabCloseButtonStyle,
    chatListButtonStyle,

    desktopTitleStyle,
    desktopWrapperStyle,
    mobileOpenWrapperStyle,
    mobileClosedWrapperStyle,
    desktopClosedWrapperStyleChat,
    withSafeAreaTop,
    SAFE_AREA_BOTTOM,
} from "./style";
import { debugLog } from '../debug';
let chatSrc = '';
let knowSrc = '';

// widgetClass ↔ 导航状态机词汇表的翻译（chat-navigation.js 用的是三端共用的
// 'home'|'chat'|'chatList'|'knowledge'，widget.js 内部沿用历史上的 *-widget
// 字符串）。'chatList' 是 chat-widget 的子视图，回到 widgetClass 时它和 'chat'
// 一样落在 chat-widget —— 子视图由 chat.js 的 viewMode 承载。
function navViewOfWidgetClass(widgetClass) {
    if (widgetClass === 'home-widget') return NAV_HOME;
    if (widgetClass === 'know-widget') return NAV_KNOWLEDGE;
    return NAV_CHAT;
}


export default class Widget extends Component {
    // appConfig 本地缓存的 key——回访场景"彻底消灭等待窗口"（见构造函数里的
    // 读取 + preInitClient 里的写入）。schema 版本校验/形状解析是纯函数
    // parseCachedAppConfig（theme-color.js，node --test 直接覆盖），这里只做
    // "读 localStorage + 交给它判断"这层不值得单测的胶水代码。
    static _widgetConfigCacheKey(appId) { return `lane_widget_config_${appId}`; }

    // 同步读本地缓存的完整 appConfig（构造函数专用——渲染首帧之前必须拿到手，
    // 不能等任何 Promise）。读不出来/格式不对/版本不match 一律当没缓存，不能
    // 让脏缓存打断首绘（宁可回退到"等服务端"这条本来就有的路径）。
    static _readCachedAppConfig(appId) {
        if (!appId) return null;
        try {
            return parseCachedAppConfig(localStorage.getItem(Widget._widgetConfigCacheKey(appId)));
        } catch (e) { return null; }
    }

    constructor(props) {
        super(props);
        // inline 模式下不存在关闭态 —— chat 视图一直展开
        const startOpen = props.conf.mode === 'inline' ? true : false;

        // ---------- 品牌色结构性闸门：读缓存（回访场景彻底消灭等待窗口） ----------
        // 上次会话成功拿到过这个 app_id 的完整 appConfig（不只是 widget_config
        // 那一小块——welcome_message/service_title 这些非颜色字段也在，"彻底
        // 消灭"不能只做到"颜色对了、内容区还在转"）就直接拿来当首帧的"已确认"
        // 配置用——不是"猜"，是"服务端上次已经明确告诉过我们"，语义上跟
        // resolveColorKnown 的第 3 条来源（本次请求刚确认）完全等价，只是时间点
        // 提前到了构造函数。颜色/暗色部分仍然用同一个 computeEarlyThemeUpdate
        // （跟 preInitClient 早期上色路径共用），保证缓存路径和实时路径对
        // widget_config 的解读（含 dark_mode_enabled/theme_mode——暗色引擎读的
        // 也是这同一份 WIDGET_CONFIG_THEME_KEY_MAP）完全一致，不会出现"亮暗判定
        // 与配色分家"。
        const cachedAppConfig = Widget._readCachedAppConfig(props.conf.app_id);
        const cachedThemeUpdate = (cachedAppConfig && cachedAppConfig.widget_config)
            ? computeEarlyThemeUpdate(cachedAppConfig, window.laneChatCustomizations, props.conf.mainColor)
            : null;
        const hasExplicitMainColor = isExplicitlySet(window.laneChatCustomizations, 'mainColor');
        // 初始 colorKnown：唯一决策出口 resolveColorKnown（theme-color.js）。
        // 第三个来源（服务端本次请求确认）此刻显然还没发生，false——preInitClient
        // 成功后会 setState 把它翻成 true，见那边注释。
        this._initialColorKnown = resolveColorKnown({
            hasExplicitCustomization: hasExplicitMainColor,
            hasCachedConfig: !!cachedThemeUpdate,
            serverConfirmed: false,
        });
        // 缓存命中就让 configReady 也从第一帧开始为真——这是"彻底消灭回访场景
        // 等待窗口"的关键一步：光有 colorKnown=true 还不够，render() 的两个渲染
        // 入口要求 configReady && colorKnown 同时为真才画真内容，只翻一个的话
        // 回访用户看到的还是"骨架转到 /usage/start 真正返回"，等待窗口只是变窄
        // 没有消灭。真实的 /usage/start 请求仍然照常发出（loadAppConfig 不因为
        // 缓存而跳过），到达后走一遍现有的 applyConfig()，缓存值和真实值不一样
        // 就自然热更新——这本来就是配置在会话中途变化时既有的行为，不是新逻辑。
        this._initialConfigReady = !!cachedThemeUpdate;

        // headerGradient 首屏就要按派生规则算好，不能直接照抄 props.conf——
        // defaultConfiguration 已经不再给 headerGradient 写死值（见
        // default-configuration.js 顶部注释），conf.headerGradient 这时候要么是
        // customizations 显式传的，要么压根没有这个 key。没有就从 mainColor 派生
        // （fix round 1：header 渐变之前完全不跟 mainColor 走，商户配了绿主色，
        // header 还是出厂蓝渐变）。缓存命中时 cachedThemeUpdate 里可能已经带了
        // 真实 mainColor，派生要用那个而不是 props.conf.mainColor（出厂默认）。
        const initialTheme = {
            ...props.conf,
            ...(cachedThemeUpdate || {}),
            headerGradient: resolveHeaderGradient(
                (cachedThemeUpdate && cachedThemeUpdate.mainColor) || props.conf.mainColor,
                cachedAppConfig && cachedAppConfig.widget_config,
                window.laneChatCustomizations
            ),
        };
        // 首次访问落地视图（home_page_enabled=true 时可配置首次落地 Home 还是
        // Chat；回访且有对话的访客永远直接进上次对话，不受这个配置影响）。
        //
        // "有没有既有对话"必须在这里、构造函数里、同步判定完——不能等 /v1/chat
        // 响应回来再决定：那时候首帧早就已经提交过一次了（React 首次渲染就是
        // 用构造函数这份 state），等数据决定就必然先画 chat 再跳 home，用户能
        // 看到那一下闪变。这里用的是跟 chat.js _getCachedChatId 完全同一把
        // localStorage 键（`lane_chatid_${appId}`）——同步、零网络请求、"这个
        // 浏览器有没有缓存过一个 chat_id"本身就是"是不是真的首次访问"的可靠
        // 代理信号（不完美：清过 localStorage 的老访客会被误判成首次访问，
        // 但这正是坐标器要求的"别新发明网络请求/信号"这条约束下能做到的最好
        // 判定，且后果只是多看一次 Home 落地页，不是数据错误）。
        //
        // homePageEnabled/initialView 这两个字段本身是服务端配置，构造函数这
        // 一刻大概率还不知道（customizations 不会有人塞这个）——resolveFirstVisitLandingView
        // 在"两者都不知道"时天然落 chat-widget（!homePageEnabled 分支），跟改动前
        // 的默认值逐字节一致，不构成回归。等 /v1/chat 早期数据到达
        // （_applyPendingEarlyTheme）或 /usage/start 全量配置到达（applyConfig）
        // 时，同一个纯函数会用真实配置重新判一次，必要时把 widgetClass 从这里
        // 的临时值升级成 home-widget——这一步永远发生在骨架切换成真实内容之前
        // （configReady 门控），用户不会看到这次升级本身。
        this._explicitViewTargeted = false; // navigate-to-chat/native navigate/teaser 点击等显式意图 → true，见 switchToChat/switchToHome/navigateHome
        // chat frame 当前在哪个子视图：'list' | 'conversation'。左上角按钮靠它
        // 区分顶层与下钻（见 _inConversation）。初值 conversation：不带参数打开
        // 聊天落的就是会话，列表要显式要求（switchToChat({subview:'list'})）。
        this._chatSubview = 'conversation';
        const hasCachedChat = this._hasCachedInitialChat(props);
        const initialWidgetClass = resolveFirstVisitLandingView({
            // ?view=chat&chat_id=N 的 chat_id 一半，或 ?view=chat 本身不带
            // chat_id（forceChatView，widget-index.js 读 window.laneChatInitialView
            // 传下来的）——两者任一存在都是"显式要落地聊天视图"的意图。
            hasExplicitChatTarget: !!(props.initialChatId || props.forceChatView),
            hasCachedChat,
            homePageEnabled: initialTheme.homePageEnabled,
            initialView: initialTheme.initialView,
        });
        this.state = {
            isChatOpen: startOpen,
            pristine: true,
            wasChatOpened: this.wasChatOpened(),
            widgetClass: initialWidgetClass, // 'home-widget' | 'chat-widget' | 'know-widget'
            // 返回导航状态（chat-navigation.js 状态机）。之后每一次
            // switchToHome/switchToChat/switchToKnowledge 都把来路压进去。
            //
            // 落地在非 Home 视图时，若商户开了 Home，要把它预置成栈底：
            // Home 启用即是导航树的根，无论访客这次有没有实际经过它。否则
            // "开了 Home 但这次直接落进会话（回访续聊 / initial_view=chat）"
            // 按返回会直接退出，而 Home 明明在那儿。判据复用 resolveHomeViewAction，
            // 不新造一套。
            nav: navInit(
                navViewOfWidgetClass(initialWidgetClass),
                resolveHomeViewAction(initialTheme.homePageEnabled) === 'home'
            ),
            wrapperWidth: {width: props.conf.desktopWidth},
            theme: initialTheme, // 添加主题状态
            chatInitialized: false, // 标记聊天组件是否已初始化
            knowInitialized: false, // 标记知识库组件是否已初始化
            homeInitialized: initialWidgetClass === 'home-widget', // 标记Home页面是否已初始化
            showGame: false, // 是否显示游戏
            gameUrl: null, // 游戏URL
            currentMode: 'chat', // 当前模式：'chat' 或 'know'
            appConfig: cachedAppConfig || null, // 应用配置——缓存命中时直接种上次的值，见构造函数注释
            configReady: this._initialConfigReady, // 配置是否已加载，防止样式抖动——缓存命中即真
            // /v1/chat 初始化失败时的错误页种类：null(无错误) |
            // 'app_unavailable'(app_id 无效/缺失，不可重试) | 'network'(连接层
            // 失败，可重试) | 'server'(服务端 5xx，可重试)。由 preInitClient 的
            // 成功/失败分支统一通过 resolveInitErrorKind 判定，见那边注释。
            initErrorKind: null,
            // 品牌色结构性闸门——唯一决策出口 resolveColorKnown（theme-color.js
            // 顶部有完整不变式说明 + 退役 resolveFabRenderGate 的根因分析）。
            // render() 的两个渲染入口（inline 分支、浮动分支）各查一次这一个
            // 状态，不是分散在 FAB/header/teaser 各处各自判断——新增的品牌色
            // 渲染点天然在保护范围内。初始值来自构造函数（显式 customizations
            // 或本地缓存命中都能让它从第一帧就是 true，见构造函数注释）；
            // 唯一另外会翻真的地方是 preInitClient 成功之后（服务端本次确认）。
            // 故意没有"客户端超时就当已解决"这条路径——那条路径正是被退役的
            // resolveFabRenderGate 里 fallbackFired 的根因，超时不是服务端确认，
            // 真正网络broken 由 initErrorKind/renderInitError 接管（渲染错误页，
            // 不渲染任何品牌色 UI），不需要靠"超时就画默认色"绕过骨架。
            colorKnown: this._initialColorKnown,
            headerHovered: false, // big header hover state
            headerHoveredAgent: null, // 当前 hover 的客服头像 index（用于显示 tooltip）
            teaserVisible: false,   // bubble teaser 当前可见性
            teaserDismissed: false, // 本 mount 内是否已被用户关闭
            teaserPayload: null,    // 服务端 Flow 推送的 teaser（优先级高于 theme.teaserMessage 配置）
            labelDone: false,       // launcher label collapsed for this session
            labelClosing: false,    // fade-out in progress
            quickButtonsDismissed: this._readQuickButtonsDismissed(),
            unreadBadgeCount: 0,    // 浮球未读徽标数字（面板关着时收到消息才累加）
            launcherHidden: false,  // laneChat.hide()/show() 控制浮球显隐，不影响已打开面板
            isClosing: false,       // 关闭出场动画进行中（动画结束才真正翻 isChatOpen）
            viewAnim: null,         // 视图切换动画 { view, dir: 'Push'|'Pop', nonce }
            headerPopover: null,    // 头部弹层：null | 'lang' | 'settings'
            headerPopoverPos: null, // 弹层相对面板的锚定位置 {top,right}（量自触发按钮）
            soundEnabled: this._readSoundPref(), // 新消息提示音开关（localStorage 持久）
            langNonce: 0,           // 语言切换后自增，触发整树重渲染让 t() 取新语言
            vvRect: null,           // 宿主页横向溢出时的 visual viewport 锚定矩形
            // 软键盘弹起时的可视区矩形 { top, height }。null = 无需介入。
            // 移动端打开态的面板直接钉在它上面，见 _updateKeyboardViewport。
            kbRect: null,
            // prefers-color-scheme 当前值（auto 模式的输入）。componentDidMount 挂
            // matchMedia change 监听实时跟随系统切换。
            prefersDark: this._prefersDarkNow(),
        };
        // widget.html 启动骨架暗色门的另一半：上次会话缓存的「这个 app 开没开暗色」。
        // 服务端配置到达前（theme.darkModeEnabled === undefined）用它兜底判定，
        // 避免开了暗色的 app 每次冷启动都先闪一屏亮色。存量/未开启商户永远是
        // '0'/缺失 → 恒亮色，暗色逻辑一丝不生效。
        this._cachedDarkOk = false;
        try {
            this._cachedDarkOk = localStorage.getItem(`lane_dark_ok_${props.conf.app_id}`) === '1';
        } catch (e) { /* ignore */ }
        chatSrc = this.props.iFrameSrc;
        knowSrc = this.props.iFrameSrc2;
        // 平板"闪闭"根因追踪用：FAB 触屏 touchend 打开面板的时间戳，见 onClickBtn/
        // onClickClose 上的详细注释。
        this._lastFabTouchOpenAt = 0;
        this._isMounted = false
        this.chatFrameRef = React.createRef();
        this.knowFrameRef = React.createRef();

        // 记录页面加载时间
        this.pageLoadTime = Date.now();
        this.visitorId = null;
        this.sessionId = null;
        this.endBeaconSent = false; // 防止重复发送

        if (props.conf.autoChatOpen) {
            this.onClick();
        }
    }

    // 构建允许的消息来源 origin 白名单
    //   - 空配置时：只允许同源（最小安全默认）
    //   - window.laneChatAllowedOrigins 可扩展额外 origin
    //   - window.laneChatServerUrl 也会被解析为 origin 并加入白名单（iframe 服务端来源）
    getAllowedOrigins = () => {
        const origins = new Set([window.location.origin]);
        if (Array.isArray(window.laneChatAllowedOrigins)) {
            window.laneChatAllowedOrigins.forEach(o => { if (o) origins.add(o); });
        }
        if (window.laneChatServerUrl) {
            try { origins.add(new URL(window.laneChatServerUrl).origin); } catch (e) { /* ignore */ }
        }
        // iframeSrc / iframeSrc2 若存在，其 origin 也是合法来源
        [this.props.iFrameSrc, this.props.iFrameSrc2].forEach(src => {
            if (!src) return;
            try { origins.add(new URL(src, window.location.href).origin); } catch (e) { /* ignore */ }
        });
        return origins;
    }

    handleMessage = (event) => {
        // 校验消息来源 origin —— 只接受白名单内的 origin
        // 注：同源 postMessage（e.g. forwardMessageToChat 自己发给自己）天然通过
        const allowed = this.getAllowedOrigins();
        if (!allowed.has(event.origin)) {
            // 静默丢弃不可信来源的消息
            return;
        }

        if (event.data && event.data.type === 'appConfigLoaded') {
            debugLog('Widget received appConfig:', event.data.appConfig);
            if (this._isMounted) {
                this.setState({ appConfig: event.data.appConfig });
            }
        }

        if (event.data && event.data.type === 'open_game') {
            this.setState({
                showGame: true,
                gameUrl: event.data.url
            });
        }

        // Flow-pushed teaser relayed up from the chat iframe (real-time WS push,
        // replaces the old short-poll). presentTeaser self-guards against panel
        // already open / dismissed, so this is safe to call whenever it lands.
        if (event.data && event.data.type === 'lane_teaser' && event.data.teaser) {
            this.presentTeaser(event.data.teaser);
        }

        // Server told us whether this registration actually fired a first_visit flow.
        // If it didn't (returning visitor / already fired), no teaser can ever be
        // produced for this session — stop polling instead of burning 2-3 requests
        // on a guaranteed-empty endpoint.
        if (event.data && event.data.type === 'lane_first_visit' && event.data.fired === false) {
            this._teaserPollDone = true;
            if (this._teaserPollTimer) { clearTimeout(this._teaserPollTimer); this._teaserPollTimer = null; }
        }

        // 面板关着的时候来了新消息(客服回复 / flow 主动消息)→ 用 teaser 那套气泡弹出来
        // 预览一下,不然访客根本不知道有人回了他。面板开着就什么都不做(消息本来就看得见)。
        if (event.data && event.data.type === 'lane_msg_preview') {
            this.presentMessagePreview(event.data);
        }

        // 总线事件转发（laneChat.on(...)）—— chat.js 的 _postToWidget 是唯一来源。
        // 面板关着才累加未读徽标；message_received/message_sent/session_loaded
        // 三个事件本身不受开关状态门控，订阅者自己决定要不要用。
        if (event.data && event.data.type === 'lane_msg_received') {
            if (!this.state.isChatOpen) {
                this._bumpUnread();
                // 面板关着时来消息响一声提示音（设置弹层可关，localStorage 持久）
                if (this.state.soundEnabled) this._playNotifySound();
            }
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit('message_received', { type: event.data.message_type, excerpt: event.data.excerpt || '', is_ai: !!event.data.is_ai });
            }
        }
        if (event.data && event.data.type === 'lane_msg_sent') {
            // 访客发过消息 = 已互动，本 session 不再弹营销 teaser
            this._markTeaserSessionDone();
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit('message_sent', { type: event.data.message_type, excerpt: event.data.excerpt || '' });
            }
        }
        if (event.data && event.data.type === 'lane_session_loaded') {
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit('session_loaded', { visitor_uuid: event.data.visitor_uuid });
            }
        }

        // 处理来自 Service Worker 的导航消息。
        //
        // `!__lcForwarded` 是防自激的闸：forwardMessageToChat 把这条消息**原样
        // 重新 postMessage 到同一个 window**（chat.js 的消费者也挂在 window 上），
        // 而 widget 自己的监听器同样收得到它 —— 于是面板已经在会话视图时会命中
        // 下面第三条分支，再 forward 一次，再收到，再 forward…… 实测一次推送点击
        // 1.5 秒内自激出 6 万条消息、主线程被打满。转发出去的那一份带上标记，
        // widget 侧直接跳过（chat.js 不看这个键，转发依旧到达）。
        if (event.data && event.data.type === 'navigate-to-chat' && !event.data.__lcForwarded) {
            debugLog('[Widget] Received navigate-to-chat message:', event.data);
            // 推送点击是明确的"打开这条会话"意图，跟深链/teaser 一样必须盖过
            // 首次访问的 Home 默认落地——见 _maybeAutoLandOnFirstVisit。
            this._explicitViewTargeted = true;

            // 如果聊天没有打开，打开它
            if (!this.state.isChatOpen) {
                this.setState({
                    pristine: false,
                    isChatOpen: true,
                    widgetClass: 'chat-widget',
                    chatInitialized: true,
                    // 推送点击是"落地"而不是"从某个视图导航过来"（navInit 而不是
                    // navGo）——但开了 Home 的租户仍以 Home 为根，返回回得去它。
                    nav: navInit(
                        NAV_CHAT,
                        resolveHomeViewAction(this.state.theme.homePageEnabled) === 'home'
                    ),
                }, () => {
                    // 等待iframe加载后转发消息
                    setTimeout(() => {
                        this.forwardMessageToChat(event.data);
                    }, 1000);
                });
            } else if (this.state.widgetClass !== 'chat-widget') {
                // 如果打开的是知识库，切换到聊天。
                // 与上面「面板还没开」那条分支同一口径：推送点击是"落地"，但开了
                // Home 的租户仍以 Home 为根，第二个参数漏掉会让 homeEnabled 变
                // undefined → 栈空 → 返回按钮整颗不渲染、返回等于关闭，正是
                // navInit 两参化要修的那个问题。
                this.setState({
                    widgetClass: 'chat-widget',
                    chatInitialized: true,
                    nav: navInit(
                        NAV_CHAT,
                        resolveHomeViewAction(this.state.theme.homePageEnabled) === 'home'
                    ),
                }, () => {
                    // 转发消息给聊天iframe
                    this.forwardMessageToChat(event.data);
                });
            } else {
                // 直接转发消息给聊天iframe
                this.forwardMessageToChat(event.data);
            }
        }
    }

    // ---------- mainColor → --widget-main-color / --widget-main-rgb ----------
    // 发送键、输入框 focus 环、若干高亮态背景全是靠这两个 CSS 自定义属性驱动
    // （var(--widget-main-color, #2563eb)，见 chat-frame.js / widget-index.js 里
    // 一大片 .send-btn / textarea:focus / rgba(var(--widget-main-rgb...)) 规则）。
    // fix round 1 之前，这两个变量只在 widget-index.js mount 时用当时的
    // conf.mainColor（customizations 那一份，server 配置还没到）写死注入 shadow
    // host 一次，之后 React state 里 theme.mainColor 再怎么变，这两个变量纹丝不动
    // ——于是 avatar 圆点（直接读 theme.mainColor 的内联 style）能变绿，发送键/
    // focus 环（读这两个变量）永远蓝。这里把它们改成随 theme.mainColor 变化同步
    // 写回 shadow host 的内联样式（内联样式可以随时更新，:host{} 规则的静态
    // <style> 文本不行）。shadowHostEl 由 widget-index.js 传入，是 attachShadow()
    // 的宿主元素本身——在它上面设置自定义属性，会像任何祖先元素的自定义属性一样
    // 穿过 shadow 边界被内部这些 var() 引用继承到。
    _syncThemeCssVars = () => {
        const el = this.props.shadowHostEl;
        const theme = this._displayTheme();
        if (!el || !el.style || !theme) return;
        const mc = theme.mainColor || '#2563eb';
        try {
            el.style.setProperty('--widget-main-color', mc);
            // colorToRgbTriplet（不是只认六位 hex 的 hexToRgbTriplet）：主色可以带
            // 透明度了，六位 hex 之外还有 rgba()/8 位 hex/三位简写。解析不出来时
            // 不写这个变量，让 CSS 侧 var(--widget-main-rgb, 37, 99, 235) 兜底 ——
            // 但**别忘了清掉上一次的值**，否则换了个解析不了的颜色会留着旧色相。
            const rgb = colorToRgbTriplet(mc);
            if (rgb) el.style.setProperty('--widget-main-rgb', rgb);
            else el.style.removeProperty('--widget-main-rgb');

            // ---------- 语义主题变量（--lc-*） ----------
            // 亮暗**都恒定义**全套 --lc-*（T1 归一，spec §5.2）：亮色写
            // LIGHT_PALETTE 钦定表（此前是全部 removeProperty、靠各调用点互不
            // 相同的 fallback——那意味着亮色没有统一语义色板，原生端无从对齐），
            // 暗色写 darkPaletteToCssVars。shadow host 上的自定义属性会像任何
            // 祖先属性一样穿过 shadow 边界被内部继承，emoji portal 也在覆盖内。
            // 两表键集合恒同构（单测锁定），diff 清理循环保留以防御未来键集变化。
            const dark = !!theme.__dark;
            const nextVars = dark && theme.__darkPalette
                ? darkPaletteToCssVars(theme.__darkPalette)
                : lightPaletteToCssVars();
            const prevVars = this._appliedDarkVars || null;
            if (prevVars) {
                for (const k of Object.keys(prevVars)) {
                    if (!nextVars || !(k in nextVars)) el.style.removeProperty(k);
                }
            }
            if (nextVars) {
                for (const k of Object.keys(nextVars)) el.style.setProperty(k, nextVars[k]);
            }
            this._appliedDarkVars = nextVars;

            // ---------- 结构层：阴影档随主题换色（几何不变） ----------
            // 静态 <style>（widget-index.js :host）已给出亮色默认，首帧不等配置。
            // 这里只在主题解析后覆写 3 个阴影变量 —— 圆角/间距/动效不随主题变，
            // 一个字节都不用重写（结构层与主题层正交，spec §0）。
            const shadowVars = shadowTokensToCssVars(dark);
            for (const k of Object.keys(shadowVars)) el.style.setProperty(k, shadowVars[k]);
            // UA 侧配色（原生滚动条/表单控件/自动填充底）跟随
            el.style.colorScheme = dark ? 'dark' : '';

            // widget.html 宿主页（#chatly-container 是它独有的挂载点 id）：主题
            // 一旦解析完成，就由 widget 接管 <html data-lc-theme>——boot script 只在
            // "缓存说这个 app 开过暗色"时预设它（避免暗色用户冷启动闪白），这里
            // 是权威收敛点：解析成亮色就摘掉（缓存过期/商户关了暗色时自愈），
            // 解析成暗色就补上（首次开暗色时 centered 背板/容器底也跟上）。
            try {
                if (typeof document !== 'undefined' && document.getElementById('chatly-container')) {
                    if (dark) document.documentElement.setAttribute('data-lc-theme', 'dark');
                    else document.documentElement.removeAttribute('data-lc-theme');
                }
            } catch (e) { /* ignore */ }

            // widget.html 启动骨架暗色门的写侧：配置已确认后记住「这个 app 的
            // dark_mode_enabled」，下次冷启动 boot script 才知道能不能立刻画暗骨架。
            // 只在服务端明确说过话（darkModeEnabled !== undefined）后写，避免把
            // 缓存值用默认 false 冲掉。
            if (this.state.theme && this.state.theme.darkModeEnabled !== undefined) {
                try {
                    const appId = this.props.conf.app_id;
                    if (appId) localStorage.setItem(`lane_dark_ok_${appId}`, this.state.theme.darkModeEnabled ? '1' : '0');
                } catch (e) { /* ignore */ }
            }
            // 同一道门的第二个输入:商户在 dash 配的 theme_mode。
            // boot script 原先只认 URL 上的 ?theme= —— 而原生 SDK 默认不传它,
            // jssdk 集成方也很少传,于是"dash 里设了 dark"的商户每次冷启动都先
            // 白一屏再跳暗。dark_mode_enabled 早就缓存了,唯独这个模式没有,门
            // 就永远开不了。写法与上面同一条纪律:服务端明确说过才写,不写默认值。
            if (this.state.theme && this.state.theme.themeMode !== undefined) {
                try {
                    const appId = this.props.conf.app_id;
                    if (appId) localStorage.setItem(`lane_theme_mode_${appId}`, String(this.state.theme.themeMode));
                } catch (e) { /* ignore */ }
            }
        } catch (e) { /* 宿主异常（已卸载等）不影响其它渲染 */ }
    }

    // ---------- 暗色主题引擎（widget 侧消费层；算法真源在 theme-color.js） ----------

    _prefersDarkNow = () => {
        try {
            return !!(typeof window !== 'undefined' && window.matchMedia
                && window.matchMedia('(prefers-color-scheme: dark)').matches);
        } catch (e) { return false; }
    }

    // 商户暗色总开关的判定：服务端说过话就听服务端；还没说话（配置未到）用上次
    // 会话缓存的值兜底 —— 开了暗色的 app 冷启动不闪亮屏，没开的 app 恒 false。
    _darkEnabledResolved = () => {
        const t = this.state.theme || {};
        if (t.darkModeEnabled !== undefined) return !!t.darkModeEnabled;
        return this._cachedDarkOk === true;
    }

    // 当次实际是否暗色。模式优先级（resolveThemeMode）：集成方显式
    // （laneChatCustomizations.theme；widget.html ?theme= 也写进同一字段）>
    // 商户 widget_config.theme_mode > light；auto 跟 prefersDark。
    // dark_mode_enabled=false（含所有存量商户）时恒 false —— 一丝暗色逻辑不生效。
    _isDarkActive = () => {
        if (!this._darkEnabledResolved()) return false;
        const mode = resolveThemeMode({
            darkModeEnabled: true,
            explicitTheme: this.props.conf.theme,
            serverThemeMode: (this.state.theme || {}).themeMode,
        });
        return resolveEffectiveTheme(mode, this.state.prefersDark) === 'dark';
    }

    // 渲染期消费的 theme：亮色 = state.theme 原样引用（零开销、零行为差异）；
    // 暗色 = 覆盖了配色字段的派生对象（state.theme 本体永远保持亮色语义，
    // applyConfig/updateTheme 照常写它，auto 模式来回切不丢亮色配置）。
    _displayTheme = () => {
        const base = this.state.theme;
        if (!base || !this._isDarkActive()) return base;
        const cache = this._displayThemeCache;
        if (cache && cache.base === base && cache.prefersDark === this.state.prefersDark) {
            return cache.value;
        }
        const palette = resolveDarkPalette({
            main_color: base.mainColor,
            user_bubble_color: base.userBubbleColor,
            user_bubble_text_color: base.userBubbleTextColor,
            header_text_color: base.headerTextColor,
        }, base.darkColors);
        const value = {
            ...base,
            mainColor: palette.main_color,
            headerGradient: palette.header_gradient,
            headerTextColor: palette.header_text_color,
            chatBubbleColor: palette.chat_bubble_color,
            chatBubbleTextColor: palette.chat_bubble_text_color,
            userBubbleColor: palette.user_bubble_color,
            userBubbleTextColor: palette.user_bubble_text_color,
            secondaryColor: palette.chat_bubble_color,
            textColor: palette.text_color,
            inputBorderColor: palette.border_color,
            __dark: true,
            __darkPalette: palette,
        };
        this._displayThemeCache = { base, prefersDark: this.state.prefersDark, value };
        return value;
    }

    componentDidMount() {
        debugLog('componentDidMount');
        this._isMounted = true; // Component is mounted
        // bug 5：另一半消费点——万一 _pendingEarlyTheme 在挂载之前就已经算好了
        // （preInitClient 数据先到、mount 后到这条理论上的顺序），这里补应用一次。
        // 正常路径下 preInitClient 本来就是 componentDidMount 自己触发的
        // （下面的 this.loadAppConfig()），不可能比这行更早跑完，所以这里几乎
        // 总是 no-op（_pendingEarlyTheme 还是 null）——纯防御，不是常态路径。
        this._applyPendingEarlyTheme();
        // 首屏就同步一次——构造函数里 initialTheme 已经算好 mainColor，不用等
        // /v1/chat 或 /usage/start 才有值（customizations 或出厂默认这一份）。
        this._syncThemeCssVars();
        // auto 模式跟随系统亮暗切换：matchMedia change 实时监听（老 WebView 只有
        // addListener，两个都试）。dark_mode_enabled=false 时 prefersDark 变了也
        // 只是无效 re-render，不产生任何视觉差异。
        try {
            this._darkMq = (typeof window !== 'undefined' && window.matchMedia)
                ? window.matchMedia('(prefers-color-scheme: dark)') : null;
            if (this._darkMq) {
                this._onDarkMqChange = (e) => {
                    if (this._isMounted) this.setState({ prefersDark: !!e.matches });
                };
                if (this._darkMq.addEventListener) this._darkMq.addEventListener('change', this._onDarkMqChange);
                else if (this._darkMq.addListener) this._darkMq.addListener(this._onDarkMqChange);
            }
        } catch (e) { /* ignore */ }
        // 语言词条按需加载：访客语言不是内置的 en/zh 时，首屏会先用 en 渲染，
        // chunk 落地后这里 bump 一次 langNonce 把整棵树刷成目标语言。
        this._unsubLocale = onLocaleLoaded(this._bumpLangNonce);
        ensureLocale(getLang());
        // 设置laneChatSDK的引用
        if (window.laneChatSDK) {
            window.laneChatSDK._widgetInstance = this;
        }

        // 监听来自iframe的消息
        window.addEventListener('message', this.handleMessage);

        // 监听页面离开事件
        this.handlePageUnload = this.handlePageUnload.bind(this);
        window.addEventListener('beforeunload', this.handlePageUnload);
        window.addEventListener('pagehide', this.handlePageUnload);

        // 加载应用配置
        this.loadAppConfig();

        // 兜底：4秒后如果配置还没加载完，也把 configReady 翻真——但这只解除骨架
        // 本身的"要不要继续转"，不解除品牌色闸门。render() 的两个渲染入口要求
        // configReady && colorKnown 同时为真才画真内容，colorKnown 只能靠
        // resolveColorKnown 的三条合法来源（显式 customizations/本地缓存/服务端
        // 本次确认）变真——不再像退役前的 fabColorResolved 那样也在这个定时器里
        // "超时就当已解决"，那正是"有概率看到默认蓝"反复复发的根因之一（详见
        // theme-color.js resolveFabRenderGate 退役说明）。真正网络broken 的场景
        // 不会靠这个计时器强行展示工厂色兜底，而是走 initErrorKind/renderInitError
        // （preInitClient 的连接层失败分类，已经在渲染优先级上盖过这两个骨架
        // 分支），所以这里 configReady 单独超时翻真、colorKnown 仍然 hold 不会
        // 导致"骨架转不出去"——不是骨架卡住，是等错误页接管或等 colorKnown 真的
        // 确认。
        this._configTimer = setTimeout(() => {
            if (this._isMounted && !this.state.configReady) {
                this.setState({ configReady: true });
            }
        }, 4000);

        // 检查 URL 参数，如果有 lane_open=1 则自动打开聊天
        this.checkAutoOpen();

        // 宿主页横向溢出防护：监听 visual viewport 变化做锚定修正
        if (typeof window !== 'undefined' && window.visualViewport) {
            window.visualViewport.addEventListener('resize', this._updateViewportAnchor);
            window.visualViewport.addEventListener('scroll', this._updateViewportAnchor);
            window.visualViewport.addEventListener('resize', this._updateKeyboardViewport);
            window.visualViewport.addEventListener('scroll', this._updateKeyboardViewport);
            this._updateViewportAnchor();
            this._updateKeyboardViewport();
        }
        // 宿主窗口 resize：面板高度 clamp / 知识库详情页尺寸跟随视口
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this._updateViewportAnchor);
            window.addEventListener('resize', this._updateKeyboardViewport);
            window.addEventListener('resize', this._onHostResize);
        }
        // 键盘适配兜底：focus/blur 触发重测（Edge Android 真机反馈：浮动面板键盘
        // 弹起后底部露出宿主页）。部分非 Chrome 内核的移动浏览器，软键盘弹起/
        // 收起时可能完全不派发 visualViewport 也不派发 window 的 resize 事件（跟
        // vvdebug.html 那份并行诊断页需要额外 setInterval 兜底才能捕捉到变化，
        // 是同一类根因）。一旦真的一个 resize 事件都没有，_updateKeyboardViewport
        // 就完全没有触发机会。focusin/focusout 拿不到"键盘到底会不会弹"的确定
        // 信号，但"文本输入框获得/失去焦点"在移动端是键盘即将弹起/收起的强信号，
        // 且跨浏览器可靠（标准 DOM 事件，会穿过 shadow 边界，不依赖
        // visualViewport 这个各家实现不一的 API）。触发的还是同一个
        // _updateKeyboardViewport，不是另起一套判定逻辑——事件本来就正常触发的
        // 浏览器（Chrome）不受影响，只是多了几次幂等的重复调用（rect 算出来一样
        // 就不会二次 setState）。
        if (typeof document !== 'undefined') {
            document.addEventListener('focusin', this._onFocusMaybeKeyboard, true);
            document.addEventListener('focusout', this._onFocusMaybeKeyboard, true);
        }

        // 延迟显示 teaser bubble（配置有 teaserMessage 且本 session 未展示过）
        this.scheduleTeaser();

        // inline / 一上来就是打开态的场景不会经过 componentDidUpdate 的跳变,
        // 这里补报一次初始状态。
        if (this.state.isChatOpen) {
            this._reportPanelState(true);
            // 同理，opened 总线事件也要在这里补发一次 —— inline 模式 isChatOpen
            // 挂载时已经是 true，componentDidUpdate 的跳变 emit 永远等不到那次
            // false→true 的变化。floating 模式挂载时 isChatOpen 恒为 false（见
            // constructor 的 startOpen），不会走进这个分支，因此不会和
            // componentDidUpdate 里点开时的那次 emit 重复。
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit('open');
            }
        }
    }

    // Teaser lifecycle is Flow-driven: server's first_visit trigger decides whether
    // to show_teaser. JS-config `teaserMessage` remains only as a dev fallback so
    // integrators can iterate without setting up Flow.
    //
    //   1. preInitClient reads response.data.teaser — server's /v1/chat call
    //      returns whatever Flow queued synchronously (usually nothing yet).
    //   2. A single short-poll to /v1/widget/teaser ~1500ms later catches up
    //      with Flow consumer that ran async via Redis queue.
    //   3. If neither yields a payload AND theme.teaserMessage is set, we fall
    //      back to the config-based teaser for dev parity.
    //
    // No `wasChatOpened` gate — server's `first_visit` vs `returning_visit`
    // decides who sees what. No sessionStorage — Redis TTL + Flow idempotency
    // handle dedup server-side.
    scheduleTeaser = () => {
        const theme = this.state.theme || this.props.conf;
        if (theme.mode === 'inline') return;    // no FAB → no teaser target
        if (this.state.isChatOpen) return;

        // If the server already handed us a payload in /v1/chat, present it now.
        if (this.state.teaserPayload) {
            this.presentTeaser(this.state.teaserPayload);
            return;
        }

        // Otherwise poll for Flow's async consumer. The teaser is often the FIRST
        // action in a first_visit flow, so it can land AFTER the first poll — and
        // the WS push can arrive BEFORE our socket handler is registered. Neither
        // miss clears the Redis key, so we retry a few times; the key survives
        // until a poll (or /v1/chat) reads it. presentTeaser is one-shot, so a WS
        // push + a later poll can't double-show.
        // 2000ms:比 WS register ack 晚一点,好让 lane_first_visit 信号先到——回访场景
        // 能在发出任何 teaser 请求之前就把轮询取消掉。first_visit 的 teaser 基本都由
        // WS push 直接送达,这里只是兜底,晚一点不影响体验。
        this._teaserPollAttempts = 0;
        this._teaserPollTimer = setTimeout(() => this.pollServerTeaser(), 2000);
    }

    // ---------- teaser 会话级一次性 ----------
    // 「用户已互动」= 开过面板 / 手动关过 teaser / 发过消息。发生过任何一种后，
    // 本 session（sessionStorage 生命周期）内营销 teaser 不再弹 —— 关面板瞬间
    // teaser 又弹回来的观感是"啪一下没了，又弹广告"。msg-preview（真实回复提醒）
    // 不受此门控。
    _teaserSessionKey = () => `lane_teaser_done_${this.props.conf.app_id || 'app'}`;

    _teaserSessionDone = () => {
        try { return sessionStorage.getItem(this._teaserSessionKey()) === '1'; } catch (e) { return false; }
    }

    _markTeaserSessionDone = () => {
        try { sessionStorage.setItem(this._teaserSessionKey(), '1'); } catch (e) { /* ignore */ }
    }

    // Present a teaser payload — structure matches ShowTeaserAction output:
    //   { message, title, avatar_url, auto_dismiss_ms, delay_ms }
    presentTeaser = (payload) => {
        if (!this._isMounted || this.state.teaserDismissed) return;
        if (this._teaserSessionDone()) return; // 本 session 用户已互动过，不再弹营销 teaser
        if (!payload || !payload.message) return;
        // One-shot per mount: WS push, /v1/chat and the poll can all deliver the
        // same teaser — first one wins, so it never re-shows after auto-dismiss.
        if (this._teaserPresented) return;
        this._teaserPresented = true;
        // Whoever delivered first wins — cancel any pending poll so we stop hitting
        // /v1/widget/teaser (this is why the WS push existed: kill the polling).
        if (this._teaserPollTimer) { clearTimeout(this._teaserPollTimer); this._teaserPollTimer = null; }

        const delay = Math.max(0, parseInt(payload.delay_ms, 10) || 0);
        const auto  = Math.max(0, parseInt(payload.auto_dismiss_ms, 10) || 0);

        this._teaserShowTimer = setTimeout(() => {
            if (!this._isMounted || this.state.isChatOpen || this.state.teaserDismissed) return;
            this.setState({ teaserPayload: payload, teaserVisible: true });
            if (auto > 0) {
                this._teaserAutoTimer = setTimeout(() => {
                    if (this._isMounted) this.setState({ teaserVisible: false });
                }, auto);
            }
        }, delay);
    }

    // 面板关闭时的新消息预览气泡 —— 复用 teaser 的展示层,但语义不同:
    // teaser 是「营销诱导」一次性;这个是「有人回你了」,每来一条新消息都该重新提示,
    // 所以绕开 _teaserPresented 的一次性锁,单独维护自己的可见状态。
    // 访客手动关掉(teaserDismissed)后本次 mount 不再打扰。
    presentMessagePreview = (payload) => {
        if (!this._isMounted) return;
        if (this.state.isChatOpen) return;          // 开着就不用提示
        if (this.state.teaserDismissed) return;     // 用户明确关过,别烦他
        const theme = this.state.theme || this.props.conf;
        if (theme.mode === 'inline') return;        // 没有浮动按钮就没有挂载点
        const text = (payload.text || '').trim();
        if (!text) return;

        if (this._teaserAutoTimer) { clearTimeout(this._teaserAutoTimer); this._teaserAutoTimer = null; }
        this.setState({
            teaserPayload: {
                message: text,
                title: payload.agent_name || undefined,
                avatar_url: payload.avatar_url || undefined,
                // 带上 trace_id 让渲染门控放行(和 flow teaser 同等待遇,不受
                // closedStyle==='button' 限制)——这是服务端来的真实消息,不是 dev fallback
                trace_id: 'msg-preview',
            },
            teaserVisible: true,
        });
        this._teaserAutoTimer = setTimeout(() => {
            if (this._isMounted) this.setState({ teaserVisible: false });
        }, 8000);
    }

    // One-shot pickup from /v1/widget/teaser; falls back to theme.teaserMessage
    // if server has nothing and the dev is iterating locally.
    pollServerTeaser = async () => {
        if (!this._isMounted || this.state.isChatOpen || this.state.teaserDismissed) return;
        if (this._teaserPresented) return; // WS push already delivered it
        if (this._teaserPollDone) return;  // server said no first_visit fired → no teaser will exist
        const theme = this.state.theme || this.props.conf;
        const MAX_ATTEMPTS = 3; // ~1.2s + 2×1.4s ≈ covers a ~4s flow consumer lag; WS push cancels these early when it wins
        this._teaserPollAttempts = (this._teaserPollAttempts || 0) + 1;
        try {
            const chatInst = this.chatFrameRef && this.chatFrameRef.current
                ? this.chatFrameRef.current.getChatInstance && this.chatFrameRef.current.getChatInstance()
                : null;
            const chatId = (chatInst && (chatInst.chat_id || (chatInst.state && chatInst.state.chat_id))) || this._preInitData?.chatId;
            const token  = ApiService && (ApiService.getToken ? ApiService.getToken() : ApiService._token);
            const base   = ApiService && ApiService.baseURL;
            if (chatId && token && base) {
                const resp = await fetch(`${base}/widget/teaser?chat_id=${encodeURIComponent(chatId)}`, {
                    headers: { 'Authorization': 'Bearer ' + token },
                    cache: 'no-store',
                });
                const j = await resp.json();
                if (j && j.code === 0 && j.data && j.data.teaser && j.data.teaser.message) {
                    this.presentTeaser(j.data.teaser);
                    return;
                }
            }
        } catch (_) { /* swallow — retry or fall through to config fallback */ }

        // Nothing yet — the Flow consumer may not have written the key. Retry a
        // few times before giving up (key isn't cleared until a successful read).
        if (!this._teaserPresented && this._teaserPollAttempts < MAX_ATTEMPTS
            && this._isMounted && !this.state.isChatOpen && !this.state.teaserDismissed) {
            this._teaserPollTimer = setTimeout(() => this.pollServerTeaser(), 1400);
            return;
        }

        // Dev fallback only — ignored in prod when Flow owns this
        if (theme.teaserMessage) {
            this.presentTeaser({
                message:         theme.teaserMessage,
                title:           theme.teaserTitle,
                avatar_url:      theme.teaserAvatarUrl,
                auto_dismiss_ms: theme.teaserAutoDismissMs,
                delay_ms:        theme.teaserDelayMs,
            });
        }
    }

    // ---------- Launcher label ----------
    // Shown once per session (launcher-engagement.js holds the rules). It stays at least
    // ~10s, then collapses on the visitor's next scroll or navigation, or at once on click
    // or close. Waiting for that next action means it does not vanish mid-read, and it is
    // gone before the visitor scrolls on through the page.
    _syncLauncherLabel() {
        if (this._labelStarted) return;
        const theme = this.state.theme;
        if (!theme.launcherLabelEnabled || !this.state.configReady || !this.state.colorKnown) return;
        if (this._isInlineMode()) return;
        const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
        if (mobile && !theme.launcherLabelMobile) return;

        this._labelStarted = true;
        const key = labelStorageKey(this.props.conf.app_id);
        let raw = null;
        try { raw = sessionStorage.getItem(key); } catch (e) { /* treated as a first visit */ }
        const session = resolveLabelSession(raw, Date.now());
        if (!session.show) {
            try { sessionStorage.setItem(key, 'done'); } catch (e) { /* ignore */ }
            this.setState({ labelDone: true });
            return;
        }
        if (raw === null) {
            try { sessionStorage.setItem(key, String(Date.now())); } catch (e) { /* ignore */ }
        }
        this._labelTimer = setTimeout(() => {
            this._labelTimer = null;
            this._addLabelCollapseListeners();
        }, session.remainingMs);
    }

    _addLabelCollapseListeners() {
        if (this._labelListening) return;
        this._labelListening = true;
        // Capture, so scrolling inside a host page's inner container counts as well.
        window.addEventListener('scroll', this._collapseLabel, { passive: true, capture: true });
        window.addEventListener('popstate', this._collapseLabel);
        window.addEventListener('hashchange', this._collapseLabel);
    }

    _removeLabelCollapseListeners() {
        if (this._labelTimer) { clearTimeout(this._labelTimer); this._labelTimer = null; }
        if (!this._labelListening) return;
        this._labelListening = false;
        window.removeEventListener('scroll', this._collapseLabel, { capture: true });
        window.removeEventListener('popstate', this._collapseLabel);
        window.removeEventListener('hashchange', this._collapseLabel);
    }

    _collapseLabel = () => {
        this._removeLabelCollapseListeners();
        if (this.state.labelDone || this.state.labelClosing) return;
        if (!this._labelStarted) return;
        try { sessionStorage.setItem(labelStorageKey(this.props.conf.app_id), 'done'); } catch (e) { /* ignore */ }
        if (!this._isMounted) return;
        this.setState({ labelClosing: true });
        this._labelCloseTimer = setTimeout(() => {
            if (this._isMounted) this.setState({ labelDone: true, labelClosing: false });
        }, this._prefersReducedMotion() ? 0 : 200);
    }

    handleLabelClick = (e) => {
        this._collapseLabel();
        // Same intent as a teaser click: go to the conversation, not the Home landing.
        this.switchToChat();
        this.onClickBtn(e);
    }

    // ---------- Quick buttons ----------
    _readQuickButtonsDismissed() {
        try {
            return localStorage.getItem(quickButtonsDismissKey(this.props.conf.app_id)) === '1';
        } catch (e) {
            return false;
        }
    }

    handleQuickButtonsDismiss = () => {
        try { localStorage.setItem(quickButtonsDismissKey(this.props.conf.app_id), '1'); } catch (e) { /* ignore */ }
        this.setState({ quickButtonsDismissed: true });
    }

    handleQuickButton = (button, e) => {
        if (button.action === 'link') {
            // Prevent the anchor's own navigation so a native host can take the link.
            if (e && e.preventDefault) e.preventDefault();
            bridgeOpenLink(button.url, { source: 'quick_button' });
            return;
        }
        this._collapseLabel();
        this.switchToChat();
        this.onClickBtn(e);
        this._runQuickChatAction(button);
    }

    // The chat mounts when the panel first opens and needs its socket registered before it
    // can send. 80 x 250ms outlasts a slow connect; after that the visitor can type.
    _runQuickChatAction = (button, attempt = 0) => {
        if (!this._isMounted) return;
        const inst = this.chatFrameRef && this.chatFrameRef.current
            && this.chatFrameRef.current.getChatInstance
            && this.chatFrameRef.current.getChatInstance();
        if (inst && inst.sendQuickAction && inst.sendQuickAction(button)) return;
        if (attempt < 80) setTimeout(() => this._runQuickChatAction(button, attempt + 1), 250);
    }

    handleTeaserDismiss = () => {
        if (this._teaserAutoTimer) { clearTimeout(this._teaserAutoTimer); this._teaserAutoTimer = null; }
        // The visitor just waved away a nudge; bringing the label back would be another one.
        this._collapseLabel();
        this._markTeaserSessionDone();
        this.setState({ teaserVisible: false, teaserDismissed: true });
    }

    handleTeaserClick = (e) => {
        this.handleTeaserDismiss();
        // teaser 点击是"我要聊天"的明确意图（跟 navigate-to-chat 同一类），必须
        // 盖过首次访问的 Home 默认落地——不能让用户点了营销气泡，面板打开却是
        // 首页。switchToChat() 顺带标记 _explicitViewTargeted（见该方法），
        // onClickBtn 单独处理"打开面板"（isChatOpen/cookie/pristine）——e.target
        // 是气泡本身，匹配不到 onClickBtn 内部的 tab/按钮分支，会走它的通用
        // "打开"分支，不会覆盖这里刚设好的 widgetClass。
        this.switchToChat();
        this.onClickBtn(e);
    }

    /**
     * 商户配的 `auto_open`：配置到位后自动把面板弹开一次。
     *
     * 调用点刻意放在 configReady && colorKnown **都过关之后** —— 提前弹会先画
     * 一版工厂蓝再跳成商户色，正是 colorKnown 闸门要挡的那种"抢跑"。
     * 判定本身是纯函数 resolveAutoOpen（theme-color.js），这里只负责会话去重
     * 和真正的 open 动作。
     */
    _maybeAutoOpen() {
        if (this._autoOpenDone) return;
        if (!this.state.configReady || !this.state.colorKnown) return;

        const appId = this.props.conf.app_id;
        const sessionKey = `lane_auto_opened_${appId}`;
        let openedThisSession = false;
        try { openedThisSession = sessionStorage.getItem(sessionKey) === '1'; } catch (e) { /* ignore */ }

        const should = resolveAutoOpen({
            autoOpen: (this.state.appConfig || {}).auto_open,
            inline: this._isInlineMode(),
            isChatOpen: this.state.isChatOpen,
            openedThisSession,
        });
        // 只要闸门过了就记 done —— 包括判定为 false 的情况。否则每次 re-render
        // 都会重新问一遍 sessionStorage，而答案不会变。
        this._autoOpenDone = true;
        if (!should) return;

        try { sessionStorage.setItem(sessionKey, '1'); } catch (e) { /* ignore */ }
        this.openChat();
    }

    /**
     * inline 挂载模式（宿主把 widget 嵌进页面容器，而不是浮球+面板）。
     * 原本这个判断在 render 里手抄了两份，加上自动打开这处就是三份 —— 收成一个
     * 出口，省得下次有人只改其中一处。
     */
    _isInlineMode() {
        return typeof window !== 'undefined' && window.laneChatMode === 'inline';
    }

    // 检查是否需要自动打开聊天（从推送通知点击跳转）
    checkAutoOpen() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('lane_open') === '1') {
                debugLog('[Widget] Auto-opening chat from push notification');
                // 延迟一点打开，确保组件已完全加载
                setTimeout(() => {
                    this.openChat();
                    // 可选：从 URL 中移除参数，避免刷新后重复打开
                    if (window.history && window.history.replaceState) {
                        urlParams.delete('lane_open');
                        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash;
                        window.history.replaceState({}, '', newUrl);
                    }
                }, 500);
            }
        } catch (e) {
            console.error('[Widget] Error checking auto-open:', e);
        }
    }

    // 加载应用配置
    async loadAppConfig() {
        try {
            debugLog('[Widget] loadAppConfig called');
            const appId = this.props.conf.app_id;
            debugLog('[Widget] appId:', appId);

            if (!appId) {
                debugLog('[Widget] No appId, skipping config load');
                return;
            }

            // 1. 先预初始化客户端：创建/查找 client + 获取 JWT（必须在 /usage/start 之前）
            this._preInitPromise = this.preInitClient(appId);
            await this._preInitPromise;

            // 1b. preInit 完成后立即挂载(隐藏的)聊天组件 → 连上 WebSocket。
            // 面板显隐仍由 isChatOpen 控制(display:none)，这里只连不显示。
            // 目的：first_visit flow 的 typing/消息、以及 teaser，都能在用户点开
            // 面板【之前】就实时收到，而不是等点开才连 WS(错过 typing、显得慢)。
            // 集成方可用 window.laneChatNoProactive=true 保留旧的"点开才连"行为。
            if (!window.laneChatNoProactive && !this.state.chatInitialized) {
                this.setState({ chatInitialized: true });
            }

            // 直达聊天入口（widget.html ?view=chat&chat_id=<N>）：this.props.initialChatId
            // 通过 {...this.props} 展开原样传给 <ChatFrame>/<Chat>（见下方两处
            // ChatFrame 渲染），Chat 自己在 componentDidMount 首次 _applyInitResponse
            // 时读它，第一次 loadMessages 就直接打目标会话——不再需要这里等
            // ChatFrame 挂载、retry 找 chat 实例、再 imperative 调 switchToChat()
            // 二次切换（那条路径 = 先渲染默认会话再跳，多打一次 load-messages、
            // 还会闪一下错误会话）。旧的 _applyInitialChatId retry 方法已随之
            // 移除——它的另一个潜在用途"热切换"(native-bridge navigate 在已挂载
            // 时到达)本来就走 switchToChat() 直连（见 widget-index.js 的
            // onNativeEvent('navigate', ...) 订阅），从未依赖过这个方法，退役
            // 不影响热切换路径。

            // 2. 然后调用 /usage/start 记录访问会话（此时 JWT 已就绪）
            const apiUrl = `${ApiService.baseURL}/usage/start?host=${window.location.host}`;

            // 读取 visitor_token cookie
            const cookieName = `lane_vt_${appId}`;
            let visitorToken = null;
            try {
                const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + cookieName + '=([^;]+)'));
                if (match) visitorToken = decodeURIComponent(match[1]);
            } catch(e) {}

            const requestBody = {
                appId: appId,
                userAgent: navigator.userAgent,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                language: navigator.language,
                platform: navigator.platform,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                timestamp: Date.now(),
                page_title: document.title,
                url: window.location.href
            };

            if (visitorToken) {
                requestBody.visitor_token = visitorToken;
            }

            // 构建 headers（附带 JWT 如果有）
            const headers = { 'Content-Type': 'application/json' };
            const jwt = ApiService.getToken();
            if (jwt) {
                headers['Authorization'] = `Bearer ${jwt}`;
            }

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.code === 0 && data.config) {
                debugLog('[Widget] Loaded app config');

                if (data.session_id) {
                    this.sessionId = data.session_id;
                }

                if (this._isMounted) {
                    this.setState({ appConfig: data.config, configReady: true });
                    this.applyConfig(data.config);
                }
            } else if (this._isMounted) {
                this.setState({ configReady: true });
            }

            // fingerprint 已在 preInitClient 阻塞加载并落入 localStorage + this.visitorFingerprint
            // 此处不再重复加载 FingerprintJS，避免浪费
        } catch (error) {
            console.error('Failed to load app config:', error);
            if (this._isMounted) {
                this.setState({ configReady: true });
            }
        }
    }

    // 错误页"重试"按钮：只在 initErrorKind 是 'network'/'server'(可重试类)时
    // 会渲染这个按钮出来（见 renderInitError），'app_unavailable' 不会——重试
    // 拿到的还是同一个"app 不存在"，没有意义。清掉错误状态、原样重跑整条
    // loadAppConfig（preInitClient 的 /v1/chat + 后续 /usage/start），不做
    // 独立的一套精简重试逻辑，行为跟首次加载完全一致。
    retryInit = () => {
        if (this._isMounted) this.setState({ initErrorKind: null });
        this.loadAppConfig();
    };

    // bug 5 修复的消费端：谁先到（preInitClient 的 /v1/chat 响应 vs 组件挂载）
    // 都调这同一个方法——mounted 才真正 setState，没 mounted 就原样留着
    // （_pendingEarlyTheme 不清空），下一次谁调用谁再试。见 preInitClient 里
    // computeEarlyThemeUpdate 调用点和 componentDidMount 里的调用点。
    _applyPendingEarlyTheme = () => {
        if (!this._isMounted || !this._pendingEarlyTheme) return;
        const earlyThemeUpdate = this._pendingEarlyTheme;
        this._pendingEarlyTheme = null;
        this.setState((prev) => {
            const nextTheme = { ...prev.theme, ...earlyThemeUpdate };
            // 首次访问落地视图：早期 widget_config 到达是 homePageEnabled/
            // initialView 第一次可能有真实值的时刻——跟构造函数那份临时判定
            // 同一个纯函数，只是这次喂真数据。发生在 configReady 之前（骨架还
            // 显示着），用户看不到这次可能的 chat→home 升级。
            const landingUpdate = this._maybeAutoLandOnFirstVisit(nextTheme);
            return landingUpdate ? { theme: nextTheme, ...landingUpdate } : { theme: nextTheme };
        });
        debugLog('[Widget] Applied early /v1/chat widget_config colors:', earlyThemeUpdate);
    }

    // 预初始化客户端身份（widget加载时调用，而非打开聊天时）
    //
    // 身份双锚点：
    //   localStorage `lane_uuid_{appId}` = 前端生成的稳定 UUID（主锚点）
    //   localStorage `lane_fp_{appId}`   = 浏览器指纹（供拉黑用）
    //   cookie       `lane_vt_{appId}`   = UUID 的 cookie 镜像（一年有效，防止 localStorage 被清）
    // 后端收到 (uuid, fp) 必须两者同时命中同一 Client 才视为回访；否则新建访客、用服务端返回的
    // 权威 uuid 回写 localStorage + cookie。
    async preInitClient(appId) {
        try {
            const UUID_KEY = `lane_uuid_${appId}`;
            const FP_KEY   = `lane_fp_${appId}`;
            const VT_COOKIE = `lane_vt_${appId}`;

            // 1. 读 / 生成 visitor_uuid
            let visitorUuid = null;
            try { visitorUuid = localStorage.getItem(UUID_KEY); } catch(e) {}
            if (!visitorUuid) {
                // cookie 兜底：有历史 visitor_token 就沿用，反向补 localStorage
                try {
                    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + VT_COOKIE + '=([^;]+)'));
                    if (m) visitorUuid = decodeURIComponent(m[1]);
                } catch(e) {}
            }
            if (!visitorUuid) {
                visitorUuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : ('v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12));
            }
            try { localStorage.setItem(UUID_KEY, visitorUuid); } catch(e) {}

            // 2. 读 / 计算 fingerprint（阻塞，但给 800ms 超时兜底）
            //    兜底值 fp-fallback-* 不进 localStorage：它只是 UA+屏幕+时区的哈希，会撞号，
            //    缓存了就永远拿不到真指纹（线上曾有约四分之一访客一直停在兜底值）。
            //    超时后真指纹在后台继续算，算完写入缓存，下次加载用真值，服务端再把旧兜底升级掉。
            let fingerprint = null;
            try {
                fingerprint = localStorage.getItem(FP_KEY);
                if (fingerprint && fingerprint.indexOf('fp-fallback-') === 0) {
                    localStorage.removeItem(FP_KEY);
                    fingerprint = null;
                }
            } catch(e) {}
            if (!fingerprint) {
                const realFp = import('@fingerprintjs/fingerprintjs')
                    .then(({ default: FingerprintJS }) => FingerprintJS.load())
                    .then(fp => fp.get())
                    .then(r => {
                        if (r && r.visitorId) {
                            try { localStorage.setItem(FP_KEY, r.visitorId); } catch(e) {}
                        }
                        return r && r.visitorId;
                    })
                    .catch(() => null);
                try {
                    fingerprint = await Promise.race([
                        realFp,
                        new Promise(resolve => setTimeout(() => resolve(null), 800))
                    ]);
                } catch(e) { fingerprint = null; }
                if (!fingerprint) {
                    // 指纹没赶上 → 用 UA + screen + timezone 生成兜底，仅本次请求使用
                    const fallback = [
                        navigator.userAgent,
                        navigator.language,
                        screen.width + 'x' + screen.height,
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                        navigator.platform || ''
                    ].join('|');
                    let h = 0;
                    for (let i = 0; i < fallback.length; i++) {
                        h = ((h << 5) - h + fallback.charCodeAt(i)) | 0;
                    }
                    fingerprint = 'fp-fallback-' + (h >>> 0).toString(16);
                }
            }

            // 3. 合并 fromData
            const fromData = {
                ...(this.props.fromData || {}),
                referrer: document.referrer,
                url: window.location.href
            };

            // 3.5 Native SDK pass-through（widget.html 从 ?fp=&did=&user_id= 写入
            //     window.laneChatSdkFp/...）：附带上送，服务端优先按它复用
            //     /sdk/sessions 建的 client 行 —— 同一设备只留一个 client。
            //     注意：浏览器自算 fingerprint 照旧发送（fp_key 拉黑体系不变），
            //     sdk 指纹只是身份解析的更高优先级锚点。
            const sdkIdentity = {};
            try {
                if (typeof window.laneChatSdkFp === 'string' && window.laneChatSdkFp) {
                    sdkIdentity.sdk_fp = window.laneChatSdkFp;
                }
                if (typeof window.laneChatSdkDeviceId === 'string' && window.laneChatSdkDeviceId) {
                    sdkIdentity.sdk_device_id = window.laneChatSdkDeviceId;
                }
                if (typeof window.laneChatExternalUserId === 'string' && window.laneChatExternalUserId) {
                    sdkIdentity.external_user_id = window.laneChatExternalUserId;
                }
            } catch (e) { /* ignore */ }

            // 4. 请求 /v1/chat
            const chatData = await ApiService.initChat(appId, visitorUuid, fingerprint, fromData, null, sdkIdentity);

            // 业务错误(如 app_id 不存在/缺失)：服务端用 HTTP 200 + code!==0 表达，
            // 不会走 fetch 的 !response.ok 分支、更不会抛异常——之前这里完全不检查
            // code，把 {code:404,msg:'Application not found'} 这类错误响应当成
            // "有 responseData 就是成功"直接往下走，client/JWT 从未真正建立，
            // 后续渲染出一个看着正常、实际没有会话的空壳 widget（真机反馈的"白屏/
            // 残骸态"）。这里先按 code 分类一次；classification 只认 400(缺
            // appId)/404(app 不存在)为"访客侧不可重试"的错误，其它业务码(如 403
            // bot/banned)不在这次要处理的范围内，维持现状不介入。
            const businessErrorKind = resolveInitErrorKind({
                businessCode: chatData?.code,
                isConnectionFailure: false,
                httpStatus: undefined,
            });
            if (businessErrorKind) {
                // 技术细节只打给集成方看(console.error)——访客不是开发者，页面上
                // 不出现 app_id/code 这类术语，见 renderInitError 的文案。
                console.error('[Widget] Chat init failed with business error:', {
                    appId, code: chatData?.code, msg: chatData?.msg,
                });
                if (this._isMounted) this.setState({ initErrorKind: businessErrorKind });
                return;
            }
            if (this._isMounted && this.state.initErrorKind) {
                // 重试成功：清掉上一次的错误页状态。
                this.setState({ initErrorKind: null });
            }

            const responseData = chatData?.data || chatData;

            if (responseData) {
                // 权威 uuid：服务端可能因双锚点失配而分配新的 → 覆盖 localStorage
                const authoritativeUuid = responseData.visitor_uuid || responseData.visitor_token;
                if (authoritativeUuid && authoritativeUuid !== visitorUuid) {
                    try { localStorage.setItem(UUID_KEY, authoritativeUuid); } catch(e) {}
                }
                if (authoritativeUuid) {
                    try {
                        document.cookie = `${VT_COOKIE}=${encodeURIComponent(authoritativeUuid)};path=/;max-age=${365*86400};SameSite=Lax`;
                    } catch(e) {}
                }
                // 如果服务端返回了 fingerprint（通常是客户端传的原样），也回写一次
                // 兜底值不回写，否则会盖掉后台刚算出来的真指纹
                if (responseData.fingerprint && responseData.fingerprint !== fingerprint
                    && String(responseData.fingerprint).indexOf('fp-fallback-') !== 0) {
                    try { localStorage.setItem(FP_KEY, responseData.fingerprint); } catch(e) {}
                }
                this._preInitData = responseData;
                this.visitorId = authoritativeUuid || visitorUuid;
                this.visitorFingerprint = responseData.fingerprint || fingerprint;

                // 尽早上色：/v1/chat 比 /usage/start 先返回（loadAppConfig 里两者是
                // 顺序 await，usage/start 还没发出），且是某些宿主（原生 SDK WebView
                // 等只调 /v1/chat、不跑 usage/start 这条 widget.js 专属流程的场景）
                // 唯一会到达的下发路径。此前这里完全不消费 appConfig.widget_config，
                // 那些场景访客侧永远是 default-configuration.js 的出厂蓝
                // （#2563eb），dash 配的颜色摆设。这里先应用一次，configReady 之后
                // usage/start 的 applyConfig() 还会再跑一遍完整版（不冲突，同一份
                // 映射函数、同一个优先级规则，值相同时是幂等覆盖）。
                //
                // fix round 4（bug 5）：数据到达和组件挂载谁先谁后，两条顺序都要
                // 落地——不能只在"数据到达时挂载已完成"这一侧消费。之前这里直接
                // `if (this._isMounted && ...) setState(...)`，_isMounted 为
                // false 的那一拍（理论上的重挂载竞态 / 未来代码改动打破调用顺序）
                // 会让这份颜色被无声丢弃、永远等不到——不是"稍后重试"，是"再也不会
                // 应用"。改成无条件先算好、存进 _pendingEarlyTheme，再统一走
                // _applyPendingEarlyTheme() 消费：mounted 就立刻应用；没 mounted
                // 就留着，componentDidMount 那一侧也会调用同一个方法查一遍待应用
                // 项，两个到达顺序都能落地。
                this._pendingEarlyTheme = computeEarlyThemeUpdate(
                    responseData.appConfig,
                    window.laneChatCustomizations,
                    this.state.theme.mainColor
                );
                if (this._pendingEarlyTheme) this._applyPendingEarlyTheme();

                // 品牌色结构性闸门：写本地缓存，供下次访问的构造函数同步读取
                // （见 Widget._readCachedAppConfig）——这是"回访场景彻底消灭等待
                // 窗口"的写侧。缓存整个 appConfig（不只是 widget_config），下次
                // 访问才能连 configReady 一起从第一帧翻真，不是只把颜色对上。
                // 只在真的拿到 widget_config 时写；appConfig 存在但没有
                // widget_config（理论上不会，服务端 sanitizeWidgetAppearance 恒
                // 返回对象，防御一下）就不动上次的缓存，避免用"没内容"覆盖掉一份
                // 还有效的旧缓存。
                if (responseData.appConfig && responseData.appConfig.widget_config) {
                    try {
                        localStorage.setItem(
                            Widget._widgetConfigCacheKey(appId),
                            JSON.stringify({
                                schemaVersion: WIDGET_CONFIG_CACHE_SCHEMA_VERSION,
                                appConfig: responseData.appConfig,
                                cachedAt: Date.now(),
                            })
                        );
                    } catch (e) { /* 存储满/隐私模式等——缓存本来就是锦上添花，写不进去不阻塞任何功能 */ }
                }

                // Flow-pushed teaser comes back on /v1/chat when the consumer already
                // processed first_visit. Stash it so scheduleTeaser picks it up
                // without hitting the short-poll.
                if (responseData.teaser && responseData.teaser.message && this._isMounted) {
                    this.setState({ teaserPayload: responseData.teaser });
                }

                debugLog('[Widget] Client pre-initialized, uuid:', (authoritativeUuid || visitorUuid).slice(0, 8) + '...');
            }

            // 品牌色结构性闸门：/v1/chat 这次请求已经跑到这里（没有抛异常、也没有
            // 命中上面的业务错误 early-return）——服务端已经明确回答过了，不管是
            // "有 widget_config"还是"没配过用工厂色"，都是 resolveColorKnown 第三条
            // 合法来源（serverConfirmed）。故意放在 try 块尾部而不是 catch/finally：
            // 失败路径不会执行到这里，颜色仍然是"未知"，交给 initErrorKind/
            // renderInitError 接管渲染（不画任何品牌色 UI），不会有"失败也当已解决，
            // 拿工厂蓝顶上"这种违反不变式的兜底。
            if (this._isMounted && !this.state.colorKnown) {
                this.setState({ colorKnown: true });
            }
        } catch (error) {
            console.warn('[Widget] Pre-init client failed:', error);
            // 连接层失败(fetch reject/超时)或服务端 5xx——跟 domain-failover 用的
            // 同一个"还没连上/连上了但对方挂了"判据(api.js 里 AbortError/
            // TypeError 是连接层信号；error.status 是 HTTP 状态码本身，不是业务
            // code)。跟业务错误分开处理是因为这两类原则上重试就可能好——按钮
            // 行为跟 UI 结构走同一个 renderInitError，只是文案 key 不同。
            const isConnFailure = error && (error.name === 'AbortError' || error instanceof TypeError);
            const kind = resolveInitErrorKind({
                businessCode: null,
                isConnectionFailure: isConnFailure,
                httpStatus: error && error.status,
            });
            if (kind && this._isMounted) {
                this.setState({ initErrorKind: kind });
            }
            // 故意不在这里(catch)把 colorKnown 设成 true——退役前的 fabColorResolved
            // 在这个位置（当时是 finally，成功失败都会到）不分青红皂白地把"失败"也
            // 当"颜色已确定"处理，直接放行工厂蓝渲染，这正是"有概率看到默认蓝"的
            // 根因之一。colorKnown 只在 try 块跑到尾部（服务端明确响应过）时才
            // 变真，见那边注释；失败路径靠上面刚设置的 initErrorKind 接管渲染，
            // render() 里错误页优先级高于任何品牌色 UI，不需要靠"假装颜色已确定"
            // 才能跳出骨架。
        }
    }

    // 应用配置到UI
    applyConfig(config) {
        debugLog('applyConfig', config);
        // 应用关闭按钮样式配置
        let themeUpdate = { ...this.state.theme };

        // laneChatCustomizations 的 closedStyle 优先级最高
        const finalClosedStyle = this.props.conf.closedStyle ||
            (config.close_button_style === 'top' ? 'chat' :
             config.close_button_style === 'bottom' ? 'button' :
             themeUpdate.closedStyle);

        if (finalClosedStyle !== themeUpdate.closedStyle) {
            themeUpdate.closedStyle = finalClosedStyle;
            debugLog('[Widget] Updating closedStyle:', {
                from: this.state.theme.closedStyle,
                to: themeUpdate.closedStyle,
                config_close_button_style: config.close_button_style,
                customization_closedStyle: this.props.conf.closedStyle
            });
        }

        // Apply theme if configured
        if (config.theme && config.theme !== 'default') {
            debugLog('Applying theme:', config.theme);
            themeUpdate.themeConfig = config.theme;
        }

        // Apply widget_config from backend —— 映射表+优先级判定统一收在
        // widgetConfigToThemeUpdate（default-configuration.js），/v1/chat 早期上色
        // （见 preInitClient）用的是同一份函数，两条路径不会各写一套字段名/优先级
        // 慢慢跑偏。customizations 传原始 window.laneChatCustomizations（不是合并后
        // 的 props.conf）—— 只有这样才能区分"集成方真的显式设置过"。
        if (config.widget_config && typeof config.widget_config === 'object') {
            const wc = config.widget_config;
            Object.assign(themeUpdate, widgetConfigToThemeUpdate(wc, window.laneChatCustomizations));
            // headerGradient 是复合派生值，不能进 widgetConfigToThemeUpdate 那张
            // "有就直传"的表——每次应用配置都要重新按三级优先级解一次（customizations
            // 显式 > 服务端显式 > 从（可能刚更新的）mainColor 派生）。fix round 1：
            // 这一步之前完全没有，header 渐变永远停在 defaultConfiguration 的静态
            // 出厂蓝，不跟 mainColor 走。
            themeUpdate.headerGradient = resolveHeaderGradient(
                themeUpdate.mainColor || this.state.theme.mainColor,
                wc,
                window.laneChatCustomizations
            );
            debugLog('[Widget] Applied widget_config:', wc);
        }

        // After setting appConfig, use backend-provided agents
        if (config.header_agents && config.header_agents.length > 0) {
            themeUpdate.headerAgents = config.header_agents;
        }
        if (config.agents_online_count !== undefined) {
            themeUpdate.agentsOnlineCount = config.agents_online_count;
        }
        if (config.avg_reply_time) {
            themeUpdate.avgReplyTime = config.avg_reply_time;
        }

        // 更新theme状态
        if (this._isMounted) {
            // 首次访问落地视图：/usage/start 的完整配置到达，跟 preInitClient 早期
            // 上色那条路径共用同一个判定（_maybeAutoLandOnFirstVisit）——万一
            // /v1/chat 的 widget_config 当时是空的（理论边缘态），这里是第二次
            // 机会。同样发生在 configReady 变 true 之前的这次 setState 里，用户
            // 看不到切换过程。
            const landingUpdate = this._maybeAutoLandOnFirstVisit(themeUpdate);
            this.setState({ theme: themeUpdate, ...(landingUpdate || {}) }, () => {
                debugLog('[Widget] Theme state updated:', this.state.theme.closedStyle);
            });
        }

        // 向聊天组件发送配置
        setTimeout(() => {
            if (this.chatFrameRef.current) {
                const chatInstance = this.chatFrameRef.current.getChatInstance();
                if (chatInstance && chatInstance.applyAppConfig) {
                    const appConfigPayload = {
                        welcome_message: config.welcome_message,
                        service_title: config.service_title,
                        show_agent_avatar: config.show_agent_avatar,
                        guide_text: config.guide_text,
                        offline_message: config.offline_message,
                    };
                    // 付费版「隐藏 Powered by」。真源是 /usage/start 的 show_branding
                    // （UsageController 按套餐算好的），而唯一的读者是 chat.js 的
                    // state.appConfig —— 这个 payload 是它的两个写入口之一，之前没带
                    // 这个键，于是付费商户勾了开关、账单照付，底部品牌行一直还在。
                    // 归一化成严格布尔：读者写的是 `!== false`，'0'/0 会被当成 true。
                    if (config.show_branding !== undefined) {
                        appConfigPayload.show_branding = !(
                            config.show_branding === false ||
                            config.show_branding === 0 ||
                            config.show_branding === '0'
                        );
                    }
                    // 兜底：顶层没下发时，回落到 widget_config.show_agent_avatar
                    const wc = config.widget_config;
                    if (appConfigPayload.show_agent_avatar === undefined &&
                        wc && typeof wc === 'object' && wc.show_agent_avatar !== undefined) {
                        appConfigPayload.show_agent_avatar = wc.show_agent_avatar;
                    }
                    chatInstance.applyAppConfig(appConfigPayload);
                }
            }
        }, 500);

        // Apply other UI configurations
        debugLog('Configuration applied:', {
            knowledge_enabled: config.knowledge_enabled,
            service_title: config.service_title,
            show_agent_avatar: config.show_agent_avatar,
            guide_text: config.guide_text,
            close_button_style: config.close_button_style,
            closedStyle: themeUpdate.closedStyle,
            theme: config.theme
        });
    }

    componentWillUnmount() {
        this._isMounted = false; // Component is about to be unmounted
        // 退订语言 chunk 通知
        if (this._unsubLocale) { this._unsubLocale(); this._unsubLocale = null; }
        // 退订系统亮暗切换监听
        if (this._darkMq && this._onDarkMqChange) {
            try {
                if (this._darkMq.removeEventListener) this._darkMq.removeEventListener('change', this._onDarkMqChange);
                else if (this._darkMq.removeListener) this._darkMq.removeListener(this._onDarkMqChange);
            } catch (e) { /* ignore */ }
            this._darkMq = null;
            this._onDarkMqChange = null;
        }
        // 移除消息监听器
        window.removeEventListener('message', this.handleMessage);
        // 移除页面离开监听器
        window.removeEventListener('beforeunload', this.handlePageUnload);
        window.removeEventListener('pagehide', this.handlePageUnload);
        // 清理配置加载兜底定时器
        if (this._configTimer) {
            clearTimeout(this._configTimer);
        }
        // 清理 teaser 定时器，避免 unmount 后 setState 报 warning
        if (this._teaserShowTimer) { clearTimeout(this._teaserShowTimer); this._teaserShowTimer = null; }
        if (this._teaserAutoTimer) { clearTimeout(this._teaserAutoTimer); this._teaserAutoTimer = null; }
        if (this._teaserPollTimer) { clearTimeout(this._teaserPollTimer); this._teaserPollTimer = null; }
        this._removeLabelCollapseListeners();
        if (this._labelCloseTimer) { clearTimeout(this._labelCloseTimer); this._labelCloseTimer = null; }
        // 清理关闭动画定时器与 visual viewport 监听
        if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
        if (this._headerHoverTimer) { clearTimeout(this._headerHoverTimer); this._headerHoverTimer = null; }
        if (this._vvRaf) { cancelAnimationFrame(this._vvRaf); this._vvRaf = null; }
        if (this._resizeRaf) { cancelAnimationFrame(this._resizeRaf); this._resizeRaf = null; }
        if (typeof window !== 'undefined' && window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this._updateViewportAnchor);
            window.visualViewport.removeEventListener('scroll', this._updateViewportAnchor);
            window.visualViewport.removeEventListener('resize', this._updateKeyboardViewport);
            window.visualViewport.removeEventListener('scroll', this._updateKeyboardViewport);
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this._updateViewportAnchor);
            // 顺手补上：componentDidMount 里给 window resize 也挂了
            // _updateKeyboardViewport（部分 Android 机型只派发 window resize，不
            // 派发 visualViewport resize，见那边注释），这里之前漏了对应的移除，
            // unmount 后这个监听器一直挂着——不是本次 bug 的根因，但既然在改
            // 键盘适配这块，顺带修掉。
            window.removeEventListener('resize', this._updateKeyboardViewport);
            window.removeEventListener('resize', this._onHostResize);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('focusin', this._onFocusMaybeKeyboard, true);
            document.removeEventListener('focusout', this._onFocusMaybeKeyboard, true);
        }
        if (this._audioCtx && this._audioCtx.close) {
            try { this._audioCtx.close(); } catch (e) { /* ignore */ }
            this._audioCtx = null;
        }
    }

    // 处理页面离开事件
    handlePageUnload() {
        // 防止重复发送
        if (this.endBeaconSent) {
            debugLog('[Widget] End beacon already sent, skipping');
            return;
        }

        if (!this.sessionId && !this.visitorId) {
            debugLog('[Widget] No session_id or visitorId, skipping end beacon');
            return;
        }

        // 标记为已发送
        this.endBeaconSent = true;

        // 计算浏览时长（秒）
        const duration = Math.floor((Date.now() - this.pageLoadTime) / 1000);

        // 使用 GET 请求，便于调试
        const params = new URLSearchParams({
            session_id: this.sessionId || '',
            fp: this.visitorId || '',
            duration: duration,
            timestamp: Date.now()
        });
        const apiUrl = `${ApiService.baseURL}/usage/end?${params.toString()}`;

        debugLog('[Widget] Sending end beacon:', {
            session_id: this.sessionId,
            fp: this.visitorId,
            duration: duration,
            url: apiUrl
        });

        // 使用 sendBeacon API (推荐) 或降级到 Image
        if (navigator.sendBeacon) {
            navigator.sendBeacon(apiUrl);
        } else {
            // 降级方案：使用 Image 发送 GET 请求
            try {
                const img = new Image();
                img.src = apiUrl;
            } catch (e) {
                console.error('Failed to send end beacon:', e);
            }
        }
    }

    // ---------- 统一尺寸模型（唯一出口，三个视图共用） ----------
    // 硬规则：只有「是否文章详情」这一个条件决定尺寸 —— tab 之间来回切换（home /
    // 会话列表 / 聊天 / 知识库列表 / 搜索）面板纹丝不动。
    //   常规档：400 × min(700, 100vh - 48)
    //   详情档：580 × min(860, 100vh - 32)（长文既要行长也要行数；小视口由 -32 兜住）
    // 两个方向都带 260ms 过渡，且面板 fixed 右下锚定 → 只向左、向上生长。
    _getPanelSize = (view) => {
        const theme = this.state.theme;
        const headerH = this.getHeaderHeight();
        // desktopHeight 配置定义为「72px header 下的内容高」（628 → 总高 700 基准）。
        // 面板总高与 header 档位无关恒为 700 上限；更高的 header 由内容区吸收。
        const idealTotal = (parseInt(theme.desktopHeight, 10) || 628) + 72;
        const floor = headerH + 320;
        const defaultH = Math.max(floor, Math.min(idealTotal, window.innerHeight - 48));
        let width, height;
        if (view === 'detail') {
            const configuredW = parseInt(theme.knowDetailWidth, 10) || 580;
            width = window.innerWidth < 620
                ? Math.max(280, window.innerWidth - 40)
                : Math.min(configuredW, window.innerWidth - 40);
            const configuredH = parseInt(theme.knowDetailHeight, 10) || 860;
            // 底边缘锚定：详情只向上生长。能长多高由「面板底边到视口顶」的余量决定
            // —— 越过这条线就只能靠下钻/挤压来腾空间，底边就会动，还可能压住浮球。
            // 再用 max(defaultH) 兜底：矮视口下详情绝不比常规档更矮（否则进详情面板
            // 反而缩了，底边同样会跳）。
            const roomAbove = window.innerHeight - this._panelBottomInset() - 24;
            height = Math.max(defaultH, Math.min(configuredH, window.innerHeight - 32, roomAbove));
        } else {
            width = Math.min(parseInt(theme.desktopWidth, 10) || 400, Math.max(280, window.innerWidth - 24));
            height = defaultH;
        }
        return { width, height };
    }

    // 面板底边到视口底的距离（桌面）。button 档要让出右下角 FAB 关闭钮的位置。
    _panelBottomInset = () => {
        const theme = this.state.theme;
        const bottom = parseInt(theme.btnPcBottom, 10) || 20;
        return theme.closedStyle === 'button'
            ? bottom + (parseInt(theme.dialogButtonSpacing, 10) || 72)
            : bottom;
    }

    // 当前应该用哪一档尺寸：只有「知识库文章详情」是第二档
    _currentPanelView = () => {
        return (this.state.widgetClass === 'know-widget' && this._knowView === 'detail') ? 'detail' : 'default';
    }

    // 处理知识库视图变化（列表 <-> 详情）：尺寸统一走 _getPanelSize（render 期实时
    // 计算），这里只记录当前子视图并触发重渲染 —— 不再各自维护一套宽高。
    handleKnowViewChange = (view, scale) => {
        debugLog('[handleKnowViewChange]', view, scale);
        this._knowView = view;
        this.forceUpdate();
        this._repositionHeaderPopover();
    }

    // 宿主窗口 resize（桌面拖拽 / 移动端旋转）：重渲染让 _getPanelSize 的视口
    // clamp 生效（旧代码只在 render 时读一次 innerHeight，之后窗口变矮面板不会缩）
    _onHostResize = () => {
        if (this._resizeRaf) return;
        this._resizeRaf = requestAnimationFrame(() => {
            this._resizeRaf = null;
            if (!this._isMounted) return;
            this.forceUpdate();
            // 面板尺寸/位置变了 → 弹层重新对准触发按钮
            this._repositionHeaderPopover();
            // 面板宽高带 CSS transition，这一帧量到的还是旧尺寸 —— 过渡结束后
            // 再量一次。语言弹层的 max-height 直接由面板高度推出，量早了会残留
            // 上一个尺寸的高度上限（矮面板下该滚的不滚）。
            if (this._resizeSettleTimer) clearTimeout(this._resizeSettleTimer);
            this._resizeSettleTimer = setTimeout(() => {
                this._resizeSettleTimer = null;
                if (this._isMounted) this._repositionHeaderPopover();
            }, 340);
        });
    }

    // handleMessage is defined above (merged)
    
    forwardMessageToChat = (messageData) => {
        // Chat component listens for 'message' events on window directly,
        // so dispatch the message data as a window postMessage.
        // 指定当前窗口 origin，而不是 '*'，避免消息被任意同页 iframe 捕获。
        //
        // __lcForwarded 标记这一份是"widget 转发给 chat 的"，widget 自己的
        // handleMessage 看到它就不再处理 —— 否则 navigate-to-chat 会在
        // window 上自激成死循环（见 handleMessage 里那条闸的注释）。
        window.postMessage({ ...messageData, __lcForwarded: true }, window.location.origin);
    }
    
    closeGame = () => {
        this.setState({
            showGame: false,
            gameUrl: null
        });
    }
    
    // SDK方法
    // 面板从「关」变「开」的唯一收敛点 —— 有好几条路会打开面板(浮动按钮、
    // navigate-to-chat、openChat、inline 模式一上来就是开的),都在这里汇合,
    // 避免逐个 setState 处挂钩子漏掉某一条。
    componentDidUpdate(prevProps, prevState) {
        // theme 引用变了（mainColor/暗色开关/dark_colors 任何一项都可能变）、系统
        // 亮暗切换、或 configReady 落定（暗色开关的 localStorage 缓存写侧要等它）
        // 就把 CSS 变量整套同步一遍——setProperty 幂等，代价可忽略；发送键/focus 环
        // 这些非 React inline-style 的元素、以及全部 --lc-* 暗色 token 都靠它。
        if (prevState.theme !== this.state.theme
            || prevState.prefersDark !== this.state.prefersDark
            || prevState.configReady !== this.state.configReady) {
            this._syncThemeCssVars();
        }

        // 商户配的 auto_open：闸门过关后弹一次。内部自带 done 标记 + 会话去重，
        // 每次 update 调也只会真正生效一次。
        this._maybeAutoOpen();
        this._syncLauncherLabel();

        // 开/关都上报 —— 服务端因此始终知道访客是不是正看着聊天面板
        // (比 visibility_status 的「标签页可见」更精确),而不只是知道"开过一次"。
        if (prevState.isChatOpen !== this.state.isChatOpen) {
            this._reportPanelState(this.state.isChatOpen);
            // 面板打开的唯一收敛点：浮球点击 / navigate-to-chat / openChat() 等所有
            // 打开路径都汇聚到这个 isChatOpen 跳变上，清未读徽标放这里就不会漏掉某一条。
            if (this.state.isChatOpen) {
                this._clearUnread();
                // 已互动 → 本 session 营销 teaser 一次性熄灭（渲染层同时消费该状态）
                this._markTeaserSessionDone();
                this._collapseLabel();
                if (this.state.teaserVisible) this.setState({ teaserVisible: false });
                // 滚动恢复 + 桌面 autofocus —— 修「刷新后首开停在会话顶部」：
                // 首滚可能发生在 display:none 里（no-op），这里在面板真正可见后补一次
                this._afterOpenChatSync();
            } else {
                // 面板关闭：收起头部弹层；清掉视图切换动画残留（display:none→block
                // 会重播 inline animation，不清的话下次开面板会先横滑一下）
                if (this.state.headerPopover || this.state.viewAnim) {
                    this.setState({ headerPopover: null, viewAnim: null });
                }
            }
            // opened/closed 总线事件也在这里统一发出——浮球点击、navigate-to-chat、
            // inline autoChatOpen、编程式 open()/close()/toggle() 全部经过这个 state 跳变，
            // 不会再有路径只翻 isChatOpen 却不 emit。widget-index.js 的 open()/close()/toggle()
            // 不再自己 _emit，避免这里和那里各发一次导致双发。
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit(this.state.isChatOpen ? 'open' : 'close');
            }
        }

        // 视图切换（home/chat/help）：方向感知 push/pop 过渡 + chat 视图首次
        // 可见时的滚动兜底（初开落在 home/help、之后才切进 chat 的场景）。
        if (prevState.widgetClass !== this.state.widgetClass) {
            const order = { 'home-widget': 0, 'chat-widget': 1, 'know-widget': 2 };
            const from = order[prevState.widgetClass] !== undefined ? order[prevState.widgetClass] : 1;
            const to = order[this.state.widgetClass] !== undefined ? order[this.state.widgetClass] : 1;
            if (!this._prefersReducedMotion()) {
                this.setState({
                    viewAnim: {
                        view: this.state.widgetClass,
                        dir: to >= from ? 'Push' : 'Pop',
                        nonce: ((this.state.viewAnim && this.state.viewAnim.nonce) || 0) + 1,
                    },
                });
            }
            if (this.state.isChatOpen && this.state.widgetClass === 'chat-widget') {
                this._afterOpenChatSync();
            }
            // 切视图时收起头部弹层
            if (this.state.headerPopover) this.setState({ headerPopover: null });
        }

        // 弹层开着时，任何会动到 header 高度 / 面板几何的重渲染都要重新对准触发
        // 按钮（配置改到只剩一个 icon、header 收敛档位变化、面板宽高过渡…）。
        // _repositionHeaderPopover 只在数值真变了才 setState，不会自激。
        if (this.state.headerPopover) this._repositionHeaderPopover();
    }

    // 告诉 chat 实例「面板打开了」→ 它发 WS panel_open → 服务端触发 first_visit。
    // chat 实例可能还没挂上(iframe 组件懒挂),重试几次;chat.js 那边还会再兜一层
    // (WS 没连上就等 register 回来补发)。
    // 把面板开/关状态传给 chat 实例(它持有 WS)。不做任何"发过没有"的记账 ——
    // 状态可以重复上报,是否触发 first_visit 由服务端按 chat 判定。
    // chat 实例可能还没挂上,重试几次;chat.js 那边还会在 register / 换会话时补发。
    _reportPanelState = (isOpen, attempt = 0) => {
        const inst = this.chatFrameRef && this.chatFrameRef.current
            && this.chatFrameRef.current.getChatInstance
            && this.chatFrameRef.current.getChatInstance();
        if (inst && typeof inst.reportPanelState === 'function') {
            inst.reportPanelState(isOpen);
            return;
        }
        if (attempt < 12) setTimeout(() => this._reportPanelState(isOpen, attempt + 1), 300);
    }

    openChat = () => {
        if (!this.state.isChatOpen) {
            this.setState({
                pristine: false,
                isChatOpen: true,
            });
            if (!this.wasChatOpened()) {
                this.setCookie();
            }
        }
    }
    
    closeChat = () => {
        // Inline / full-page host (widget.html, iframe): closing would blank the
        // page since the panel IS the page. _requestClose 内部会拦掉 inline。
        this._requestClose();
    }
    
    toggleChat = () => {
        this.onClick();
    }
    
    isChatOpen = () => {
        return this.state.isChatOpen;
    }

    // laneChat.unreadCount() 依赖 —— 浮球徽标当前数字
    getUnreadCount = () => this.state.unreadBadgeCount;

    // ---------- 动效红线：prefers-reduced-motion 全 guard ----------
    _prefersReducedMotion = () => {
        try {
            return typeof window !== 'undefined'
                && window.matchMedia
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (e) { return false; }
    }

    // ---------- 新消息提示音（Web Audio 短音，无外部资源） ----------
    _readSoundPref = () => {
        try { return localStorage.getItem('lane_widget_sound') !== 'off'; } catch (e) { return true; }
    }

    _toggleSound = () => {
        const next = !this.state.soundEnabled;
        try { localStorage.setItem('lane_widget_sound', next ? 'on' : 'off'); } catch (e) { /* ignore */ }
        this.setState({ soundEnabled: next });
        // 开启时试听一声：即时反馈 + 借这次用户手势解锁 AudioContext
        if (next) this._playNotifySound(true);
    }

    _playNotifySound = (force = false) => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!this._audioCtx) this._audioCtx = new Ctx();
            const ctx = this._audioCtx;
            if (ctx.state === 'suspended') {
                // 无用户手势时 resume 会被浏览器拒绝 —— 静默放弃，不刷 console
                const p = ctx.resume();
                if (p && p.catch) p.catch(() => {});
                if (ctx.state === 'suspended' && !force) return;
            }
            const now = ctx.currentTime;
            // 双音上行短提示音（880→1175Hz），~180ms，音量克制
            [[880, 0], [1174.66, 0.09]].forEach(([freq, offset]) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, now + offset);
                gain.gain.setValueAtTime(0, now + offset);
                gain.gain.linearRampToValueAtTime(0.07, now + offset + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.16);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(now + offset);
                osc.stop(now + offset + 0.18);
            });
        } catch (e) { /* 声音永不阻塞主流程 */ }
    }

    // ---------- 关闭出场动画 ----------
    // 所有关闭路径（header X / FAB close / SDK close() / toggle）都汇到这里：
    // 先播反向出场（scale+fade 回浮球角落），动画结束再真正翻 isChatOpen。
    // 出场时长走结构 token（mobile base=200 / PC fast=150，出场比入场快一档，
    // 见 render 处 lcPanelOut* 的注释）；这里的卸载定时器必须 ≥ CSS 出场时长，
    // 取 210ms = 最长出场档(200) + 10ms 余量。prefers-reduced-motion 时 CSS
    // 变量已被压成 1ms（token 层统一降级），且这里直接关，无动画。
    _requestClose = () => {
        if (typeof window !== 'undefined' && window.laneChatMode === 'inline') return;
        if (!this.state.isChatOpen || this.state.isClosing) return;
        if (this._prefersReducedMotion()) {
            this.setState({ isChatOpen: false });
            return;
        }
        this.setState({ isClosing: true });
        this._closeTimer = setTimeout(() => {
            this._closeTimer = null;
            if (this._isMounted) this.setState({ isChatOpen: false, isClosing: false });
        }, 210);
    }

    // ---------- 面板打开后的收敛动作：滚动恢复 + 桌面 autofocus ----------
    _afterOpenChatSync = (attempt = 0) => {
        const inst = this.chatFrameRef && this.chatFrameRef.current
            && this.chatFrameRef.current.getChatInstance
            && this.chatFrameRef.current.getChatInstance();
        if (!inst) {
            if (attempt < 10) setTimeout(() => this._afterOpenChatSync(attempt + 1), 150);
            return;
        }
        requestAnimationFrame(() => {
            if (!this._isMounted || !this.state.isChatOpen) return;
            if (this.state.widgetClass !== 'chat-widget') return;
            if (inst.restoreScrollOnOpen) inst.restoreScrollOnOpen();
            else if (inst.instantScrollToBottom) inst.instantScrollToBottom();
            const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
            // 桌面端打开即聚焦输入框；移动端不强制，避免键盘弹跳
            if (!mobile && inst.focusComposer) inst.focusComposer();
        });
    }

    // ---------- 宿主页横向溢出防护：visual viewport 锚定 ----------
    // 不响应式宿主页（移动端常见）会把 layout viewport 撑宽（如 490px），
    // position:fixed; right:18px 的浮球/面板按 layout viewport 布局 → 滑出可视区，
    // widget 完全不可用。检测到「layout 比 visual 宽」时改用 left/top + visual
    // viewport 矩形锚定。正常页面（无溢出）完全不介入。
    /**
     * 软键盘适配 —— 把移动端全屏面板钉到 visual viewport 上。
     *
     * 面板是 position:fixed;inset:0;height:100%（mobileOpenWrapperStyle），
     * 而各平台对"fixed 元素 + 软键盘"的处理完全不同：
     *   · iOS Safari —— layout viewport 不缩，fixed 相对它定位；系统还会把页面
     *     顶上去让 offsetTop 变正。面板既不变矮、还整体偏上。
     *   · Android Chrome —— layout viewport 不缩，visualViewport 缩；面板底部
     *     被键盘盖住。
     *   · Android WebView(adjustResize) —— window 直接缩到位，本来就对。
     *
     * 与其按平台分支（之前那版就是这么错的：判据在 Android Chrome 上恒为假，
     * 结果给两台 Android 套上了 iOS 的补偿，顶部空一块、底部顶出屏幕），
     * 这里**不做任何平台判断**，直接用 visualViewport 的矩形覆盖面板：
     *   top = offsetTop, height = height。
     * 三种平台都自然落到正确位置 —— WebView 上 vv 恒等于 layout viewport，
     * 覆盖等于没覆盖。
     *
     * 关键点：改的必须是**包裹层本身**。之前 chat.js 改的是它里面的子 div，
     * 子 div 缩了、外面这层白底的 fixed 容器没缩，露出来的白边正是用户看到的
     * 那道空隙。
     */
    // focus/blur 兜底入口——只在移动端、且目标是文本输入类元素时才触发（桌面端
    // 聚焦输入框不代表键盘；非输入元素聚焦跟键盘无关，比如点了 tab/按钮）。
    // e.target 经过 shadow 边界会被 retarget，但这里不需要精确知道是哪个输入框，
    // 只要"有键盘相关的焦点变化"这个信号本身，退回保守判断即可。
    _onFocusMaybeKeyboard = (e) => {
        const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
        if (!mobile) return;
        const tag = e && e.target && e.target.tagName;
        const editable = e && e.target && e.target.isContentEditable;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) {
            this._updateKeyboardViewport();
        }
    }

    _updateKeyboardViewport = () => {
        if (this._kbRaf) return;
        this._kbRaf = requestAnimationFrame(() => {
            this._kbRaf = null;
            if (!this._isMounted) return;
            const vv = typeof window !== 'undefined' && window.visualViewport;
            let rect = null;
            // 捏合缩放时不介入（scale>1 是用户主动放大，不是键盘）
            if (vv && (vv.scale || 1) <= 1.01) {
                const top = Math.round(vv.offsetTop || 0);
                const height = Math.round(vv.height || 0);
                const layoutH = window.innerHeight || 0;
                // 只有"可视区确实小于布局视口"或"被顶上去了"才需要覆盖；
                // 其余情况回 null，保持原样式，零介入。
                if (height > 0 && (layoutH - height > 1 || top > 0)) {
                    rect = { top, height };
                }
            }
            const prev = this.state.kbRect;
            const same = (!rect && !prev) ||
                (rect && prev && rect.top === prev.top && rect.height === prev.height);
            if (!same) this.setState({ kbRect: rect });
        });
    }

    _updateViewportAnchor = () => {
        if (this._vvRaf) return;
        this._vvRaf = requestAnimationFrame(() => {
            this._vvRaf = null;
            if (!this._isMounted) return;
            const vv = typeof window !== 'undefined' && window.visualViewport;
            let rect = null;
            // scale>1 是用户主动捏合缩放，不是宿主页溢出 —— 不介入
            if (vv && (vv.scale || 1) <= 1.01 && (window.innerWidth - vv.width) > 4) {
                rect = {
                    left: Math.round(vv.offsetLeft),
                    top: Math.round(vv.offsetTop),
                    width: Math.round(vv.width),
                    height: Math.round(vv.height),
                };
            }
            const prev = this.state.vvRect;
            const same = (!rect && !prev) || (rect && prev
                && rect.left === prev.left && rect.top === prev.top
                && rect.width === prev.width && rect.height === prev.height);
            if (!same) this.setState({ vvRect: rect });
        });
    }

    // 各渲染分支的 visual viewport 修正样式；未检测到溢出时返回 null（零介入）
    _vvAnchorStyle = (kind) => {
        const rect = this.state.vvRect;
        if (!rect) return null;
        const theme = this.state.theme;
        if (kind === 'fab') {
            const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
            const rightInset = parseInt((mobile ? theme.btnMobileRight : theme.btnPcRight) || '18', 10) || 18;
            const bottomInset = parseInt((mobile ? theme.btnMobileBottom : theme.btnPcBottom) || '18', 10) || 18;
            const FAB = 56;
            return {
                left: `${Math.max(0, rect.left + rect.width - rightInset - FAB)}px`,
                top: `${Math.max(0, rect.top + rect.height - bottomInset - FAB)}px`,
                right: 'auto',
                bottom: 'auto',
            };
        }
        if (kind === 'mobileOpen') {
            return {
                left: `${rect.left}px`,
                top: `${rect.top}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                right: 'auto',
                bottom: 'auto',
            };
        }
        if (kind === 'desktopPanel') {
            // 必须用 right 锚定，不能换算成 left。
            // 旧实现把右锚改写成 `left = 视口右 - inset - w`，w 取自 state.wrapperWidth
            // ——那是「上一次视图切换时写入的宽度」（常规档 400），而真实宽度由
            // _getPanelSize 在 render 期给出（详情档 580）。进详情时 left 不动、宽度
            // +180 → 右边缘直接冲出视口 180px。而且这条路径在「经典滚动条」浏览器
            // （Windows/Linux Chrome：innerWidth 含滚动条、visualViewport.width 不含，
            // 差 15px > 4px 阈值）上几乎每个页面都会命中，只有 overlay 滚动条的
            // headless/macOS 才躲过 —— 所以上一轮没测出来。
            // 换成 right 之后宽度变化只向左延展，右边缘与宽度完全解耦。
            const rightInset = parseInt(theme.btnPcRight || '20', 10) || 20;
            // fixed 的 right 相对 layout viewport（documentElement.clientWidth，已排除
            // 经典滚动条）；rect 是 visual viewport。两者相等时补偿为 0，零介入。
            const layoutW = (typeof document !== 'undefined' && document.documentElement
                && document.documentElement.clientWidth) || window.innerWidth;
            return {
                right: `${Math.max(0, layoutW - (rect.left + rect.width) + rightInset)}px`,
                left: 'auto',
            };
        }
        return null;
    }

    // 视图切换动画样式：只挂在刚切入的视图容器上。A/B 交替保证同名重播。
    _viewAnimStyle = (view) => {
        const va = this.state.viewAnim;
        if (!va || va.view !== view || this._prefersReducedMotion()) return null;
        const parity = va.nonce % 2 === 0 ? 'A' : 'B';
        return { animation: `lcView${va.dir}${parity} var(--lc-dur-base) var(--lc-ease-out)` };
    }

    // laneChat.show()/hide() 依赖 —— 只控制浮球显隐，不影响已打开的面板
    setLauncherHidden = (hidden) => {
        this.setState({ launcherHidden: !!hidden });
    };

    // 面板关着时收到新消息才累加；同时广播 unread_changed 给 laneChat.on() 订阅者
    _bumpUnread = () => {
        const next = this.state.unreadBadgeCount + 1;
        this.setState({ unreadBadgeCount: next });
        if (window.laneChat && window.laneChat._emit) window.laneChat._emit('unread_changed', { count: next });
    };

    // 面板打开时清零；0 时不重复 emit，避免每次开面板都刷一次无意义事件
    _clearUnread = () => {
        if (this.state.unreadBadgeCount === 0) return;
        this.setState({ unreadBadgeCount: 0 });
        if (window.laneChat && window.laneChat._emit) window.laneChat._emit('unread_changed', { count: 0 });
    };

    updateTheme = (themeConfig) => {
        this.setState({
            theme: { ...this.state.theme, ...themeConfig }
        });
    }

    // opts: { article, returnTo } —— 带 article 时直达该文章详情（home 的帮助文章
    // 条目）。同第 28 项：目标必须在这一次调用里带到底，绝不能"先进列表再跳详情"。
    //   · Know 还没挂载 → _knowInitialArticle 让它挂载即渲染详情；
    //   · 已挂载 → 同步 openArticle（不是 setTimeout），与下面的 setState 同一次提交。
    // returnTo 记住来路：从 home 进来的详情，点返回要回 home，不是掉进知识库列表。
    switchToKnowledge = (opts = {}) => {
        const article = opts.article || null;
        const knowInst = this.knowFrameRef && this.knowFrameRef.current
            && this.knowFrameRef.current.getKnowInstance
            && this.knowFrameRef.current.getKnowInstance();
        if (article) {
            this._knowInitialArticle = article;
            this._knowReturnTo = opts.returnTo || null;
            // 同一次提交里就按详情档算尺寸，面板不会先跳成 400 再变 580
            this._knowView = 'detail';
            if (knowInst && knowInst.openArticle) {
                knowInst.openArticle(article, { returnTo: opts.returnTo || null });
            }
        } else {
            this._knowInitialArticle = null;
            this._knowReturnTo = null;
            this._knowView = 'list';
            if (this.knowFrameRef && this.knowFrameRef.current && this.knowFrameRef.current.resetToList) {
                this.knowFrameRef.current.resetToList();
            }
        }
        const { theme } = this.state;
        const chatWidth = parseInt(theme.desktopWidth) || 400;
        const configuredKnowWidth = theme.knowWidth ? parseInt(theme.knowWidth) : chatWidth;
        const knowWidth = Math.max(configuredKnowWidth, chatWidth) + 'px';
        this.setState({
            widgetClass: 'know-widget',
            wrapperWidth: { width: knowWidth },
            knowInitialized: true,
            nav: (opts.lateral ? navSwitchTab : navGo)(this.state.nav, NAV_KNOWLEDGE),
        });
    }

    // opts: { subview: 'list' } | { chatId } —— 目标子视图必须在这一次调用里带下去。
    // 旧实现是 switchToChat() 之后 setTimeout(…, 100) 再翻子视图 → 中间整整渲染了
    // 一帧「某个会话」才跳到会话列表，用户看到闪一下错误目标。这里改成一步到位：
    //   · chat 组件还没挂载 → 用 _chatInitialViewMode 让它挂载即渲染目标视图；
    //   · 已挂载 → 同步调用（不是 setTimeout），与下面的 setState 在同一次 React
    //     提交里生效，chat 容器从 display:none 变可见时就已经是目标视图。
    switchToChat = (opts = {}) => {
        // 任何显式切到 Chat 的调用（tab 点击路由到这里的除外，那条走 onClick
        // 自己的 data-type 分支，见其注释）都代表导航已经发生过一次——首次访问
        // 的自动落地判定（_maybeAutoLandOnFirstVisit）之后不该再插手，配置数据
        // 比这次调用晚到也不能把用户/外部系统已经导向的视图拽走。
        this._explicitViewTargeted = true;
        const wantList = opts.subview === 'list';
        this._chatInitialViewMode = wantList ? 'chatList' : null;
        // 只有"明确指定去哪"时才改子视图记录。不带参数的调用(Chat tab 点击)
        // 刻意保持原样 —— chat frame 也保持原样,记录必须跟着画面走,否则就是
        // 这次要修的那个不符:画面在列表、状态说会话。
        if (wantList) this._chatSubview = 'list';
        else if (opts.chatId) this._chatSubview = 'conversation';
        const inst = this.chatFrameRef.current
            && this.chatFrameRef.current.getChatInstance
            && this.chatFrameRef.current.getChatInstance();
        if (inst) {
            this._navSyncing = true;
            try {
                if (wantList && inst.showChatList) inst.showChatList();
                else if (opts.chatId && inst.switchToChat) inst.switchToChat(opts.chatId);
            } finally {
                this._navSyncing = false;
            }
        }
        this.setState({
            widgetClass: 'chat-widget',
            wrapperWidth: { width: this.state.theme.desktopWidth || this.props.conf.desktopWidth },
            chatInitialized: true,
            nav: (opts.lateral ? navSwitchTab : navGo)(this.state.nav, wantList ? NAV_CHAT_LIST : NAV_CHAT),
        });
    }

    switchToHome = () => {
        // 同 switchToChat 顶部注释：显式导航发生过，自动落地判定让路。
        this._explicitViewTargeted = true;
        this.setState({
            widgetClass: 'home-widget',
            homeInitialized: true,
            wrapperWidth: { width: this.state.theme.desktopWidth || this.props.conf.desktopWidth },
            nav: navGo(this.state.nav, NAV_HOME),
        });
    }

    // chat.js 自己翻子视图时的回调（会话列表 ↔ 某条会话）——widget 不可能靠
    // 猜知道 chat.js 现在渲的是列表还是会话，而"从列表点进会话"正是需求里
    // "返回要回到那个来路"的第二条主线，来路必须在发生的那一刻被记下来。
    // 注意 navGo 对"目标 === 当前"是空操作，所以 widget 主动调用
    // switchToChat({subview:'list'}) 触发的这次回调不会重复压栈。
    handleChatViewModeChange = (mode) => {
        // 真实子视图的唯一真源。左上角按钮要靠它区分"会话列表"(顶层)和"某个
        // 会话"(下钻),而 nav.view 会短暂与画面不符 —— 点 Chat tab 时
        // switchToChat() 记的是 NAV_CHAT,而 chat frame 其实还停在列表上。
        this._chatSubview = mode === 'chatList' ? 'list' : 'conversation';
        // widget 自己 imperative 翻子视图时 chat.js 也会回调 —— 那一路的 nav
        // 已经由调用方算好（switchToChat 压栈 / _applyNavState 弹栈），这里再
        // 算一次会重复压栈，用旗标吞掉。
        if (this._navSyncing) return;
        const target = mode === 'chatList' ? NAV_CHAT_LIST : NAV_CHAT;
        this.setState(prev => ({ nav: navGo(prev.nav, target) }));
    }

    handleHomeNavigate = (target, data) => {
        if (target === 'chat') {
            // 一次到位：「查看全部对话」直达会话列表，最近会话卡直达那条会话，
            // 中间不渲染任何其它子视图（见 switchToChat 注释）。
            if (data && data.showList) this.switchToChat({ subview: 'list' });
            else if (data && data.chatId) this.switchToChat({ chatId: data.chatId });
            else this.switchToChat();
        } else if (target === 'knowledge') {
            // 帮助文章条目 → 直达那篇文章的详情，并记住来路是 home（返回回 home）。
            // 无 article 时（Help tab）走列表分支：switchToKnowledge 内部先同步
            // resetToList() 再 setState，所以也不会闪一帧详情。
            if (data && (data.article || data.articleId)) {
                this.switchToKnowledge({ article: data.article || { id: data.articleId }, returnTo: 'home' });
            } else {
                this.switchToKnowledge();
            }
        }
    }

    // 知识库详情里点返回、且这次是从 home 进来的 → 回 home（用户来的地方）。
    // 走统一的 _goBack：来路已经在 switchToKnowledge 那一刻压过栈了，这里不用
    // 也不该再判一次"home 开没开"——那正是旧实现跳错的根因。
    handleKnowExit = (target) => {
        if (target === 'home') this._goBack();
    }

    /**
     * 返回一步（header 左上角返回按钮 / 知识库详情返回的唯一出口）。
     *
     * 目标由**来路**决定（chat-navigation.js 的 originStack），不是由当前视图
     * 加配置去猜：
     *   · 从 Home 进的会话 → 回 Home；
     *   · 从会话列表进的会话 → 回会话列表；
     *   · 没有来路（落地即会话，商户没开 Home 时的常态）→ 关闭整个聊天。
     * 最后这条就是需求里的"没开启 home 则直接关闭"——它是"栈空"的自然结果，
     * 不是再读一次 homePageEnabled。native 两端的 facadeBack 返回 null 时同样
     * 收尾整个界面（Activity finish / dismiss），三端落点一致。
     */
    _goBack = () => {
        const next = navBack(this.state.nav);
        if (!next) {
            this._requestClose();
            return;
        }
        this._applyNavState(next);
    }

    // 把返回后的目标状态渲出来。这里刻意不复用 switchToXxx —— 那些是"前进"，
    // 会再压一次栈；返回必须原样采用 navBack 已经算好的栈。
    _applyNavState = (next) => {
        this._explicitViewTargeted = true;
        const width = { width: this.state.theme.desktopWidth || this.props.conf.desktopWidth };
        if (next.view === NAV_HOME) {
            this.setState({ widgetClass: 'home-widget', homeInitialized: true, wrapperWidth: width, nav: next });
            return;
        }
        if (next.view === NAV_KNOWLEDGE) {
            const { theme } = this.state;
            const chatWidth = parseInt(theme.desktopWidth) || 400;
            const configuredKnowWidth = theme.knowWidth ? parseInt(theme.knowWidth) : chatWidth;
            if (this.knowFrameRef && this.knowFrameRef.current && this.knowFrameRef.current.resetToList) {
                this.knowFrameRef.current.resetToList();
            }
            this._knowInitialArticle = null;
            this._knowReturnTo = null;
            this._knowView = 'list';
            this.setState({
                widgetClass: 'know-widget',
                wrapperWidth: { width: Math.max(configuredKnowWidth, chatWidth) + 'px' },
                knowInitialized: true,
                nav: next,
            });
            return;
        }
        // chat / chatList —— 同一个 widgetClass，子视图在 chat.js 里。同步翻，
        // 与 switchToChat 一样避免中间闪一帧错误子视图。
        const wantList = next.view === NAV_CHAT_LIST;
        this._chatInitialViewMode = wantList ? 'chatList' : null;
        const inst = this.chatFrameRef.current
            && this.chatFrameRef.current.getChatInstance
            && this.chatFrameRef.current.getChatInstance();
        if (inst) {
            this._navSyncing = true;
            try {
                if (wantList && inst.showChatList) inst.showChatList();
                else if (!wantList && inst.hideChatList) inst.hideChatList();
            } finally {
                this._navSyncing = false;
            }
        }
        this.setState({ widgetClass: 'chat-widget', wrapperWidth: width, chatInitialized: true, nav: next });
    }

    handleListButtonClick = (e) => {
        e.stopPropagation();
        this._goBack();
    }

    /**
     * 文章详情屏上,左上角那颗是 **Home** 而不是返回(见 _navButtonVisible 第 4 条)。
     * 走 navigateHome 而不是 switchToHome:Home 被商户关掉时它自带降级,
     * 与 bridge 的 navigate{view:'home'} 共用同一条门控,不另开一条判断。
     */
    handleHomeButtonClick = (e) => {
        e.stopPropagation();
        this.navigateHome();
    }

    /** 会话内那颗:上到会话列表(它的真父级,也是列表唯一的入口 —— 列表没有 tab)。 */
    handleChatListButtonClick = (e) => {
        e.stopPropagation();
        this.switchToChat({ subview: 'list' });
    }

    // native-bridge navigate {view:'home'} 的门控封装（fix round 1）：跟
    // handleListButtonClick 走同一条 resolveHomeViewAction 判断——原生容器不能靠
    // 发一个 bridge 事件就强行渲出租户在 dash 关掉的 Home 首页，未开启时降级成
    // 会话列表（switchToChat({subview:'list'})，跟点击列表按钮时的降级路径完全
    // 一致）。独立方法而不是让 widget-index.js 直接摸 state.theme.homePageEnabled——
    // 内部导航决策留在 Widget 内部，调用方只管"要去 home"这个意图。
    navigateHome = () => {
        if (resolveHomeViewAction(this.state.theme.homePageEnabled) === 'home') {
            this.switchToHome();
        } else {
            debugLog('[Widget] navigate: homePageEnabled=false, downgrading home→chat list');
            this.switchToChat({ subview: 'list' });
        }
    }

    // 左上角**返回**按钮。图标跟着"返回落点"走（回 Home 画房子、回会话列表画
    // 列表、其余画返回箭头），而不是跟着 homePageEnabled 这类配置走——按钮画的
    // 是"按下去会去哪"，配置只是间接线索，之前正是靠它猜才会跳错。
    //
    // 没有来路时整个按钮不渲染：此刻返回等于关闭，而 header 右侧本来就有 ✕，
    // 再摆一个"看起来像导航、按下去却关闭"的按钮是在骗人。渲染门控与
    // showListButton===false 的商户硬关闭、_navRevealed 的 always/hover 正交，
    // 三者都得过。
    _backTarget = () => navBackTarget(this.state.nav);

    renderListButton = (positionStyle) => {
        const { theme } = this.state;
        if (theme.showListButton === false) return null;
        // 落点由"你在哪"唯一决定(cornerButtonTarget),不看返回栈。
        const target = this._cornerTarget();
        if (!target) return null;
        if (target === NAV_HOME && !this._homeReachable()) return null;
        const isHomeButton = target === NAV_HOME;
        const label = target === NAV_HOME ? t('nav.home')
            : (target === NAV_CHAT_LIST ? t('nav.chat_list') : t('know.back'));
        return (
            <button
                // 稳定的测试/自动化钩子：header 里有好几颗 aria-label 按钮
                // （语言/设置/关闭），返回钮必须能被无歧义地选中。
                data-lc-nav="back"
                data-lc-back-target={target}
                style={{ ...chatListButtonStyle, ...positionStyle }}
                onClick={isHomeButton ? this.handleHomeButtonClick : (this._backTarget() ? this.handleListButtonClick : this.handleChatListButtonClick)}
                // hover 显隐由头像区触发；按钮自己也要维持 hover 态，否则从头像
                // 移到按钮的瞬间按钮会消失（headerHovered 只由头像区写入）
                onMouseEnter={() => this._setHeaderHover(true)}
                onMouseLeave={() => this._setHeaderHover(false)}
                title={label}
                aria-label={label}
            >
                {target === NAV_HOME ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                ) : target === NAV_CHAT_LIST ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12h18M3 6h18M3 18h18"/>
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5"/>
                        <polyline points="12 19 5 12 12 5"/>
                    </svg>
                )}
            </button>
        );
    }

    // 左上角导航按钮（renderListButton）的显示方式门控（导航简化 2026-08）。
    // header_nav_display: 'always'（常驻，默认）| 'hover'（悬停才出现，旧行为）。
    // tab 行不再默认放 Home（show_home_tab 默认关，见 renderTabs 的 hasHome）后，
    // 这个按钮成为主导航入口——默认常驻，避免"tab 无 Home + 按钮 hover 藏起来"
    // 让访客无路可回。只有商户显式配成 'hover' 才回到旧的悬停显隐；未配置
    // （undefined，含所有存量租户与 config 到达前）一律按 'always' 处理。
    // 注意：这不改 showListButton===false 的硬关闭语义——商户明确关掉按钮时
    // 依旧不渲染（renderListButton 内早返回），always 只管"该显示时是否常驻"。
    _navDisplayAlways = () => this.state.theme.headerNavDisplay !== 'hover';

    // 左上角按钮「本次是否应显现」：always 档恒 true；hover 档沿旧逻辑看 headerHovered。
    // 各 header 分支的 list 按钮显隐 + 让位 padding 都读这个，替代原来直接读 headerHovered
    // （headerHovered 现在只剩 hover 档在用；知识库 hover icon 那个独立入口不受影响）。
    _navRevealed = () => this._navDisplayAlways() || this.state.headerHovered;

    // 返回按钮这一帧到底占不占位。三个正交条件全过才算：
    //   1. _navRevealed —— always/hover 的显示方式门控（导航简化 2026-08）；
    //   2. showListButton !== false —— 商户硬关闭；
    //   3. 有返回落点 —— 没有来路时按钮根本不渲染（见 renderListButton）。
    // 各 header 分支的让位 padding 必须读这个同一个判据，否则会留出 32px 空洞
    // （或者反过来，按钮画出来却被标题压住）。
    //   4. 知识库文章详情是特例 —— 那一屏 KbBackBar 自带返回钮。两颗都画成"返回"
    //      会上下叠着出现,而且落点还不一样(header 这颗回"进知识库之前的地方",
    //      KbBackBar 那颗回文章列表):两套导航互不知情,视觉上却是同一个动作。
    //      解法不是把 header 这颗藏掉(那样从文章回 Home 要走两步),而是**换成
    //      Home**:两颗按钮角色就分明了 —— 上面出去回首页,下面退回文章列表。
    //      商户在 dash 关掉 Home 时它去无可去,这时才不画(KbBackBar 已覆盖返回)。
    _navButtonVisible = () => {
        if (!this._navRevealed()) return false;
        if (this.state.theme.showListButton === false) return false;   // 商户硬关闭
        const target = this._cornerTarget();
        if (!target) return false;                                     // 已在 Home
        // Home 被商户关掉时它去无可去;会话列表永远可达(它是 chat 的子视图)。
        return target !== NAV_HOME || this._homeReachable();
    };

    /** Home 这一刻是否真能去(商户可在 dash 关掉它)。 */
    _homeReachable = () => resolveHomeViewAction(this.state.theme.homePageEnabled) === 'home';

    /** 知识库文章详情屏(KbBackBar 在场)。与 _currentPanelView 同一判据。 */
    _inKnowledgeDetail = () => this.state.widgetClass === 'know-widget' && this._knowView === 'detail';

    /**
     * 现在是不是"某一个会话"里(下钻),而不是会话列表(顶层)。
     *
     * 读 _chatSubview 而不是 nav.view:点 Chat tab 时 switchToChat() 记的是
     * NAV_CHAT,而 chat frame 保留着原来的列表子视图 —— 状态与画面不符。
     * 这个不符正是"点 chat tab 回到列表、左上角却变返回、按下去跳去知识库"的
     * 根因:NAV_CHAT 不在返回栈里,navGo 于是又压一层而不是截断回去。
     */
    _cornerTarget = () => cornerButtonTarget(this.state.widgetClass, this._chatSubview, this._backTarget());

    /**
     * 左上角这一颗现在是 Home 还是返回。
     *
     * 顶层目的地(Home / 会话列表 / 知识库)之间靠 tab 平级切换,彼此没有"上一层"
     * 可回;唯一合理的出口是 Home。只有**会话内部**是真正的下钻,而且它没有别的
     * 返回入口(知识库文章有 KbBackBar,会话没有),所以那里保留返回。
     * 这样图标只在会话里才是箭头,顶层永远是房子 —— 同一个位置不再变身份。
     */


    hasKnowledgeTabs = () => {
        const c = this.state.appConfig;
        if (!c) return true; // config 未到达前，先按有 tab 处理
        const enabled = c.knowledge_enabled !== false && c.knowledge_enabled !== 0;
        // 空知识库(无 active 内容)→ 不显示 Chat/Help 双 tab，直接进聊天。
        // 旧后端不下发该字段时 undefined，!== false 为 true → 保持原行为(显示)。
        const hasContent = c.knowledge_has_content !== false && c.knowledge_has_content !== 0;
        return enabled && hasContent;
    }

    // ---------- default 档两行 header 的高度模型 ----------
    // 结构永远是两行，任何配置组合下都不做「tab 合并回第一行」：左侧头像组数量随
    // 客服上下线变、标题长度站长可改、tab 文案随语言变 —— 任何按剩余空间自动合并
    // 的策略都会在运行时跳变。稳定性比省下的 32px 值钱。
    // 变的只是同一结构下的留白：第一行元素变少 → 第一行块高收敛。
    //   有右侧 icon（语言/设置任一开）：8 + 40 + 8 = 56（icon 40×40 触摸目标定下限）
    //   两个 icon 全关，有头像       ：8 + 36 + 8 = 52（关闭键 32 + 上下留白）
    //   两个 icon 全关，且无头像      ：6 + 32 + 6 = 44（只剩标题 + 关闭）
    // 第二行 tab 是导航区，高度恒定（HEADER_TAB_ROW_H），不参与收敛。
    // 注意 padV 从 10 降到 8 不是"又改了一版留白"：旧代码 padding 10 + 40 + 8 + 40
    // + 10 = 108 塞进写死的 height:104 里，实际上溢出 4px 被挤掉，肉眼的上下留白
    // 本来就是 8。现在把账做平，总高仍是 104，但 tab 行不再被压扁 2px。
    static HEADER_TAB_ROW_H = 40;   // tab 行自然高：pill 34 + 轨道 padding 3×2；underline 36 + 4
    static HEADER_TAB_GAP = 8;      // 第一行与 tab 行的间距

    // 只看配置，不看 isChatOpen：右侧 icon 组在关闭态不渲染，但若把它算进高度模型，
    // header 会在开合瞬间跳 4px。高度只由配置决定 → 开合前后纹丝不动。
    _headerHasTools = () => this._headerToolEnabled('show_language_button')
        || this._headerToolEnabled('show_settings_button');


    _headerHasAvatars = () => this.state.theme.showHeaderAvatar !== false
        && this.state.widgetClass !== 'home-widget';

    // 返回 { row1, padV }：第一行 min-height 与 header 容器的上下 padding
    // 总高 = padV*2 + row1 + 8 + 40 → 104 / 100 / 92
    _headerRowMetrics = () => {
        if (this._headerHasTools()) return { row1: 40, padV: 8 };
        if (this._headerHasAvatars()) return { row1: 36, padV: 8 };
        return { row1: 32, padV: 6 };
    }

    // fill 布局(原生 WebView ?platform=、无 ?layout 参数的小屏 widget.html 兜底)
    // 面板贴视口边缘，四角应该是直角——圆角只在"悬浮面板"这个观感下才有意义
    // （centered 悬浮卡片、JS-SDK 浮动小面板都保留圆角）。conf.layout 由
    // widget.html 的 boot script 算好、经 widget-index.js 传入（浮动 JS-SDK 面板
    // 不经过 widget.html，不会设 laneChatMode='inline'，天然不落进这个判断）。
    _isFillFullPage = () => this.state.theme.mode === 'inline' && this.state.theme.layout === 'fill';

    // inline 模式下要不要给根 div 一个硬性 minHeight 兜底（历史默认 520px）：
    // 只在"宿主可能没给我们真实高度"时才需要——widget.html（无论 fill 还是
    // centered）自己的 CSS 链（html/body/#chatly-container 都是明确的 height:
    // 100%/dvh，见 widget.html 顶部注释）永远提供真实高度，#chatly-container
    // 又 overflow:hidden。CSS flexbox 的 min-height 是硬地板，flex-shrink 越不过
    // 去——键盘弹出时可视高度可能远小于 520px（真机反馈：Android WebView fill
    // 布局下键盘弹起，composer/"Powered by" 被裁掉半截，键盘高度本身还因设备
    // /输入法而异，不是个固定阈值，所以不能靠"再加一个更大的兜底数字"绕过，
    // 只能不跟真实高度打架）——520 的地板会顶破这条真实链路，把面板撑高到
    // 520px、溢出部分被 #chatly-container 裁掉。只有"宿主环境没有 widget.html
    // 这层、我们没法确定对方给没给我们高度"（第三方直接把 JS SDK inline 模式挂进
    // 自己页面的任意 div）时，才需要这个兜底防止零高度塌陷。#chatly-container
    // 是 widget.html 独有的挂载点 id，用它的存在与否精确区分这两种宿主。
    // source：renderSkeleton 传 conf（this.props.conf，configReady 前 state.theme
    // 还没定），真实 inline 根 div 传 state.theme——两处字段名一致(inlineMinHeight)，
    // 调用方各自传各自手头有的那份，不额外互相依赖。
    _inlineMinHeightFallback = (source) => {
        const hasReliableHeightChain = typeof document !== 'undefined' && !!document.getElementById('chatly-container');
        return resolveInlineMinHeightFallback({
            hasReliableHeightChain,
            configuredMinHeight: source && source.inlineMinHeight,
        });
    };

    getHeaderHeight = () => {
        const { theme } = this.state;
        if (theme.headerMode === 'large') {
            return this.hasKnowledgeTabs() ? 160 : 130;
        }
        // default 档：有知识库 tab 时是两行结构（第一行头像/标题/icon + 第二行 tab）
        if (theme.headerMode !== 'compact' && this.hasKnowledgeTabs()) {
            const m = this._headerRowMetrics();
            // header 变矮时总高不变（_getPanelSize 说了算），多出来的空间由消息区
            // 吸收（desktopHeight = 总高 - headerH），不会出现底部空隙。
            // 所有视图（含 home）统一两行：tab 永远在第二行的同一位置，切视图时
            // tab 纹丝不动。曾经把 home 收成单行（tab 与 icon 同排）省下 48px，但
            // tab 会从左上角跳到中间 —— 位置跳变比多占一行难受得多，已回退。
            return m.padV * 2 + m.row1 + Widget.HEADER_TAB_GAP + Widget.HEADER_TAB_ROW_H;
        }
        return 72;
    }

    // size: 'large' (36px, for large header) or 'small' (28px, for default/compact header)
    renderAgentAvatars = (agents, size = 'large') => {
        const theme = this._displayTheme();
        const mainColor = theme.mainColor || '#2563eb';
        const s = size === 'small' ? 28 : 36;
        const fontSize = size === 'small' ? '11px' : '14px';
        const defaultFontSize = size === 'small' ? '12px' : '16px';
        const defaultSize = size === 'small' ? 28 : 40;
        const overlap = size === 'small' ? -8 : -10;

        if (!agents || agents.length === 0) {
            return (
                <div style={{
                    width: `${defaultSize}px`, height: `${defaultSize}px`, borderRadius: 'var(--lc-radius-full)',
                    background: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: defaultFontSize, fontWeight: 600,
                    color: mainColor, border: '2px solid rgba(255,255,255,0.8)',
                }}>CS</div>
            );
        }

        // 溢出防护：桌面最多 3 个、移动端最多 2 个，超出折叠成 +N 徽章。
        // 之前 4 个的上限在多客服 app 里仍会把 header 撑挤（12 个在线客服时
        // 头像组 + 徽章横向溢出，挤压标题与右上角 icon）。
        const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
        const maxShow = mobile ? 2 : 3;
        const shown = agents.slice(0, maxShow);
        const overflow = agents.length - maxShow;

        const hovered = this.state.headerHoveredAgent;
        const tooltipPos = this.state.headerTooltipPos;

        const showTooltip = (i, el) => {
            if (!el) return;
            const r = el.getBoundingClientRect();
            this.setState({
                headerHoveredAgent: i,
                // 头像下方 8px 居中
                headerTooltipPos: { top: r.bottom + 8, left: r.left + r.width / 2 },
            });
        };
        const hideTooltip = () => this.setState({ headerHoveredAgent: null, headerTooltipPos: null });

        return (
            // flex-shrink:0 + max-width：头像组永远不被压缩也不无限扩张，
            // 可压缩的是标题区（min-width:0 + truncate），右上角 icon 区同样 shrink:0
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative', flexShrink: 0, maxWidth: mobile ? '96px' : '132px' }}>
                {shown.map((agent, i) => {
                    const isHover = hovered === i;
                    return (
                        <div
                            key={i}
                            title={agent.name || t('brand.default_title')}
                            onMouseEnter={(e) => showTooltip(i, e.currentTarget)}
                            onMouseLeave={hideTooltip}
                            onTouchStart={(e) => showTooltip(i, e.currentTarget)}
                            onTouchEnd={() => setTimeout(hideTooltip, 1500)}
                            style={{
                                width: `${s}px`, height: `${s}px`, borderRadius: 'var(--lc-radius-full)',
                                border: '2px solid rgba(255,255,255,0.8)',
                                marginLeft: i === 0 ? 0 : `${overlap}px`,
                                overflow: 'visible',
                                background: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize, fontWeight: 600, color: mainColor,
                                zIndex: isHover ? 100 : (maxShow - i),
                                position: 'relative',
                                cursor: 'pointer',
                                transform: isHover ? 'translateY(-2px) scale(1.1)' : 'none',
                                transition: 'transform var(--lc-dur-base) var(--lc-ease-out)',
                                boxSizing: 'border-box',
                            }}
                        >
                            <div style={{
                                width: '100%', height: '100%', borderRadius: 'var(--lc-radius-full)',
                                overflow: 'hidden', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                            }}>
                                {agent.avatar_url
                                    ? <img src={agent.avatar_url} alt={agent.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : (agent.name?.charAt(0)?.toUpperCase() || 'A')
                                }
                            </div>
                            {/* 在线状态绿点（右下角） */}
                            {agent.online !== false && (
                                <span style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: 0,
                                    width: `${Math.max(8, Math.round(s * 0.28))}px`,
                                    height: `${Math.max(8, Math.round(s * 0.28))}px`,
                                    borderRadius: 'var(--lc-radius-full)',
                                    background: '#22c55e',
                                    border: '2px solid #ffffff',
                                    boxSizing: 'border-box',
                                    pointerEvents: 'none',
                                }} />
                            )}
                        </div>
                    );
                })}
                {/* Portal 到 document.body，彻底脱离任何 transform/overflow stacking context */}
                {hovered !== null && tooltipPos && shown[hovered] && shown[hovered].name && typeof document !== 'undefined' &&
                    ReactDOM.createPortal(
                        <div style={{
                            position: 'fixed',
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                            transform: 'translateX(-50%)',
                            background: 'rgba(15, 23, 42, 0.92)',
                            color: '#fff',
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: 'var(--lc-radius-sm)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            boxShadow: 'var(--lc-shadow-2)',
                            zIndex: 2147483647,
                            fontWeight: 500,
                        }}>
                            {shown[hovered].name}
                        </div>,
                        document.body
                    )
                }
                {overflow > 0 && (
                    <div
                        title={t('status.agents_overflow', { count: overflow })}
                        style={{
                            marginLeft: `${overlap}px`, padding: '0 6px',
                            height: `${s}px`, borderRadius: `${s/2}px`,
                            background: 'rgba(255,255,255,0.25)',
                            color: '#fff', fontSize: size === 'small' ? '10px' : '12px', fontWeight: 600,
                            display: 'flex', alignItems: 'center',
                            border: '2px solid rgba(255,255,255,0.8)', zIndex: 0,
                            cursor: 'help',
                        }}
                    >+{overflow}</div>
                )}
            </div>
        );
    }

    // ---------- 头部工具 icon：语言切换 + 设置（通知声音） ----------

    // dash 配置的显隐开关：show_language_button / show_settings_button。
    // 两条下发路径都认（usage/start 的 config.widget_config、/v1/chat 的 appConfig），
    // 字段缺省一律当 true。
    _headerToolEnabled = (key) => {
        const sources = [
            this.state.appConfig,
            this.state.appConfig && this.state.appConfig.widget_config,
            this._preInitData && this._preInitData.appConfig,
        ];
        for (const src of sources) {
            if (src && typeof src === 'object' && src[key] !== undefined) {
                return !(src[key] === false || src[key] === 0 || src[key] === '0');
            }
        }
        return true;
    }

    _setWidgetLang = (lang) => {
        const picked = setLang(lang, true); // localStorage 持久，覆盖自动判定
        // 同步服务端，系统文案和 AI 回复才会跟着换。这里失败也不丢：下次 init 会
        // 带 language_manual 再报一次（比如还没有 token 时选的语言）。
        Promise.resolve().then(() => ApiService.setLanguage(picked)).catch(() => {});
        // t() 在渲染期取当前语言 —— langNonce 自增触发整树重渲染即可全量换语言。
        // 词条按需加载：先立刻重渲染（弹层收起 + 选中态跟上，不卡手），chunk 到了
        // onLocaleLoaded 会再 bump 一次把文案刷成目标语言。
        this.setState({ langNonce: this.state.langNonce + 1, headerPopover: null });
        ensureLocale(lang);
    }

    // 语言 chunk 落地后整树重渲染一次。componentDidMount 里订阅，unmount 退订。
    _bumpLangNonce = () => {
        if (!this._isMounted) return;
        this.setState((s) => ({ langNonce: s.langNonce + 1 }));
    }

    // ---------- 头部弹层锚定 ----------
    // 弹层挂在面板 wrapper 上（header 有 overflow:hidden，放不进去），所以拿不到
    // 「按钮外包 position:relative」那种零 JS 的天然跟随，只能量出来。
    // 绝不能写死偏移：header 第一行高度是三档自适应（104/100/92），右侧 icon 组
    // 的成员也随配置增减（语言关掉就只剩设置），任何固定 top/right 都会对不上。
    //
    // _panelRef 是浮动面板/inline 全页两条 render 分支共用的同一个 ref——同一个
    // 组件实例这辈子只会走其中一条（theme.mode 不会中途变），互不冲突。以前只有
    // 浮动面板那处 wrapperStyle div 挂了它，inline 分支（widget.html 全页/iframe/
    // 原生 WebView）从没挂过：_measureHeaderPopover 里 `panel = _panelRef.current`
    // 永远是 null，量不到真实按钮位置，只能回落到下面这个粗略估算——JS-SDK 浮动
    // 面板量得到、跟着按钮走；fullpage 量不到、用估算值，两个入口从此长得不一样，
    // fullpage 那份还会因为 inline 面板可以铺满整页导致估算的 top/right 跟真实
    // 按钮差出一大截（这就是用户报告的"设置面板跑到页面底部"）。挂上这个 ref
    // 之后两条分支都走真测量，同一份逻辑、同一个锚点算法，不用维护两套。
    _headerToolRefs = { lang: React.createRef(), settings: React.createRef() };
    _panelRef = React.createRef();
    _popoverRef = React.createRef();

    // 返回相对面板的 { top, right }；量不到时返回 null（调用方回落到旧的粗略值）
    _measureHeaderPopover = (which) => {
        const btnRef = this._headerToolRefs[which];
        const btn = btnRef && btnRef.current;
        const panel = this._panelRef.current;
        if (!btn || !panel) return null;
        const b = btn.getBoundingClientRect();
        const p = panel.getBoundingClientRect();
        if (!b.width || !p.width) return null;
        // 弹层实际宽度随内容变（语言两列网格 ~250、设置面板 272），只用一个常数
        // 会低估宽度、漏判左溢出。已渲染时量真值，首帧按弹层类型给估值。
        const popEl = this._popoverRef.current;
        const POP_W = (popEl && popEl.offsetWidth) || (which === 'lang' ? 250 : 190);
        const EDGE = 8;      // 弹层与面板边缘的最小间距
        const top = Math.round(b.bottom - p.top + 8);
        // 右边缘对齐「触发按钮」的右边缘（不是面板右边缘）
        let right = Math.round(p.right - b.right);
        // 越界兜底：右对齐会让弹层左溢出面板时，改成左对齐按钮左边缘
        if (p.width - right - POP_W < EDGE) {
            right = Math.round(p.right - b.left - POP_W);
        }
        // 无论如何夹在面板内
        right = Math.min(Math.max(EDGE, right), Math.max(EDGE, Math.round(p.width - POP_W - EDGE)));
        // 高度预算：14 种语言的两列网格会超出短面板，给弹层一个「面板高度 60%」
        // 的上限，超出部分弹层内部滚动（同样不写死，面板高度是自适应的）。
        const maxH = Math.max(140, Math.round(p.height * 0.6));
        return { top, right, maxH };
    }

    // resize / header 高度变化 / 视图切换后重算（面板与按钮都可能动过）
    _repositionHeaderPopover = () => {
        const which = this.state.headerPopover;
        if (!which) return;
        const pos = this._measureHeaderPopover(which);
        const prev = this.state.headerPopoverPos;
        if (!pos) return;
        if (prev && prev.top === pos.top && prev.right === pos.right && prev.maxH === pos.maxH) return;
        this.setState({ headerPopoverPos: pos });
    }

    _toggleHeaderPopover = (which, e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const next = this.state.headerPopover === which ? null : which;
        this.setState({
            headerPopover: next,
            headerPopoverPos: next ? this._measureHeaderPopover(which) : null,
        });
        // 首帧用 minWidth 估的宽度；渲染出来后按真实宽度校正一次左溢出判断。
        // 右对齐本身与宽度无关，所以只有窄面板的兜底分支会真的动，无可见跳变。
        if (next) requestAnimationFrame(() => { if (this._isMounted) this._repositionHeaderPopover(); });
    }

    // headerHovered 的唯一写入口 —— 只由「头像区 / hover 时出现的 list 按钮」触发，
    // 不再挂在整个 header 容器上（那会让语言/设置 icon 的 hover/点击连带触发
    // 头像位移动画）。离开侧 160ms 去抖：头像 ↔ 刚出现的按钮之间移动不闪烁。
    _setHeaderHover = (v) => {
        if (this._headerHoverTimer) { clearTimeout(this._headerHoverTimer); this._headerHoverTimer = null; }
        if (v) {
            if (!this.state.headerHovered) this.setState({ headerHovered: true });
        } else {
            this._headerHoverTimer = setTimeout(() => {
                this._headerHoverTimer = null;
                if (this._isMounted && this.state.headerHovered) this.setState({ headerHovered: false });
            }, 160);
        }
    }

    // 新 icon 的独立按压反馈（scale 0.96），不与头像动画共享任何状态/类名
    _iconPressHandlers = {
        onMouseDown: (e) => { e.currentTarget.style.transform = 'scale(0.96)'; },
        onMouseUp: (e) => { e.currentTarget.style.transform = 'scale(1)'; },
        onMouseLeave: (e) => { e.currentTarget.style.transform = 'scale(1)'; },
    }

    // 头部右侧的两枚工具 icon（在关闭按钮旁）。样式与现有 header 按钮一致；
    // 移动端触摸目标放大到 40×40（icon 视觉尺寸不变）。
    renderHeaderTools = () => {
        if (!this.state.isChatOpen) return null;
        const showLang = this._headerToolEnabled('show_language_button');
        const showSettings = this._headerToolEnabled('show_settings_button');
        if (!showLang && !showSettings) return null;
        const mobile = this.props.isMobile === true || this.props.isMobile === 'true';
        const btnStyle = { ...chatListButtonStyle, marginRight: 0, ...(mobile ? { width: '40px', height: '40px' } : {}) };
        const activeStyle = { background: 'rgba(255, 255, 255, 0.3)' };
        return (
            <React.Fragment>
                {showLang && (
                    <button
                        ref={this._headerToolRefs.lang}
                        style={{ ...btnStyle, ...(this.state.headerPopover === 'lang' ? activeStyle : {}) }}
                        onClick={(e) => this._toggleHeaderPopover('lang', e)}
                        {...this._iconPressHandlers}
                        aria-label={t('header.language')}
                        title={t('header.language')}
                    >
                        {/* translate / language icon */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20" />
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                    </button>
                )}
                {showSettings && (
                    <button
                        ref={this._headerToolRefs.settings}
                        style={{ ...btnStyle, ...(this.state.headerPopover === 'settings' ? activeStyle : {}) }}
                        onClick={(e) => this._toggleHeaderPopover('settings', e)}
                        {...this._iconPressHandlers}
                        aria-label={t('header.settings')}
                        title={t('header.settings')}
                    >
                        {/* gear icon */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                    </button>
                )}
            </React.Fragment>
        );
    }

    // 弹层挂在面板 wrapper（fixed）下，header 有 overflow:hidden 所以不能放 header 里。
    // closed shadow 里 document 级 click-outside 收不到 —— 用面板内透明 backdrop 收口。
    renderHeaderPopover = () => {
        const { headerPopover } = this.state;
        const theme = this._displayTheme();
        if (!headerPopover || !this.state.isChatOpen) return null;
        const mainColor = theme.mainColor || '#2563eb';
        const currentLang = getLang();
        const reduced = this._prefersReducedMotion();
        const isLang = headerPopover === 'lang';
        // 锚定到触发按钮（见 _measureHeaderPopover）。量不到时才回落到「整个 header
        // 底边 + 8」这个粗略值 —— 那正是旧的写死行为，弹层会掉到 tab 行下方。
        const pos = this.state.headerPopoverPos;
        const popStyle = {
            position: 'absolute',
            top: `${pos ? pos.top : this.getHeaderHeight() + 8}px`,
            right: `${pos ? pos.right : 12}px`,
            zIndex: 6001,
            background: 'var(--lc-surface, #ffffff)',
            borderRadius: 'var(--lc-radius-md)',
            boxShadow: 'var(--lc-shadow-3)',
            padding: '6px',
            minWidth: isLang ? '246px' : '190px',
            // 两列网格在窄面板（移动端全宽以外的小尺寸）也不能顶出面板
            maxWidth: 'calc(100% - 16px)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            // 入场 dur-base + ease-out（规范 §3.3；Android/iOS 已是 200ms，
            // web 的 0.18s + CSS 关键字曲线是唯一没走 token 的那个）
            animation: reduced ? 'none' : 'messageSlideIn var(--lc-dur-base) var(--lc-ease-out)',
        };
        const rowStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--lc-space-2)',
            width: '100%',
            border: 'none',
            background: 'transparent',
            padding: '9px 10px',
            borderRadius: 'var(--lc-radius-sm)',
            fontSize: '13px',
            color: 'var(--lc-text, #1e293b)',
            cursor: 'pointer',
            textAlign: 'left',
            boxSizing: 'border-box',
        };
        return (
            <React.Fragment>
                {/* 透明 backdrop：点面板任意其它区域关闭弹层（不依赖 document 监听） */}
                <div
                    style={{ position: 'absolute', inset: 0, zIndex: 6000, background: 'transparent' }}
                    onClick={() => this.setState({ headerPopover: null })}
                />
                <div ref={this._popoverRef} style={popStyle} onClick={(e) => e.stopPropagation()}>
                    {isLang && (
                        <React.Fragment>
                            <div style={{ padding: '6px 10px 6px', fontSize: '11px', fontWeight: 600, color: 'var(--lc-text-3, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {t('header.language')}
                            </div>
                            {/* 两列网格：14 项排单列会有 ~560px 高，任何面板都装不下。
                                高度上限来自 _measureHeaderPopover 的 maxH（面板高 60%），
                                减去上面那行标题的高度，超出部分在网格内部滚。 */}
                            <div
                                role="listbox"
                                aria-label={t('header.language')}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                    gap: '2px',
                                    maxHeight: `${Math.max(120, (pos && pos.maxH ? pos.maxH : 300) - 32)}px`,
                                    overflowY: 'auto',
                                    overscrollBehavior: 'contain',
                                    WebkitOverflowScrolling: 'touch',
                                }}
                            >
                                {SUPPORTED_LANGS.map((lang) => {
                                    const active = lang === currentLang;
                                    const name = LANG_NATIVE_NAMES[lang] || lang;
                                    const flag = flagInnerHtml(lang);
                                    return (
                                        <button
                                            key={lang}
                                            role="option"
                                            aria-selected={active}
                                            lang={lang}
                                            style={{
                                                ...rowStyle,
                                                justifyContent: 'flex-start',
                                                gap: 'var(--lc-space-2)',
                                                padding: '7px 8px',
                                                minWidth: 0,
                                                ...(active
                                                    ? { background: 'rgba(37, 99, 235, 0.10)', color: mainColor, fontWeight: 600 }
                                                    : {}),
                                            }}
                                            onClick={() => this._setWidgetLang(lang)}
                                        >
                                            {/* 国旗是装饰，语义由后面的自称文字承担 —— 圆角靠这层
                                                overflow:hidden 裁，SVG 内部不用 clipPath（同一 shadow
                                                root 里 14 个内联 SVG 的 id 会撞车） */}
                                            <span
                                                aria-hidden="true"
                                                style={{
                                                    flexShrink: 0,
                                                    display: 'block',
                                                    width: '21px',
                                                    height: '14px',
                                                    borderRadius: 'var(--lc-radius-xs)',
                                                    overflow: 'hidden',
                                                    lineHeight: 0,
                                                }}
                                            >
                                                {flag && (
                                                    <svg
                                                        viewBox={FLAG_VIEWBOX}
                                                        width="21"
                                                        height="14"
                                                        focusable="false"
                                                        style={{ display: 'block' }}
                                                        dangerouslySetInnerHTML={{ __html: flag }}
                                                    />
                                                )}
                                            </span>
                                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {name}
                                            </span>
                                            {active && (
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </React.Fragment>
                    )}
                    {headerPopover === 'settings' && (
                        <React.Fragment>
                            <div style={{ padding: '6px 10px 4px', fontSize: '11px', fontWeight: 600, color: 'var(--lc-text-3, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {t('header.settings')}
                            </div>
                            <button style={rowStyle} onClick={this._toggleSound} aria-pressed={this.state.soundEnabled}>
                                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                    <span style={{ fontWeight: 500 }}>{t('settings.sound')}</span>
                                    <span style={{ fontSize: '11px', color: 'var(--lc-text-3, #94a3b8)' }}>{t('settings.sound_hint')}</span>
                                </span>
                                {/* toggle switch —— transform/opacity only */}
                                <span style={{
                                    flexShrink: 0,
                                    width: '34px',
                                    height: '20px',
                                    borderRadius: 'var(--lc-radius-full)',
                                    background: this.state.soundEnabled ? mainColor : 'var(--lc-surface-3, #cbd5e1)',
                                    position: 'relative',
                                    transition: 'background var(--lc-dur-base) var(--lc-ease-out)',
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        top: '2px',
                                        left: '2px',
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: 'var(--lc-radius-full)',
                                        background: '#fff',
                                        boxShadow: 'var(--lc-shadow-1)',
                                        transform: this.state.soundEnabled ? 'translateX(14px)' : 'translateX(0)',
                                        transition: reduced ? 'none' : 'transform var(--lc-dur-fast) var(--lc-ease-out)',
                                    }} />
                                </span>
                            </button>
                        </React.Fragment>
                    )}
                </div>
            </React.Fragment>
        );
    }

    // tab 样式档位：pill（默认，胶囊分段控件）| underline（下划线+滑动指示条）。
    // 配置字段 tab_style，来源同 show_language_button；缺省/非法值一律 pill。
    _tabStyle = () => {
        const sources = [
            this.state.appConfig,
            this.state.appConfig && this.state.appConfig.widget_config,
            this._preInitData && this._preInitData.appConfig,
        ];
        for (const src of sources) {
            if (src && typeof src === 'object' && src.tab_style !== undefined) {
                return src.tab_style === 'underline' ? 'underline' : 'pill';
            }
        }
        return 'pill';
    }

    // 知识库版式档位：auto（默认）| flat（分类条筛选）| nested（分类列表 → 文章列表）。
    // 来源同 tab_style；缺省/非法值一律 auto。auto 到底落 flat 还是 nested 由 widget 侧
    // 按「实际拉到几个分类」判定（服务端不知道也不该管这件事），判定点在 KnowledgeList。
    _kbLayoutMode = () => {
        const sources = [
            this.state.appConfig,
            this.state.appConfig && this.state.appConfig.widget_config,
            this._preInitData && this._preInitData.appConfig,
        ];
        for (const src of sources) {
            if (src && typeof src === 'object' && src.kb_layout_mode !== undefined) {
                const v = src.kb_layout_mode;
                return (v === 'flat' || v === 'nested') ? v : 'auto';
            }
        }
        return 'auto';
    }

    // ---------- Chat/Help tabs（三种 header 共用） ----------
    // pill 档：设计语言文档定案的胶囊分段控件（Crisp 结构）——浅色轨道 + 活动项
    // 实心 pill。头部是站长主色/渐变，所以活动 pill 用白底+主色字（白色在彩色
    // 表面上扮演"主色"角色，任意主色下都成立）。
    // underline 档：透明 tab + 底部主色指示条 translateX 平滑滑动。
    renderTabs = (extraStyle = {}) => {
        const { widgetClass } = this.state;
        const theme = this._displayTheme();
        const mainColor = theme.mainColor || '#2563eb';
        // home 是 tab 之外的首页层级，不是聊天视图 —— 以前这里写的是
        // `widgetClass === 'know-widget' ? 'know' : 'chat'`，于是站在 home 上
        // Chat tab 被点亮，界面在骗用户"你在聊天里"。三态各归各位。
        const activeTab = widgetClass === 'know-widget' ? 'know'
            : (widgetClass === 'home-widget' ? 'home' : 'chat');
        const styleMode = this._tabStyle();
        const reduced = this._prefersReducedMotion();
        // tab 行的 Home 图标（导航简化 2026-08）：默认**不**放进 tab —— 左上角
        // 的 renderListButton 已是常驻导航入口，tab 再放 Home 是重复。商户可用
        // show_home_tab 显式开回。两个前置条件都要满足：
        //   1. show_home_tab 为真（默认 false=隐藏；undefined/含存量租户按隐藏处理）；
        //   2. homePageEnabled 为真（首页开着才有 home 可回，点了才不落空）。
        const showHomeTab = theme.showHomeTab === true || theme.showHomeTab === 1 || theme.showHomeTab === 'true';
        const homeNavEnabled = theme.homePageEnabled !== false && theme.homePageEnabled !== 0;
        const hasHome = showHomeTab && homeNavEnabled;

        // underline 档用等宽 tab，指示条位移 = index * TAB_W，纯 transform 动画
        const TAB_W = 96;

        // 内容驱动的降级：可用宽度 < 内容自然宽度时收成「仅图标」（不写死屏宽断点，
        // 也绝不做 Crisp 那种把文字截断成 "Messag..." 的事）
        const tabLabels = [t('nav.chat_tab'), t('nav.knowledge_tab')];
        const naturalW = tabLabels.reduce((s, l) => s + 15 + 6 + 28 + l.length * 8, 0) + 18
            + (hasHome ? 38 : 0); // home 图标槽（32px + 6px 间距）
        const availW = (this._getPanelSize(this._currentPanelView()).width || 400) - 40;
        const iconOnly = naturalW > availW;

        // home 图标：Crisp 的做法 —— 独立坐在 tab 组左侧，不进 pill 轨道 / 不进
        // underline 等宽队列。这样 Chat/Help 两档的既有排版和指示条位移数学完全不动，
        // 而 home 又有了一个常驻、可触摸的回归入口（header 那个 list 按钮是 hover 才
        // 出现的，触屏上根本摸不到，配置还能关掉 → 从 home 点进 tab 就回不去了）。
        const homeTab = hasHome ? (
            <div
                className="chat-small lc-tab-home"
                data-type="home"
                role="button"
                tabIndex={0}
                title={t('nav.home')}
                aria-label={t('nav.home')}
                aria-current={activeTab === 'home' ? 'page' : undefined}
                style={{
                    ...chatSmallStyle,
                    margin: 0,
                    padding: 0,
                    gap: 0,
                    width: '32px',
                    height: '32px',
                    flexShrink: 0,
                    justifyContent: 'center',
                    borderRadius: 'var(--lc-radius-full)',
                    backdropFilter: 'none',
                    background: activeTab === 'home' ? '#ffffff' : 'transparent',
                    color: activeTab === 'home' ? mainColor : '#ffffff',
                    ...(activeTab === 'home'
                        ? { boxShadow: 'var(--lc-shadow-1)' }
                        : { opacity: 0.9 }),
                }}
            >
                <span style={chatIconStyle}>
                    <svg style={chatIconSvgStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                </span>
            </div>
        ) : null;

        // 活动项是 600 字重、非活动项是 500 —— 文字宽度不同，而整条 tab 行是
        // `margin: 0 auto` 居中的，所以切视图时整行会横向抖 1px。把标签放进一个
        // inline-grid，同格里叠一个不可见的 600 字重幽灵：格子永远按粗体宽度撑开，
        // 可见文字在里面居中，字重变化不再影响任何几何。
        // （underline 档的 tab 是定宽 TAB_W，本来就没这个问题。）
        const stableLabel = (label) => (
            <span style={{ display: 'inline-grid', justifyItems: 'center', alignItems: 'center' }}>
                <span aria-hidden="true" style={{ gridArea: '1 / 1', fontWeight: 600, visibility: 'hidden' }}>{label}</span>
                <span style={{ gridArea: '1 / 1' }}>{label}</span>
            </span>
        );

        const pillTab = (type, label, iconSvg) => {
            const active = activeTab === type;
            return (
                <div
                    className="chat-small lc-tab-pill"
                    data-type={type}
                    title={label}
                    aria-label={label}
                    style={{
                        ...chatSmallStyle,
                        margin: 0,
                        borderRadius: 'var(--lc-radius-full)',
                        padding: iconOnly ? '5px 12px' : '5px 14px',
                        background: active ? '#ffffff' : 'transparent',
                        backdropFilter: 'none',
                        ...(active
                            ? { color: mainColor, fontWeight: 600, boxShadow: 'var(--lc-shadow-1)' }
                            : { opacity: 0.9 }),
                    }}
                >
                    <span style={chatIconStyle}>{iconSvg(active ? mainColor : '#ffffff')}</span>
                    {!iconOnly && stableLabel(label)}
                </div>
            );
        };

        const underlineTab = (type, label, iconSvg) => {
            const active = activeTab === type;
            return (
                <div
                    className="chat-small lc-tab-underline"
                    data-type={type}
                    style={{
                        ...chatSmallStyle,
                        margin: 0,
                        background: 'transparent',
                        backdropFilter: 'none',
                        borderRadius: 'var(--lc-radius-sm)',
                        width: `${TAB_W}px`,
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        padding: '6px 0',
                        ...(active ? { fontWeight: 600 } : { opacity: 0.75 }),
                    }}
                >
                    <span style={chatIconStyle}>{iconSvg('#ffffff')}</span>
                    {label}
                </div>
            );
        };

        if (styleMode === 'underline') {
            const activeIndex = activeTab === 'know' ? 1 : 0;
            return (
                <div className="chat-header" style={{ ...chatHeaderStyle, alignItems: 'center', gap: homeTab ? 'var(--lc-space-2)' : 0, ...extraStyle }}>
                    {homeTab}
                    <div className="chat-header-sub" style={{ ...chatHeaderSubStyle, gap: 0, position: 'relative', paddingBottom: 'var(--lc-space-1)' }}>
                        {underlineTab('chat', t('nav.chat_tab'), this._tabChatIcon)}
                        {underlineTab('know', t('nav.knowledge_tab'), this._tabKnowIcon)}
                        {/* 滑动指示条：白色（在主色 header 上即"高亮色"），220ms transform。
                            站在 home 上时两个 tab 都不是当前项 —— 指示条直接淡出，
                            不去指一个用户并没在看的 tab。 */}
                        <span className="lc-tabs-indicator" aria-hidden="true" style={{
                            position: 'absolute',
                            bottom: 0,
                            left: `${(TAB_W - 28) / 2}px`,
                            width: '28px',
                            height: '3px',
                            borderRadius: 'var(--lc-radius-xs)',
                            background: '#ffffff',
                            opacity: activeTab === 'home' ? 0 : 1,
                            transform: `translateX(${activeIndex * TAB_W}px)`,
                            // 位移 dur-base + ease-out（规范 §3.3）。原来的
                            // `.22s cubic-bezier(.22,1,.36,1)` 是自带一条曲线的字面量，
                            // 与 token 表里的 ease-out 只差观感、却让三端无从对齐。
                            transition: reduced ? 'none' : 'transform var(--lc-dur-base) var(--lc-ease-out), opacity var(--lc-dur-fast) var(--lc-ease-out)',
                            pointerEvents: 'none',
                        }} />
                    </div>
                </div>
            );
        }

        // pill 档：浅色轨道包住整组 tab（Crisp 结构）；home 图标在轨道之外，
        // 所以切到 home 时轨道里两个 pill 都是未选中态。
        return (
            <div className="chat-header" style={{ ...chatHeaderStyle, alignItems: 'center', gap: homeTab ? 'var(--lc-space-2)' : 0, ...extraStyle }}>
                {homeTab}
                <div className="chat-header-sub" style={{
                    ...chatHeaderSubStyle,
                    gap: '2px',
                    background: 'rgba(255, 255, 255, 0.16)',
                    borderRadius: 'var(--lc-radius-full)',
                    padding: '3px',
                }}>
                    {pillTab('chat', t('nav.chat_tab'), this._tabChatIcon)}
                    {pillTab('know', t('nav.knowledge_tab'), this._tabKnowIcon)}
                </div>
            </div>
        );
    }

    _tabChatIcon = (fill) => (
        <svg style={chatIconSvgStyle} viewBox="0 0 1168 1024" xmlns="http://www.w3.org/2000/svg" width="200" height="200"><path d="M313.214 407.417c1.31-121.283 79.049-199.663 199.768-200.703 100.996-0.87 202.037-1.715 302.974 0.605 36.165 0.835 43.174-12.723 38.56-44.484C842.102 77.102 769.402 13.366 676.66 12.706c-164.128-1.18-328.25-1.135-492.378-0.09C76.552 13.311 4.077 84.286 1.372 191.412c-1.87 73.9-1.795 147.915 0.02 221.815 2.215 90.483 52.763 153.664 140.956 173.411 27.596 6.174 32.001 13.073 21.132 38.045-19.812 45.514-36.86 92.237-55.057 138.451 3.674 2.91 7.364 5.824 11.033 8.724 51.698-32.141 102.526-65.806 155.464-95.777 28.486-16.123 40.25-35.115 38.92-68.521C311.184 540.949 312.499 474.138 313.214 407.417L313.214 407.417zM1009.954 244.019c-167.722-3.545-335.614-3.375-503.336-0.055-91.637 1.81-152.019 63.436-155.649 154.699-3.575 90.073-3.77 180.52 0.095 270.563 4.119 95.767 68.981 153.429 166.647 154.909 54.113 0.81 108.35 2.185 162.353-0.51 41.644-2.085 76.255 9.014 111.065 31.836 79.964 52.428 162.228 101.321 256.515 159.653-17.068-43.344-28.201-71.405-39.16-99.531-11.033-28.291-21.897-56.652-33.685-87.203 10.174-1.71 16.968-3.435 23.837-3.934 112.21-8.119 166.427-64.716 167.537-176.736 0.78-79.384 1.485-158.798-0.23-238.147C1163.808 310.915 1106.086 246.049 1009.954 244.019L1009.954 244.019z" fill={fill}></path></svg>
    )

    _tabKnowIcon = (fill) => (
        <svg style={chatIconSvgStyle} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width="200" height="200"><path d="M470.8352 160.768a279.6544 279.6544 0 0 0-22.528-9.216C358.0416 123.5456 222.72 92.8256 88.064 105.216a40.5504 40.5504 0 0 0-36.864 39.8848v728.0128c0 11.3152 4.9664 21.5552 13.2096 29.0816a42.3424 42.3424 0 0 0 30.7712 10.752c130.3552-9.6768 260.096 10.8032 375.0912 58.2656l0.512 0.512V160.768z m463.5648-55.552c-135.2704-12.3904-270.5408 18.3296-360.192 46.336-7.68 2.7136-15.36 5.9392-22.528 9.216v809.8816l0.512-0.512c114.944-47.4624 244.736-67.3792 375.0912-58.2144a38.2976 38.2976 0 0 0 30.7712-10.752 40.1408 40.1408 0 0 0 13.2096-29.1328V145.1008a39.0144 39.0144 0 0 0-36.864-39.8848z" fill={fill}></path></svg>
    )

    renderLargeHeader = () => {
        const { appConfig, isChatOpen } = this.state;
        const theme = this._displayTheme();
        const { isMobile } = this.props;
        // Inline / full-page host (widget.html, iframe embed): the panel IS the
        // page, there's no floating button to collapse back to — so a close
        // button would just blank the page. Suppress all close affordances here.
        //
        // EXCEPT native containers (Android/iOS/Flutter WebView host): they render
        // the widget inline/fullscreen too, but unlike an iframe embed there IS a
        // real "exit" — the native shell itself — the widget just can't reach it
        // without the close button telling it to. Force the button back on and
        // route its tap through the bridge instead of the (no-op in inline mode)
        // panel-collapse path. See onClickClose and native-bridge.hasNativeTransport.
        const isInline = this._isInlineMode();
        const isNative = hasNativeTransport();
        const hasKnowledge = this.hasKnowledgeTabs();
        const headerHeight = hasKnowledge ? 160 : 130;
        // 沉浸式状态栏适配（仅 fill 布局）：外层盒子的高度/顶部内边距同步加一份
        // env(safe-area-inset-top)——盒子（背景）向上多出安全区那一截，paddingTop
        // 也多出同样一截，两者相减，flex 内容实际可用的空间（justifyContent:
        // center 据此居中）跟改动前逐像素一致，只是整体下移了安全区那么多，不会
        // 被顶到状态栏底下。下面两处 top:'12px' 的绝对定位元素也要跟着加，因为
        // absolute 定位是相对这个盒子的 padding box 算的，加 paddingTop 本身
        // 不会把它们推下去。
        const isFillSafeArea = this._isFillFullPage();
        const topAbs = withSafeAreaTop(12, isFillSafeArea);

        return (
            <div style={{
                // Home 首页把 header 与下方欢迎区拼成一条连续 hero，渐变要按同一个虚拟盒绘制
                ...(this.state.widgetClass === 'home-widget'
                    ? homeHeroBackgroundStyle(theme, 0)
                    : { background: theme.headerGradient || theme.mainColor }),
                ...desktopTitleStyle,
                ...(this._isFillFullPage() ? { borderRadius: 0 } : {}),
                height: withSafeAreaTop(headerHeight, isFillSafeArea),
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: withSafeAreaTop(16, isFillSafeArea),
                paddingRight: 'var(--lc-space-4)',
                paddingBottom: 'var(--lc-space-4)',
                paddingLeft: 'var(--lc-space-4)',
            }}
            onClick={this.onClick}
            >
                {/* Pattern overlay */}
                <div style={getHeaderPatternStyle(theme.headerPattern)} />

                {/* List/home button - always（默认常驻）或 hover（旧悬停行为），见 _navRevealed */}
                {isChatOpen && this._navButtonVisible() && (() => {
                    const isRight = theme.listButtonPosition === 'right';
                    return this.renderListButton({
                        position: 'absolute',
                        top: topAbs,
                        [isRight ? 'right' : 'left']: isRight ? '44px' : '12px',
                        zIndex: 10,
                        marginRight: 0,
                    });
                })()}

                {/* Right buttons (top-right): language / settings / close
                    簇内间距 space-2（裁决 R17）。原本是 space-1，而规范 §2.3 要求相邻
                    触控目标间隙 ≥8 —— 按 4 排的话两颗按钮的 44 命中区互相重叠，内侧那
                    颗实际只剩约 36 可点，且点它的边缘会触发旁边那颗。§3.3 里写死 4 的
                    那条已作废。改的是横向间距，header 三条高度公式不受影响。 */}
                {isChatOpen && (
                    <div style={{ position: 'absolute', top: topAbs, right: '12px', display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', zIndex: 10 }}>
                        {this.renderHeaderTools()}
                        {(isNative || (!isInline && (isMobile || theme.closedStyle !== 'button' || this._fabCloseSqueezed))) && (
                            <button
                                style={{...closeButtonStyle, position: 'relative', top: 'auto', right: 'auto', transform: 'none'}}
                                onClick={this.onClickClose}
                                aria-label={t('common.close')}
                            >
                                <LcIcon name="close" size={14} />
                            </button>
                        )}
                    </div>
                )}

                {/* Avatar row */}
                {/* home 模式下欢迎区已经展示了客服头像，这里不再重复一份 */}
                {theme.showHeaderAvatar !== false && this.state.widgetClass !== 'home-widget' && (
                    <div style={{ display: 'flex', zIndex: 1, marginBottom: 'var(--lc-space-2)' }}
                         onMouseEnter={() => this._setHeaderHover(true)}
                         onMouseLeave={() => this._setHeaderHover(false)}
                    >
                        {this.renderAgentAvatars(theme.headerAgents)}
                    </div>
                )}

                {/* Title */}
                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600, zIndex: 1 }}>
                    {resolveServiceTitle(appConfig, t('brand.service_center'))}
                </div>

                {/* Status + response time */}
                {(() => {
                    const onlineCount = theme.agentsOnlineCount || 0;
                    const isOnline = onlineCount > 0;
                    const statusText = isOnline
                        ? t('status.agents_online', { count: onlineCount })
                        : (offlineStatusLine(theme) || t('status.offline'));
                    return (
                        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px', zIndex: 1, marginTop: 'var(--lc-space-1)', display: 'flex', alignItems: 'center', gap: 'var(--lc-space-1)' }}>
                            <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: 'var(--lc-radius-full)', background: isOnline ? '#4ade80' : '#94a3b8' }}></span>
                            {statusText}
                        </div>
                    );
                })()}

                {/* Tab bar (if knowledge enabled) */}
                {hasKnowledge && this.renderTabs({ marginTop: '10px' })}
            </div>
        );
    }

    renderCompactHeader = () => {
        const { isChatOpen, appConfig, headerHovered } = this.state;
        const theme = this._displayTheme();
        const isInline = this._isInlineMode();
        // Native container force-show: see renderLargeHeader for the full rationale.
        const isNative = hasNativeTransport();
        const { isMobile } = this.props;
        const hasKnowledge = this.hasKnowledgeTabs();
        // 沉浸式状态栏适配（仅 fill 布局）：desktopTitleStyle 的 72px 高/0 顶部内
        // 边距同步加一份安全区（原理同 renderLargeHeader）。这个头部的可见元素
        // 全是靠 top:'50%' + translateY(-50%) 在这个盒子里垂直居中的（不是
        // top:固定像素）——50% 是相对盒子长高之后的高度算的，如果直接让盒子长
        // 高，50% 会往安全区里偏，所以换算成"盒子内容区(固定 72px)自己的正中"：
        // 内容区顶边 = paddingTop = env()，正中 = env() + 36。
        const isFillSafeArea = this._isFillFullPage();
        const centerTop = isFillSafeArea ? 'calc(36px + env(safe-area-inset-top, 0px))' : '50%';

        return (
            <div
                style={{
                    // 同上：home 模式下与欢迎区共用一个虚拟渐变盒，接缝处才不跳色
                    ...(this.state.widgetClass === 'home-widget'
                        ? homeHeroBackgroundStyle(theme, 0)
                        : { background: theme.headerGradient || theme.mainColor }),
                    ...desktopTitleStyle,
                    ...(this._isFillFullPage() ? { borderRadius: 0 } : {}),
                    height: withSafeAreaTop(72, isFillSafeArea),
                    paddingTop: withSafeAreaTop(0, isFillSafeArea),
                }}
                onClick={this.onClick}
            >
                <div style={getHeaderPatternStyle(theme.headerPattern)} />

                {/* List/home button - always/hover, left side（见 _navRevealed） */}
                {isChatOpen && this._navButtonVisible() && theme.listButtonPosition !== 'right' && (() => {
                    return this.renderListButton({
                        position: 'absolute',
                        top: centerTop,
                        left: '12px',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        marginRight: 0,
                    });
                })()}

                <div style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', zIndex: 1, flex: 1, minWidth: 0,
                    paddingLeft: (isChatOpen && this._navButtonVisible() && theme.listButtonPosition !== 'right') ? '32px' : '0',
                    transition: 'padding-left var(--lc-dur-fast) var(--lc-ease-out)',
                }}>
                    {/* Agent avatars —— home 模式下欢迎区已有更大的头像/标题/状态，
                        这里整块不渲染，否则上下各一份重复（header 只留 tab 和关闭按钮）。
                        headerHovered 只由这个头像区触发（span 不参与布局） */}
                    {theme.showHeaderAvatar !== false && this.state.widgetClass !== 'home-widget' && (
                        <span style={{ display: 'inline-flex' }}
                              onMouseEnter={() => this._setHeaderHover(true)}
                              onMouseLeave={() => this._setHeaderHover(false)}>
                            {this.renderAgentAvatars(theme.headerAgents, 'small')}
                        </span>
                    )}
                    {this.state.widgetClass !== 'home-widget' && (
                    /* 标题区是唯一可压缩项：min-width:0 + 单行截断，客服再多也不挤右上角 icon */
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, flex: 1 }}>
                        <div style={{ color: theme.headerTextColor || '#fff', fontSize: '14px', fontWeight: '600', lineHeight: '1.3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {resolveServiceTitle(appConfig, t('brand.service_center'))}
                        </div>
                        <div style={{ color: theme.headerTextColor || '#fff', opacity: 0.7, fontSize: '11px', lineHeight: '1.3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(() => {
                                const onlineCount = theme.agentsOnlineCount || 0;
                                if (onlineCount > 0) return t('status.agents_online', { count: onlineCount });
                                return offlineStatusLine(theme);
                            })()}
                        </div>
                    </div>
                    )}
                </div>
                {/* Right buttons —— shrink:0：语言/设置/关闭永远可点 */}
                {isChatOpen && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', zIndex: 10, flexShrink: 0 }}>
                        {/* Knowledge icon - hover only（同 list 按钮：自己维持 hover 态） */}
                        {hasKnowledge && headerHovered && (
                            <button
                                style={chatListButtonStyle}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.switchToKnowledge();
                                }}
                                onMouseEnter={() => this._setHeaderHover(true)}
                                onMouseLeave={() => this._setHeaderHover(false)}
                                title={t('nav.knowledge_base')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                </svg>
                            </button>
                        )}
                        {/* List/home button - right side, always/hover（见 _navRevealed） */}
                        {this._navButtonVisible() && theme.listButtonPosition === 'right' && this.renderListButton({})}
                        {/* Language / settings icons */}
                        {this.renderHeaderTools()}
                        {/* Close button - always visible (except inline/full-page host; native
                            containers always show it too — see renderLargeHeader) */}
                        {(isNative || (!isInline && (isMobile || theme.closedStyle !== 'button' || this._fabCloseSqueezed))) && (
                            <button
                                style={{...closeButtonStyle, position: 'relative', top: 'auto', right: 'auto', transform: 'none'}}
                                onClick={this.onClickClose}
                                aria-label={t('common.close')}
                            >
                                <LcIcon name="close" size={14} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    renderDefaultHeader = () => {
        const { isChatOpen } = this.state;
        const theme = this._displayTheme();
        const isInline = this._isInlineMode();
        // Native container force-show: see renderLargeHeader for the full rationale.
        const isNative = hasNativeTransport();
        const { isMobile } = this.props;
        const mobile = isMobile === true || isMobile === 'true';
        const hasKnowledge = this.hasKnowledgeTabs();
        const isHome = this.state.widgetClass === 'home-widget';
        const showAvatars = theme.showHeaderAvatar !== false && !isHome;
        const bgStyle = isHome
            ? homeHeroBackgroundStyle(theme, 0)
            : { background: theme.headerGradient || theme.mainColor };
        // 沉浸式状态栏适配（仅 fill 布局）：两个分支各自的高度/顶部内边距都要加
        // 一份安全区，原理同 renderLargeHeader/renderCompactHeader。
        const isFillSafeArea = this._isFillFullPage();

        // 移动端触摸目标 ≥40px（关闭是唯一退出方式，永远可点）；icon 间距移动端收紧
        const closeBtn = isChatOpen && (isNative || (!isInline && (isMobile || theme.closedStyle !== 'button' || this._fabCloseSqueezed))) ? (
            <button
                style={{
                    ...closeButtonStyle, position: 'relative', top: 'auto', right: 'auto', transform: 'none',
                    ...(mobile ? { width: '40px', height: '40px' } : {}),
                    flexShrink: 0,
                }}
                onClick={this.onClickClose}
                aria-label={t('common.close')}
            >
                <LcIcon name="close" size={14} />
            </button>
        ) : null;

        // ---------- 两行结构（有知识库 tab 时；桌面移动一致） ----------
        // 第一行：[头像组][标题/在线状态]←压缩→[语言][设置][关闭]；第二行：tab 独立一行。
        // tab 不再与 header 元素共享横向空间 → 390px 下不可能重叠。
        if (hasKnowledge) {
            const leftBtnVisible = isChatOpen && this._navButtonVisible() && theme.listButtonPosition !== 'right';
            // 元素变少 → 收紧留白（结构不变，见 _headerRowMetrics 注释）
            const rowM = this._headerRowMetrics();

            // header 高度在极少数情况下仍会变：home 不渲染头像组，所以「两个 tool icon
            // 都关掉」时 home 是 92、其它视图是 100（见 _headerRowMetrics）。给它一条
            // 过渡，切换时不硬跳。内容区不需要配套过渡 —— 它是 flex:1，高度由布局
            // 实时算出，永远等于面板总高减 header，见 contentHeightStyle。
            //
            // bug 8：inline 模式下骨架（renderSkeleton({inline:true})，72px，无
            // transition）交接给这个真实两行 header（92/100/104px，带这条
            // transition）的那一刻，生产环境用 MutationObserver 逐帧抓到的证据是：
            // 同一个 DOM 节点的 style 从 72px 直接被改写成 104px，且改写时
            // transition 属性已经在场——浏览器于是把骨架的 72px 当成"变化前的值"，
            // 播放成一次"header 加载完之后慢慢长高"的动画。没有再深挖 React/
            // react-dom 18 在这个具体场景下为什么复用了这个节点（不影响修法，见下）。
            //
            // 不跟 DOM 复用机制较劲，直接在数据源头掐断："第一次真正带着 configReady
            // 数据画出这个 header"的那一次渲染，样式里压根不出现这条 transition——
            // _headerTransitionPainted 是同步的实例字段，在 render 函数体内原地读
            // 并翻一次，不经过 setState/componentDidUpdate 那套异步链路，所以这一次
            // render 求值时就已经决定了这一次的 headerTransition 是 {}，不依赖
            // "浏览器有没有机会在两次 commit 之间先画一帧"这类调度时序（这条路径已
            // 实测过，不成立——同步的实例字段读写才是唯一验证有效的方案，生产环境
            // MutationObserver 复核过：72→104 这次 style 改写不再带 transition
            // 属性）。此后（含本次会话剩余的每一次渲染）headerTransition 正常
            // 存在——home ↔ 其它视图切换那条真实高度差依旧动画，跟改动前一致。
            const isFirstHeaderPaintWithRealData = !this._headerTransitionPainted;
            this._headerTransitionPainted = true;
            const headerTransition = (this._prefersReducedMotion() || isFirstHeaderPaintWithRealData)
                ? {}
                : { transition: 'height var(--lc-dur-slow) var(--lc-ease-out), padding var(--lc-dur-slow) var(--lc-ease-out)' };

            return (
                <div
                    style={{
                        ...bgStyle,
                        ...desktopTitleStyle,
                        ...(this._isFillFullPage() ? { borderRadius: 0 } : {}),
                        height: withSafeAreaTop(this.getHeaderHeight(), isFillSafeArea),
                        lineHeight: 'normal',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        paddingTop: withSafeAreaTop(rowM.padV, isFillSafeArea),
                        paddingRight: 'var(--lc-space-3)',
                        paddingBottom: `${rowM.padV}px`,
                        paddingLeft: 'var(--lc-space-3)',
                        ...headerTransition,
                    }}
                    onClick={this.onClick}
                >
                    <div style={getHeaderPatternStyle(theme.headerPattern)} />

                    {/* 第一行 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', zIndex: 1, minHeight: `${rowM.row1}px`, position: 'relative' }}>
                        {/* hover 显现的 list/home 按钮（头像区触发） */}
                        {leftBtnVisible && this.renderListButton({
                            position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 10, marginRight: 0,
                        })}
                        {showAvatars && (
                            <span
                                style={{
                                    display: 'inline-flex', flexShrink: 0,
                                    marginLeft: leftBtnVisible ? '36px' : '0px',
                                    transition: 'margin-left var(--lc-dur-fast) var(--lc-ease-out)',
                                }}
                                onMouseEnter={() => this._setHeaderHover(true)}
                                onMouseLeave={() => this._setHeaderHover(false)}
                            >
                                {this.renderAgentAvatars(theme.headerAgents, 'small')}
                            </span>
                        )}
                        {/* 标题区：唯一可压缩项（min-width:0 + truncate） */}
                        {!isHome ? (
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div style={{ color: theme.headerTextColor || '#fff', fontSize: '14px', fontWeight: 600, lineHeight: '1.3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {resolveServiceTitle(this.state.appConfig, t('brand.service_center'))}
                                </div>
                                {(() => {
                                    const onlineCount = theme.agentsOnlineCount || 0;
                                    const txt = onlineCount > 0
                                        ? t('status.agents_online', { count: onlineCount })
                                        : offlineStatusLine(theme);
                                    return txt ? (
                                        <div style={{ color: theme.headerTextColor || '#fff', opacity: 0.75, fontSize: '11px', lineHeight: '1.3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {txt}
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        ) : <div style={{ flex: 1 }} />}
                        {/* 右侧 icon 组：shrink 0，永不被挤压 */}
                        {isChatOpen && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', zIndex: 10, flexShrink: 0 }}>
                                {this._navButtonVisible() && theme.listButtonPosition === 'right' && this.renderListButton({})}
                                {this.renderHeaderTools()}
                                {closeBtn}
                            </div>
                        )}
                    </div>

                    {/* 第二行：tab 独立一行，高度恒定（导航区不参与留白收敛） */}
                    {this.renderTabs({ marginTop: `${Widget.HEADER_TAB_GAP}px`, height: `${Widget.HEADER_TAB_ROW_H}px`, alignItems: 'center' })}
                </div>
            );
        }

        // ---------- 无知识库 tab：单行结构（标题 + 右侧按钮） ----------
        // 沉浸式状态栏适配：跟 renderCompactHeader 同一个换算——盒子内容区固定
        // 72px，两个 top:'50%' 绝对定位元素的居中基准换算成"内容区自己的正中"。
        const centerTop = isFillSafeArea ? 'calc(36px + env(safe-area-inset-top, 0px))' : '50%';
        return (
            <div
                style={{
                    // 同上：home 模式下与欢迎区共用一个虚拟渐变盒，接缝处才不跳色
                    ...bgStyle,
                    ...desktopTitleStyle,
                    ...(this._isFillFullPage() ? { borderRadius: 0 } : {}),
                    height: withSafeAreaTop(72, isFillSafeArea),
                    paddingTop: withSafeAreaTop(0, isFillSafeArea),
                }}
                onClick={this.onClick}
            >
                <div style={getHeaderPatternStyle(theme.headerPattern)} />

                {/* List/home button - always/hover（见 _navRevealed） */}
                {isChatOpen && this._navButtonVisible() && (() => {
                    const isRight = theme.listButtonPosition === 'right';
                    return this.renderListButton({
                        position: 'absolute',
                        top: centerTop,
                        transform: 'translateY(-50%)',
                        [isRight ? 'right' : 'left']: isRight ? '44px' : '12px',
                        zIndex: 10,
                        marginRight: 0,
                    });
                })()}

                {/* Agent avatars - absolutely positioned left, shifts right when left-side button visible.
                    home 模式下欢迎区已有更大的头像，这里不渲染，避免上下重复。
                    headerHovered 只由头像区自己触发（语言/设置 icon 不再连带头像动画） */}
                {showAvatars && (
                    <div style={{
                        position: 'absolute',
                        left: (isChatOpen && this._navButtonVisible() && theme.listButtonPosition !== 'right') ? '44px' : '12px',
                        top: centerTop,
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        transition: 'left var(--lc-dur-fast) var(--lc-ease-out)',
                    }}
                        onMouseEnter={() => this._setHeaderHover(true)}
                        onMouseLeave={() => this._setHeaderHover(false)}
                    >
                        {this.renderAgentAvatars(theme.headerAgents, 'small')}
                    </div>
                )}
                <div style={{
                    display: 'flex', alignItems: 'center', width: '100%', zIndex: 1, minWidth: 0,
                    paddingLeft: (
                        // home 模式下头像不渲染（见上），就不该再给它留 68px 空位
                        (showAvatars ? 68 : 0)
                        + ((isChatOpen && this._navButtonVisible() && theme.listButtonPosition !== 'right') ? 32 : 0)
                    ) + 'px',
                    transition: 'padding-left var(--lc-dur-fast) var(--lc-ease-out)',
                }}>
                    {isHome ? null : (
                        <div style={{ ...serviceInfoWrapperStyle, minWidth: 0 }}>
                            <div style={{ ...serviceTitleStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{resolveServiceTitle(this.state.appConfig, t('brand.service_center'))}</div>
                            {this.state.appConfig && this.state.appConfig.guide_text && (
                                <div style={{ ...guideTextStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{this.state.appConfig.guide_text}</div>
                            )}
                        </div>
                    )}
                </div>
                {/* Right side buttons */}
                {isChatOpen && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', position: 'absolute', top: centerTop, right: '12px', transform: 'translateY(-50%)', zIndex: 10, flexShrink: 0 }}>
                        {this.renderHeaderTools()}
                        {closeBtn}
                    </div>
                )}
            </div>
        );
    }

    // ---------- 首屏骨架：/v1/chat 还没返回时的占位 UI ----------
    // 纯中性灰 + 呼吸脉冲（.lc-skeleton-block，keyframes/类定义在
    // widget-index.js 的 shadow <style> 里，跟面板开合动画那批放一起）。不含任何
    // theme 颜色——这正是重点：宁可先什么颜色都不是，也不要先顶一版 default-
    // configuration.js 的出厂蓝再跳成 dash 配的颜色，那一下跳变比骨架屏更显眼。
    // configReady 一旦为 true 就再也不会调用这个方法，骨架只存在于首屏这一下。
    renderSkeleton = (opts = {}) => {
        const inline = !!opts.inline;
        const conf = this.props.conf || {};
        const panelStyle = inline
            ? {
                position: 'relative', width: '100%', height: '100%',
                minHeight: this._inlineMinHeightFallback(conf),
                display: 'flex', flexDirection: 'column',
                background: 'var(--lc-bg, #f8fafc)',
                // fill 布局贴视口边缘该是直角；同一份判断在 inlineStyle（真实内容,
                // 下面 ~2830 行）和 widget.html 的静态 #lc-boot-skeleton（border-
                // radius:inherit，自动跟 #chatly-container 走）三处必须一致，否则
                // 骨架和真实内容之间会有一下圆角/直角的闪变。
                borderRadius: conf.layout === 'fill' ? 0 : 12,
                overflow: 'hidden',
                boxShadow: 'var(--lc-shadow-2)',
            }
            : {
                ...desktopWrapperStyle,
                ...(conf.btnPcBottom && { bottom: conf.btnPcBottom }),
                ...(conf.btnPcRight && { right: conf.btnPcRight }),
                width: `${parseInt(conf.desktopWidth, 10) || 400}px`,
                height: '700px',
                display: 'flex', flexDirection: 'column',
            };
        // 沉浸式状态栏适配：骨架的 72px header 行要跟真实 header（renderCompactHeader/
        // renderDefaultHeader 的无知识库分支，同样是 72px）长同一份安全区，否则
        // 骨架→真实内容交接的瞬间会跳高度（bug8 那类"矮的骨架突然变高会被浏览器
        // 当成一次动画播放"的同一根因，这次是 72 vs 72+env，不是 72 vs 104）。
        // widget.html 的静态 #lc-boot-skeleton 也要同步这份，见该文件对应 CSS。
        const skeletonFillSafeArea = inline && this._isFillFullPage();
        return (
            <div className="lc-skeleton-panel" style={panelStyle} aria-hidden="true" aria-busy="true">
                <div style={{
                    height: withSafeAreaTop(72, skeletonFillSafeArea), flexShrink: 0,
                    background: 'var(--lc-surface-2, #f1f5f9)', display: 'flex', alignItems: 'center',
                    paddingTop: withSafeAreaTop(0, skeletonFillSafeArea), paddingRight: 'var(--lc-space-4)', paddingBottom: '0px', paddingLeft: 'var(--lc-space-4)',
                    gap: 'var(--lc-space-2)',
                }}>
                    <div className="lc-skeleton-block" style={{ width: '36px', height: '36px', borderRadius: 'var(--lc-radius-full)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--lc-space-2)' }}>
                        <div className="lc-skeleton-block" style={{ width: '55%', height: '12px' }} />
                        <div className="lc-skeleton-block" style={{ width: '35%', height: '10px' }} />
                    </div>
                </div>
                <div style={{ flex: '1 1 auto', minHeight: 0, padding: 'var(--lc-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--lc-space-3)', background: 'var(--lc-surface, #fff)' }}>
                    <div className="lc-skeleton-block" style={{ width: '78%', height: '14px', alignSelf: 'flex-start' }} />
                    <div className="lc-skeleton-block" style={{ width: '52%', height: '14px', alignSelf: 'flex-start' }} />
                    <div className="lc-skeleton-block" style={{ width: '64%', height: '14px', alignSelf: 'flex-end' }} />
                </div>
            </div>
        );
    }

    // ---------- 初始化失败错误页 ----------
    // /v1/chat 拿不到有效会话时的兜底 UI（app_id 无效/缺失、连接层失败、服务端
    // 5xx——分类见 preInitClient 里 resolveInitErrorKind 的调用点）。之前这三种
    // 情况完全没有专门处理，要么骨架永转、要么渲染出一个看着正常实际没有会话
    // 的空壳（真机反馈的"白屏/残骸态"）。
    //
    // 视觉上跟 renderSkeleton 共用同一套外层 panelStyle 计算（同一个盒子，不会
    // 有骨架→错误页切换时的尺寸跳变），内容换成居中卡片：克制的灰色图标（不是
    // 吓人的红色报错脸）+ 标题 + 一句说明 + （可重试类）重试按钮。
    // app_unavailable 不给重试按钮——重试只会拿到同一个"app 不存在"，对访客
    // 没有意义；技术细节只在 preInitClient 里 console.error 给集成方看，这里
    // 的文案全部是访客能懂的话，不出现 app_id/code 这类术语。
    renderInitError = (opts = {}) => {
        const inline = !!opts.inline;
        const conf = this.props.conf || {};
        const kind = this.state.initErrorKind;
        const panelStyle = inline
            ? {
                position: 'relative', width: '100%', height: '100%',
                minHeight: this._inlineMinHeightFallback(conf),
                display: 'flex', flexDirection: 'column',
                background: 'var(--lc-bg, #f8fafc)',
                borderRadius: this._isFillFullPage() ? 0 : 'var(--lc-radius-md)',
                overflow: 'hidden',
                boxShadow: 'var(--lc-shadow-2)',
                // 无 header 的独立错误页也是从 y=0 起画的（fill 布局），图标/文案
                // 靠内层 justifyContent:'center' 整体居中，加一点顶部安全区留白
                // 防止极端窄高视口下卡片贴到状态栏；不影响正常视口下的居中观感
                // （只是把可居中区域的顶边下移了安全区那么多）。
                paddingTop: withSafeAreaTop(0, this._isFillFullPage()),
            }
            : {
                // z-index 必须盖过面板内所有内容——composer 的 .msg-input 自己就是
                // z-index:1000(见 widget-index.js 的 shadow <style>)，emoji/GIF 弹层
                // 还要更高(9999)。用一个明显更高的值(matches fabCloseButtonStyle 那种
                // "确定盖住一切"的量级)，不然错误页只是视觉上盖住中间内容，composer
                // 输入框/发送键其实还在下面能点——那还是"看着正常、实际是空壳"。
                position: 'absolute', inset: 0, zIndex: 2147483000,
                display: 'flex', flexDirection: 'column',
                background: 'var(--lc-bg, #f8fafc)',
                borderRadius: 'inherit',
            };
        const canRetry = kind === 'network' || kind === 'server';
        const titleKey = kind === 'app_unavailable' ? 'error.app_unavailable_title'
            : kind === 'server' ? 'error.server_title'
            : 'error.network_title';
        const bodyKey = kind === 'app_unavailable' ? 'error.app_unavailable_body'
            : kind === 'server' ? 'error.server_body'
            : 'error.network_body';
        return (
            <div className="lc-init-error-panel" style={panelStyle}>
                <div style={{
                    flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                    padding: 'var(--lc-space-8) var(--lc-space-6)', gap: 'var(--lc-space-3)',
                }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                         style={{ color: 'var(--lc-text-3, #94a3b8)', marginBottom: 'var(--lc-space-1)' }}>
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M12 8v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--lc-text, #334155)' }}>{t(titleKey)}</div>
                    <div style={{ fontSize: '13px', color: 'var(--lc-text-2, #64748b)', maxWidth: '280px', lineHeight: 1.5 }}>{t(bodyKey)}</div>
                    {canRetry && (
                        <button
                            onClick={this.retryInit}
                            style={{
                                marginTop: 'var(--lc-space-2)', padding: '8px 20px', borderRadius: 'var(--lc-radius-sm)',
                                border: 'none', background: this._displayTheme().mainColor || '#2563eb',
                                color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            {t('common.retry')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Label and quick buttons belong to the floating launcher, so they exist only in its
    // closed state. A teaser or message preview takes the space while it is up.
    renderLauncherExtras(isMobile) {
        const { theme, isChatOpen, teaserVisible, teaserPayload } = this.state;
        if (isChatOpen || this._isInlineMode() || this.state.launcherHidden || this.state.initErrorKind) return null;
        if (!(isMobile || theme.alwaysUseFloatingButton || theme.closedStyle === 'button')) return null;

        const teaserUp = !!(teaserVisible && teaserPayload && teaserPayload.message);
        const showLabel = shouldRenderLabel({
            enabled: theme.launcherLabelEnabled,
            isMobile,
            mobileEnabled: theme.launcherLabelMobile,
            sessionDone: this.state.labelDone,
            isChatOpen,
            teaserVisible: teaserUp,
            launcherHidden: false,
        });
        const buttons = (teaserUp || this.state.quickButtonsDismissed) ? [] : visibleQuickButtons(theme.quickButtons, isMobile);
        if (!showLabel && buttons.length === 0) return null;

        const lines = showLabel ? labelLines({
            text: theme.launcherLabelText,
            defaultText: t('launcher.label_default'),
            showResponseTime: theme.launcherLabelShowResponseTime,
            responseTimeText: theme.responseTimeText,
            avgReplyTime: theme.avgReplyTime,
        }) : null;

        return (
            <React.Fragment>
                {buttons.length > 0 && (
                    <QuickButtons
                        buttons={buttons}
                        launcherSize={theme.buttonSize}
                        isMobile={isMobile}
                        groupLabel={t('quick.group_label')}
                        dismissLabel={t('quick.dismiss')}
                        onAction={this.handleQuickButton}
                        onDismiss={this.handleQuickButtonsDismiss}
                    />
                )}
                {showLabel && (
                    <LauncherLabel
                        primary={lines.primary}
                        secondary={lines.secondary}
                        launcherSize={theme.buttonSize}
                        isMobile={isMobile}
                        closing={this.state.labelClosing}
                        closeLabel={t('launcher.label_close')}
                        reducedMotion={this._prefersReducedMotion()}
                        onClick={this.handleLabelClick}
                        onClose={this._collapseLabel}
                    />
                )}
            </React.Fragment>
        );
    }

    render() {
        const { conf, fromData, isMobile } = this.props;
        const { isChatOpen, pristine, widgetClass, wrapperWidth, chatInitialized, knowInitialized, homeInitialized, showGame, gameUrl, teaserVisible } = this.state;
        // 渲染期一律消费 _displayTheme()：亮色下就是 state.theme 本体（零差异）；
        // 暗色下配色字段被派生覆盖，尺寸/开关等非颜色字段原样。
        const theme = this._displayTheme();

        // ---------- Inline embed 模式 ----------
        // 没有浮动按钮、没有开合动画，chat 视图铺满宿主容器。宿主 div 由
        // widget-index.js 在 mount 时设置为 position:relative + 用户指定的尺寸。
        if (theme.mode === 'inline') {
            // 初始化失败：错误页优先级高于骨架/正常内容——不管 configReady 是不是
            // 已经（被 loadAppConfig 的 /usage/start 那段无条件）翻成 true，只要
            // 判定过一次错误就先给错误页，不渲染一个没有真实会话的空壳。
            if (this.state.initErrorKind) return this.renderInitError({ inline: true });
            // inline 宿主（widget.html 全页面 / iframe 嵌入 / 原生 WebView）没有浮动
            // 按钮可以先顶着——这就是页面本身，/v1/chat 没返回前必须有点东西。之前
            // 这里直接 return null，网络慢的几百毫秒到 4 秒兜底之间宿主区域完全空白；
            // 现在换成中性灰骨架，颜色一到（见 preInitClient 的早期上色）就跟着
            // configReady 一起换成真 UI，不会有"先出蓝色默认主题、再跳成 dash 配的
            // 颜色"这一下更显眼的二次跳变。
            //
            // 品牌色结构性闸门：!colorKnown 单独也要拦——configReady 可能靠 4s
            // 客户端超时先翻真（componentDidMount 那个定时器），但那不代表颜色已
            // 确定，colorKnown 只服从 resolveColorKnown 的三条合法来源（显式
            // customizations/本地缓存/服务端本次确认，theme-color.js 顶部有完整
            // 不变式说明）。两者都要过关才画真实内容，这是 inline 分支唯一的一次
            // 判断——fill/centered/原生 WebView 首绘共用同一条路径，不需要各自
            // 再判一遍。
            if (!this.state.configReady || !this.state.colorKnown) return this.renderSkeleton({ inline: true });
            const inlineStyle = {
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: this._inlineMinHeightFallback(theme),
                display: 'flex',
                flexDirection: 'column',
                // 真机反馈(Android WebView, fill)：切 tab(Chat↔Help↔Home)时屏幕白色
                // 一闪而过。根因：这个 div 是 chat/know/home 三个 widgetClass 分支
                // div 共同的父容器，那三个 div 自己都没设 background（懒挂载/切换
                // 间隙露出的就是这层的颜色）；这里之前硬编码 '#f8fafc'，从不读
                // --lc-bg，暗色模式下（_syncThemeCssVars 已经把 --lc-bg 正确写到
                // shadow host 上）这层却恒定还是亮色——用无头浏览器直接测过：
                // isDarkActive:true、shadow host 上 --lc-bg:#0f1115，这个 div 的
                // computed background 却是 rgb(248,250,252)，就是这条硬编码。
                // renderSkeleton({inline:true}) 的骨架分支早就是 var(--lc-bg,
                // #f8fafc)（同一个盒子，见下面注释），只有这条"真内容"路径当年漏了
                // token 化——两边不一致本身就是骨架→真内容切换时的隐性跳变源之一。
                // 桌面浏览器光栅化快，这个间隙通常不可见；WebView 更慢，会看见一下。
                background: 'var(--lc-bg, #f8fafc)',
                // fill 布局（原生 WebView ?platform=、无 ?layout 参数的小屏兜底）
                // 贴视口边缘，该是直角——圆角只在"悬浮面板"这个观感下才有意义。
                // #chatly-container 在 fill 下本来就是 0（widget.html 只在 centered
                // 分支里给它设 16px），这个 div 是它内部唯一的内容，圆角必须跟着
                // 归零，否则四角会露出 #chatly-container 的白底（本次要修的 bug）。
                // renderSkeleton({inline:true}) 的骨架用同一个 conf.layout 判断，
                // 两处必须一致，不然骨架切真实内容那一下会有圆角/直角的闪变。
                borderRadius: this._isFillFullPage() ? 0 : 'var(--lc-radius-md)',
                overflow: 'hidden',
                boxShadow: 'var(--lc-shadow-2)',
            };
            // Inline 模式下永远渲染 chat header + 内容区 —— 不存在"关闭"态。
            //
            // 内容区必须跟浮动面板布局（下面 ~3030 行那份）同一套三态切换
            // （chat/know/home widgetClass → ChatFrame/KnowFrame/HomeView），
            // 否则就是 W2 那个 bug：header 的 tab/Home 点击正确 setState 了
            // widgetClass（header 自己也确实按 isHome 重渲染），但内容区在
            // Knowledge tab / Home 页功能上线时从没跟着补三态分支，硬编码只
            // 渲染 ChatFrame——点 Help/Home 视觉上"卡在 chat 视图"，实际是
            // widgetClass 早就变了，只是没人读它。inline 专属分支，浮动面板
            // 那份早就是对的，这里补齐同一份逻辑（含 contentHeightStyle /
            // _viewAnimStyle，直接复用同名方法，数值内联——inline 分支在
            // render() 里比 floating 那份的局部变量定义更早 return，够不到）。
            const inlineContentHeightStyle = { flex: '1 1 auto', minHeight: 0 };
            return (
                <div style={inlineStyle} ref={this._panelRef}>
                    {theme.headerMode === 'large' ? this.renderLargeHeader()
                        : theme.headerMode === 'compact' ? this.renderCompactHeader()
                        : this.renderDefaultHeader()}

                    <div style={{
                        display: widgetClass === 'chat-widget' ? 'block' : 'none',
                        position: 'relative', overflow: 'hidden',
                        ...inlineContentHeightStyle,
                        ...this._viewAnimStyle('chat-widget'),
                    }} className="chat-widget">
                        {(!pristine || chatInitialized) && <ChatFrame ref={this.chatFrameRef} {...this.props} theme={theme}
                                   initialViewMode={this._chatInitialViewMode}
                                   onViewModeChange={this.handleChatViewModeChange}
                                   preInitData={this._preInitData} preInitPromise={this._preInitPromise} />}
                    </div>

                    <div style={{
                        display: widgetClass === 'know-widget' ? 'block' : 'none',
                        position: 'relative', overflow: 'hidden',
                        ...inlineContentHeightStyle,
                        ...this._viewAnimStyle('know-widget'),
                    }} className="know-widget">
                        {(!pristine || knowInitialized) && <KnowFrame ref={this.knowFrameRef} {...this.props} theme={theme} appConfig={this.state.appConfig} onViewChange={this.handleKnowViewChange} initialArticle={this._knowInitialArticle} initialReturnTo={this._knowReturnTo} onExit={this.handleKnowExit} kbLayoutMode={this._kbLayoutMode()} />}
                    </div>

                    <div style={{
                        display: widgetClass === 'home-widget' ? 'flex' : 'none',
                        position: 'relative', overflow: 'hidden', flexDirection: 'column',
                        ...inlineContentHeightStyle,
                        ...this._viewAnimStyle('home-widget'),
                    }} className="home-widget">
                        {homeInitialized && <HomeView
                            appId={conf.app_id || conf.appId}
                            theme={theme}
                            headerHeight={this.getHeaderHeight()}
                            appConfig={this.state.appConfig}
                            visitorId={this.visitorId}
                            onNavigate={this.handleHomeNavigate}
                        />}
                    </div>

                    {/* 头部弹层（语言 / 设置） */}
                    {this.renderHeaderPopover()}
                </div>
            );
        }

        // 统一尺寸模型（_getPanelSize 唯一出口）：理想总高 700、max-height 视口-48；
        // 宽度按视图分档（常规 400 / 详情 580）。resize 时 _onHostResize 触发重渲染。
        const panelSize = this._getPanelSize(this._currentPanelView());
        const desktopHeight = panelSize.height - this.getHeaderHeight();
        const wrapperHeight = { height: desktopHeight };
        // 内容区不再自己算高度，而是 flex:1 占满 header 之外的剩余空间。
        // 之前是显式 height = 面板总高 - headerH，于是 header 高度一变（home 单行 56
        // ↔ 其余两行 104），两个高度必须靠"时长/缓动完全一致"才能在每一帧加起来正好
        // 等于面板总高 —— 而刚切进来的内容区是从 display:none 出来的，根本不参与过渡，
        // 直接落在终值上：过渡途中 header + content ≠ 总高，于是底部要么露一条缝、
        // 要么裁掉一截。改成 flex:1 之后这个等式由布局本身保证，恒等成立、无需过渡。
        const contentHeightStyle = {
            flex: '1 1 auto',
            minHeight: 0,   // flex item 默认 min-height:auto 会撑破容器，内部就滚不动了
        };

        // 小视口下面板与右下角 FAB 关闭钮抢不开空间时进入"挤压模式"：
        // 面板贴回低位（不牺牲高度）、隐藏 FAB 关闭钮、改在 header 显示 ✕。
        const fabZone = (parseInt(theme.btnPcBottom, 10) || 20) + (parseInt(theme.dialogButtonSpacing, 10) || 72);
        this._fabCloseSqueezed = !isMobile && theme.closedStyle === 'button' && isChatOpen
            && (window.innerHeight - panelSize.height - fabZone) < 8;

        // 创建自定义位置的样式对象，而不是修改全局样式
        const customDesktopWrapperStyle = {
            ...desktopWrapperStyle,
            ...(theme.btnPcBottom && { bottom: theme.btnPcBottom }),
            ...(theme.btnPcRight && { right: theme.btnPcRight })
        };

        // button模式下的浮动按钮样式（圆形按钮）
        const customDesktopButtonWrapperStyle = {
            ...mobileClosedWrapperStyle,
            ...(theme.btnPcBottom && { bottom: theme.btnPcBottom }),
            ...(theme.btnPcRight && { right: theme.btnPcRight })
        };
        debugLog('[customDesktopButtonWrapperStyle]', customDesktopButtonWrapperStyle);

        const customMobileClosedWrapperStyle = {
            ...mobileClosedWrapperStyle,
            ...(theme.btnMobileRight && { right: theme.btnMobileRight }),
            ...(theme.btnMobileBottom && { bottom: theme.btnMobileBottom })
        };

        let wrapperStyle;
        debugLog('[Wrapper Style Decision]', {
            isChatOpen,
            isMobile,
            alwaysUseFloatingButton: theme.alwaysUseFloatingButton,
            closedStyle: theme.closedStyle
        });

        // 移动端
        if (isMobile) {
            if (isChatOpen) {
                debugLog('[Branch] Mobile open');
                wrapperStyle = mobileOpenWrapperStyle; // 打开：全屏
                // 软键盘弹起时把面板钉到可视区（见 _updateKeyboardViewport）。
                // bottom 必须显式改成 auto —— mobileOpenWrapperStyle 里 top/bottom
                // 都是 0，同时再给 height 会产生过约束，各引擎取舍不一。
                if (this.state.kbRect) {
                    wrapperStyle = {
                        ...wrapperStyle,
                        top: `${this.state.kbRect.top}px`,
                        bottom: 'auto',
                        height: `${this.state.kbRect.height}px`,
                    };
                }
            } else {
                debugLog('[Branch] Mobile closed');
                wrapperStyle = { ...customMobileClosedWrapperStyle }; // 关闭：浮动按钮
            }
        }
        // PC端
        else {
            debugLog('[Branch] PC mode');

            // button 模式：使用浮动按钮样式
            if (theme.closedStyle === 'button') {
                debugLog('[Branch] Button mode');
                wrapperStyle = isChatOpen
                    ? { ...customDesktopWrapperStyle, ...wrapperWidth } // 打开时：对话框
                    : { ...customDesktopButtonWrapperStyle }; // 未打开时：圆形浮动按钮
                debugLog('[wrapperStyle after assignment]', wrapperStyle);

                // 如果打开，对话框需要在按钮上方显示，让出右下角 FAB-close 的空间。
                // 挤压模式（小视口）：FAB-close 已隐藏（header 显示 ✕），面板贴低位。
                if (isChatOpen) {
                    const bottomValue = this._fabCloseSqueezed
                        ? Math.max(12, window.innerHeight - panelSize.height - 24)
                        : fabZone;
                    wrapperStyle = {
                        ...wrapperStyle,
                        bottom: `${bottomValue}px`
                    };
                }
            }
            // alwaysUseFloatingButton：始终使用浮动按钮（移动端样式）
            else if (!isChatOpen && theme.alwaysUseFloatingButton) {
                debugLog('[Branch] alwaysUseFloatingButton');
                wrapperStyle = { ...customMobileClosedWrapperStyle };
            }
            // chat 模式：传统样式
            else if (theme.closedStyle === 'chat' || isChatOpen || this.wasChatOpened()) {
                debugLog('[Branch] Chat mode');
                wrapperStyle = isChatOpen
                    ? { ...customDesktopWrapperStyle, ...wrapperWidth }
                    : { ...customDesktopWrapperStyle };
            }
            // 默认旧样式
            else {
                debugLog('[Branch] Default old style');
                wrapperStyle = { ...desktopClosedWrapperStyleChat };
            }
        }
        /*debugLog(_iframeSrc, chatProps.iFrameSrc);
        let ops = chatProps;
        debugLog(ops)
        ops.iFrameSrc = _iframeSrc;
*/
        debugLog('[Widget Render]', {
            closedStyle: theme.closedStyle,
            isChatOpen,
            isMobile,
            btnPcBottom: theme.btnPcBottom,
            wrapperStyle
        });

        // 品牌色结构性闸门 + 配置加载闸门，两者任一没过都不渲染真实 UI（防止样式
        // 抖动，也防止"抢跑"工厂色）——这是这个渲染入口唯一的一次判断，下面的
        // FAB/header/teaser/内容区 全部在这道闸之后，不需要各自再判一遍。
        //   - !configReady：/usage/start 完整配置还没到（或还没超时兜底）。
        //   - !colorKnown：品牌色还没有一个"服务端确认过/缓存/显式配置"的合法
        //     来源（resolveColorKnown，theme-color.js）。configReady 有可能靠
        //     4s 客户端超时先翻真，但那不代表颜色已确定——colorKnown 故意不会
        //     被超时打开，两者都要过关才放行，不会出现"骨架一结束就抢跑蓝"。
        // 浮动模式下面板通常还没打开（isChatOpen 初始为 false），此时连浮球本身
        // 一起先不画——出现得晚个几百毫秒比"先画默认蓝浮球、颜色到了再跳色"更不
        // 显眼，维持原有行为。但用户已经点开过面板（lane_open=1 自动打开 / 编程式
        // open() 早于 config 落地这种竞态，也是这次要修的"抢跑蓝"高发入口之一）时，
        // isChatOpen 已经是 true，这时候不能什么都不画——用中性灰骨架占位。
        // 例外:初始化**终局失败**。此时永远不会再有合法的品牌色来源了,继续
        // 什么都不画等于把浮球一起吞掉 —— 页面上看不出这里本该有客服入口,而
        // renderInitError 的重试按钮挂在"面板打开后"的内容里,面板又打不开,
        // 于是错误页和重试永远够不着。下面那句注释("面板关着时不受影响——错误
        // 不该拦住整个浮球")写的正是本该有的行为,是这道闸把它抵消了。
        // 放行后浮球用 neutralLauncherColor 画,不猜品牌色,不破 colorKnown 不变式。
        const initFailed = !!this.state.initErrorKind;
        if ((!this.state.configReady || !this.state.colorKnown) && !initFailed) {
            return isChatOpen ? this.renderSkeleton({ inline: false }) : null;
        }

        // 面板开合动画：开 = 从浮球角落 scale+fade 入场；关 = 反向出场 180ms，
        // 动画结束后 _requestClose 的定时器才真正翻 isChatOpen（不再瞬间消失）。
        // prefers-reduced-motion 时两个方向都不挂动画。
        // 同值在后续 re-render 时不会重播；只有从关闭→打开首次出现才播一次。
        if (isChatOpen && !this._prefersReducedMotion()) {
            // NOTE: do NOT add will-change:transform / a persistent transform here.
            // The FAB-close button inside this wrapper is position:fixed and must be
            // positioned relative to the VIEWPORT (so it sits below the panel). A
            // transform/will-change:transform on the wrapper turns it into the
            // containing block for fixed descendants → the FAB-close snaps inside the
            // panel and covers the input box. The `animation` only creates that
            // containing block for its short duration (acceptable), so no will-change.
            wrapperStyle = {
                ...wrapperStyle,
                transformOrigin: isMobile ? 'center bottom' : 'bottom right',
                // 出场比入场快一档(spec §5:跟手的关键):mobile 全屏面板
                // slow(300) 入/base(200) 出;PC 小面板 base(200) 入/fast(150) 出。
                // 曲线:入 ease-out / 出 ease-in(token)。
                animation: this.state.isClosing
                    ? (isMobile
                        ? 'lcPanelOutMobile var(--lc-dur-base) var(--lc-ease-in) forwards'
                        : 'lcPanelOut var(--lc-dur-fast) var(--lc-ease-in) forwards')
                    : (isMobile
                        ? 'lcPanelInMobile var(--lc-dur-slow) var(--lc-ease-out)'
                        : 'lcPanelIn var(--lc-dur-base) var(--lc-ease-out)'),
            };
        }

        // 宿主页横向溢出防护：检测到 layout viewport 比 visual viewport 宽时，
        // 用 visual viewport 矩形重新锚定（正常页面 vvRect 为 null，零介入）。
        {
            const vvKind = isChatOpen
                ? (isMobile ? 'mobileOpen' : 'desktopPanel')
                : ((isMobile || theme.alwaysUseFloatingButton || theme.closedStyle === 'button') ? 'fab' : null);
            const vvFix = vvKind ? this._vvAnchorStyle(vvKind) : null;
            if (vvFix) wrapperStyle = { ...wrapperStyle, ...vvFix };
        }

        // 桌面打开态：宽高统一由 _getPanelSize 决定（常规 400×700 / 详情 580×860，
        // 含视口 clamp）。两个方向都带 260ms 过渡 —— 面板 fixed 右下锚定，右边缘、
        // 底边缘都不动，只向左、向上延展，配合视图 push/pop 不会"甩"。
        // 注意：宽度绝不能用 transform/left 做动画（右边缘会跟着跑出视口）。
        if (!isMobile && isChatOpen) {
            wrapperStyle = {
                ...wrapperStyle,
                width: `${panelSize.width}px`,
                height: `${panelSize.height}px`,
                ...(this._prefersReducedMotion() ? {} : { transition: 'width var(--lc-dur-slow) var(--lc-ease-out), height var(--lc-dur-slow) var(--lc-ease-out)' }),
            };
        }
        // 打开态的面板是 flex 列：header 固定 + 内容区 flex:1（见 contentHeightStyle）。
        // 只在打开时加 —— 关闭态这个 wrapper 就是浮球本身，不能动它的布局。
        if (isChatOpen) {
            wrapperStyle = { ...wrapperStyle, display: 'flex', flexDirection: 'column' };
        }

        // FAB/header 不再各自判断"颜色确不确定"——上面 `!configReady ||
        // !colorKnown` 那道闸已经是这条渲染路径唯一的品牌色门控，能执行到这里
        // 就已经保证 colorKnown === true，theme.mainColor 是确定的正确答案
        // （customizations/缓存/服务端确认三选一，不会是"猜的"工厂色）。
        return (
            <React.Fragment>
            <div style={wrapperStyle} ref={this._panelRef}>

                {/* Open/close button */}
                {(isMobile || theme.alwaysUseFloatingButton || theme.closedStyle === 'button') && !isChatOpen ?
                    (
                    <div style={this.state.launcherHidden ? { display: 'none' } : undefined}>
                        {/* size：dash 的"按钮大小"(widget_config.button_size)。在此之前
                            这个值一路映射到 theme.buttonSize 就断了，没有任何地方读它，
                            按钮恒为 56——商户拖完滑块只有 dash 预览会动。 */}
                        <ChatFloatingButton color={this.state.colorKnown ? theme.mainColor : neutralLauncherColor(this._isDarkActive())} size={theme.buttonSize} iconStyle={theme.launcherIconStyle} iconColor={theme.launcherIconColor} imageUrl={theme.launcherImageUrl} onClick={this.onClickBtn} unreadCount={this.state.unreadBadgeCount} />
                    </div>)
                    :
                    (theme.closedStyle === 'chat' || isChatOpen || this.wasChatOpened()) ?
                        (theme.headerMode === 'large' ? this.renderLargeHeader() : theme.headerMode === 'compact' ? this.renderCompactHeader() : this.renderDefaultHeader())
                        :
                        <ChatTitleMsg onClick={this.onClick} conf={theme} />
                }

                {/* Teaser bubble — driven by Flow's show_teaser action (or dev fallback） */}
                {!isChatOpen && teaserVisible && this.state.teaserPayload && this.state.teaserPayload.message && (
                    // A Flow-pushed teaser (has trace_id) is intentional — show it in
                    // any floating closed style. The button-only gate stays for the
                    // dev-config fallback so it can't clutter the "chat" closed prompt.
                    (isMobile || theme.alwaysUseFloatingButton || theme.closedStyle === 'button' || this.state.teaserPayload.trace_id) && (
                        <ChatTeaserBubble
                            message={this.state.teaserPayload.message}
                            title={this.state.teaserPayload.title}
                            avatarUrl={this.state.teaserPayload.avatar_url}
                            color={theme.mainColor}
                            isMobile={isMobile}
                            onClick={this.handleTeaserClick}
                            onDismiss={this.handleTeaserDismiss}
                        />
                    )
                )}

                {this.renderLauncherExtras(isMobile)}

                {/* 初始化失败错误页：浮动面板打开时，盖在 Chat/Know/Home 内容之上
                   （absolute inset:0，见 renderInitError 的非 inline 分支），不用
                   逐个改下面三块内容 div 各自的 display 条件。面板关着(FAB 态)时
                   不受影响——错误不该拦住整个浮球，只拦"打开后看到的内容"。 */}
                {isChatOpen && this.state.initErrorKind && this.renderInitError({ inline: false })}

                {/* Chat IFrame */}
                <div style={{
                    display: isChatOpen && widgetClass === 'chat-widget' ? 'block' : 'none',
                    ...contentHeightStyle,
                    ...this._viewAnimStyle('chat-widget'),
                }}
                className="chat-widget"
                >
                    {(!pristine || chatInitialized) && <ChatFrame ref={this.chatFrameRef} {...this.props} theme={theme} initialViewMode={this._chatInitialViewMode} onViewModeChange={this.handleChatViewModeChange} preInitData={this._preInitData} preInitPromise={this._preInitPromise} />}
                </div>

                {/* Knowledge Component —— 高度与 chat 同一来源（统一尺寸模型） */}
                <div style={{
                    display: isChatOpen && widgetClass === 'know-widget' ? 'block' : 'none',
                    ...contentHeightStyle,
                    ...this._viewAnimStyle('know-widget'),
                }}
                className="know-widget"
                >
                    {(!pristine || knowInitialized) && <KnowFrame ref={this.knowFrameRef} {...this.props} theme={theme} appConfig={this.state.appConfig} onViewChange={this.handleKnowViewChange} initialArticle={this._knowInitialArticle} initialReturnTo={this._knowReturnTo} onExit={this.handleKnowExit} kbLayoutMode={this._kbLayoutMode()} />}
                </div>

                {/* Home View */}
                <div style={{
                    display: isChatOpen && widgetClass === 'home-widget' ? 'flex' : 'none',
                    ...contentHeightStyle,
                    flexDirection: 'column',
                    ...this._viewAnimStyle('home-widget'),
                }} className="home-widget">
                    {homeInitialized && <HomeView
                        appId={conf.app_id || conf.appId}
                        theme={theme}
                        headerHeight={this.getHeaderHeight()}
                        appConfig={this.state.appConfig}
                        visitorId={this.visitorId}
                        onNavigate={this.handleHomeNavigate}
                    />}
                </div>

                {/* Game IFrame - 覆盖整个widget包括header */}
                {showGame && gameUrl && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'var(--lc-surface, #fff)',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            height: '40px',
                            background: theme.mainColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 15px',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}>
                            <span>{t('nav.game_center')}</span>
                            <button 
                                onClick={this.closeGame}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <LcIcon name="close" size={14} />
                            </button>
                        </div>
                        <iframe
                            src={gameUrl}
                            style={{
                                flex: 1,
                                width: '100%',
                                border: 'none'
                            }}
                            title="Game"
                        />
                    </div>
                )}

                {/* 头部弹层（语言 / 设置）：挂在面板 wrapper 上，header 有 overflow:hidden */}
                {this.renderHeaderPopover()}

            </div>
            {/* FAB 关闭按钮 —— 放在动画 wrapper *之外*。它是 position:fixed；
               若留在被 animation/transform 的 wrapper 内，wrapper 会成为 fixed 的
               包含块，开场动画结束时按钮“弹回”视口坐标 → 抖动。移到外层后始终相对
               视口固定，不受面板入场动画影响，无抖动。 */}
            {isChatOpen && theme.closedStyle === 'button' && !isMobile && !this._fabCloseSqueezed && (
                <button
                    style={{
                        ...fabCloseButtonStyle,
                        background: theme.mainColor || 'rgba(0, 0, 0, 0.6)',
                        bottom: theme.btnPcBottom || '20px',
                        right: theme.btnPcRight || '20px',
                    }}
                    onClick={this.onClickClose}
                    onMouseEnter={(e) => { e.target.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
                    aria-label={t('common.close')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="white"/>
                    </svg>
                </button>
            )}
            </React.Fragment>
        );
    }


    onSwitch = () => {
        debugLog("onSwitch")
    }

    onClickClose = (e) => {
        // 检查事件对象是否存在
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        // 平板"闪闭"根因修复：desktop 'button' 分支下关闭钮跟 FAB 同位置/同尺寸，
        // 触屏打开面板后浏览器补发的兼容合成 click 会落在刚换上来的关闭钮上（详见
        // onClickBtn 里的时间戳记录处的完整分析）。500ms 窗口内、且当前确实处于
        // 会产生这种同位重叠的分支（!isMobile && closedStyle==='button'）时，
        // 把这次点击当成同一次触摸的尾巴吞掉，不当作用户主动点了关闭。
        // 真实的二次快速点击关闭（同一位置、500ms 内）本就是小概率场景，容许延后。
        const mobileProp = this.props.isMobile === true || this.props.isMobile === 'true';
        if (this._lastFabTouchOpenAt
            && !mobileProp
            && this.state.theme && this.state.theme.closedStyle === 'button'
            && (Date.now() - this._lastFabTouchOpenAt) < 500) {
            return;
        }
        // Native container (Android/iOS/Flutter WebView host, fullscreen/inline):
        // there's no floating button to collapse back to, so _requestClose() is a
        // guaranteed no-op in inline mode (see its own `laneChatMode === 'inline'`
        // early return) — clicking ✕ would do literally nothing. The native shell
        // is the only thing that can actually dismiss the screen.
        //
        // 真机反馈("iOS 点 ✕ 无反应")查出来的根因：这里过去只发了 EVENT 'closed'
        // （经 window.laneChat._emit('close') → bridgeName 映射），从没发过
        // SDK_BRIDGE_PROTOCOL.md §4 widget→native·call 表里登记的 CALL 'close'。
        // 两者语义完全不同——'closed' 是"widget 自己的开合状态变了"这一被动
        // 通知（native 侧只是转给宿主 App 自己的回调，WidgetEvent.Closed，不驱动
        // 任何 UI）；'close' 才是"请你把这屏幕关掉"的主动指令，iOS
        // LaneChatWebView.swift 的 handleClose()、Android BridgeProtocol.kt 的
        // `"close" -> host.close()` 都挂在这个 CALL 上，不是那个 EVENT。
        // Android 这条缺口一直被 ChatCloseSynthesis.kt 兜底掩盖（检测到"从没收到
        // 真正的 close" 就自己合成一个）；iOS 没有等价兜底，全面屏沉浸式模式下
        // ✕ 又是唯一退出入口，缺口直接变成"点了没反应"。EVENT 照发不动（其它
        // 消费方可能依赖它做状态同步），额外把真正驱动关闭的 CALL 发出去。
        if (hasNativeTransport()) {
            if (window.laneChat && window.laneChat._emit) {
                window.laneChat._emit('close');
            }
            nativeBridge.call('close', {});
            return;
        }
        // Inline / full-page host: never collapse the panel (it's the whole page).
        // 出场动画 + 真正关闭统一走 _requestClose。
        this._requestClose();
    }

    onClick = (e) => {
        //window.console.log(e);
        // window.console.log("click ing");

        // 检查事件对象是否存在（可能从 API 调用如 toggleChat 中没有传递）
        if (!e || !e.target) {
            // 如果没有事件对象，简单切换聊天状态；关闭走出场动画收敛点
            if (this.state.isChatOpen) {
                this._requestClose();
                return;
            }
            let stateData = {
                pristine: false,
                isChatOpen: true,
            };
            if (!this.wasChatOpened()) {
                this.setCookie();
                stateData.wasChatOpened = true;
            }
            if (this._isMounted) {
                this.setState(stateData);
            }
            return;
        }

        // 获取点击元素的类名
        const className = e.target.className;
        const tagName = e.target.tagName;
        let dataType = e.target.dataset.type;

        // （曾经这里有一条 `.close` closest() 分支，命中就把面板开合切换一次——
        // 排查这次"header 看起来能点、其实是陷阱"的问题时确认它是死代码：header
        // 渲染树里没有任何元素带字面 className="close"，真正的关闭按钮走的是
        // closeButtonStyle 的内联样式 + 自己的 onClick={onClickClose}，从不依赖
        // 这条委托触发。删掉而不是留着——即使从未真正触发过，继续留着也是这次
        // 要清掉的"header 区域点击=关闭"这一类问题的同伙，不该留作误导。）

        const closestDataTypeElement = e.target.closest('[data-type]');
        //window.console.log("closestDataTypeElement", closestDataTypeElement)
        if (closestDataTypeElement) {
            dataType = closestDataTypeElement.getAttribute('data-type');
        }

        // 检查是否点击在聊天/知识按钮区域
        const clickedInButtonArea = e.target.closest('.chat-header-sub');
        if (clickedInButtonArea) {
            // 如果点击在按钮区域，只处理切换，不关闭对话框
            if (e.stopPropagation) e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
        }


            if(e.target.closest('.chat-small') || e.target.closest('.chat-header-sub')){
            if(dataType === 'home'){
                // tab 行最左的 home 图标 —— 常驻回归入口（见 renderTabs 注释）。
                if (e.stopPropagation) e.stopPropagation();
                if (e.preventDefault) e.preventDefault();
                this.switchToHome();
                return;
            }
            if(dataType === 'know'){
                if (e.stopPropagation) e.stopPropagation(); // 阻止事件冒泡
                if (e.preventDefault) e.preventDefault(); // 阻止默认行为
                // 走 switchToKnowledge 而不是就地 setState —— 这里以前是同一段
                // 视图切换逻辑的第二份拷贝，返回导航要记来路后就必须收敛成一条
                // 路径，否则 tab 进的知识库没有来路、返回会误判成关闭。
                this.switchToKnowledge();
                debugLog("know");
                return;
            }
            if(dataType == 'chat'){
                if (e.stopPropagation) e.stopPropagation(); // 阻止事件冒泡
                if (e.preventDefault) e.preventDefault(); // 阻止默认行为
                this.switchToChat();
                debugLog("chat");
                return;
            }
            // window.console.log(this.state.chatProps);
            return;
        }

        // 不再点击header区域关闭对话框，移除该逻辑
    }



    onClickBtn = (e) => {
        //window.console.log(e);
        // window.console.log("click ing");

        // 检查事件对象是否存在
        if (!e || !e.target) {
            // 如果没有事件对象，简单打开聊天
            this.openChat();
            return;
        }

        // 获取点击元素的类名
        const className = e.target.className;
        const tagName = e.target.tagName;
        debugLog("Clicked element class name:", className);
        const dataType = e.target.dataset.type;
        debugLog("Clicked element data-type:", dataType);

        debugLog("a", knowSrc, chatSrc)
        if(className == "chat-svg" || className == "chat-small" || className == 'chat-header-sub'){
            // tab 之间是**平级**切换,不产生来路(lateral:true)。此前走的是压栈
            // 路径,于是"知识库 tab → Chat tab"会在栈里留下 KNOWLEDGE,会话里的
            // 返回就落到知识库 —— 而访客只是用 tab 换了个地方看。
            if(dataType == 'know'){
                // 同 onClickHeader 的 know 分支：收敛到 switchToKnowledge，
                // 让来路记录只有一个写入点。
                this.switchToKnowledge({ lateral: true });
            }
            if(dataType == 'chat'){
                this.switchToChat({ lateral: true });
            }
            return;
        }

        // 触屏 tap 打开：记时间戳。平板（isMobile 按 screen.width 判成桌面）+
        // closedStyle==='button' 分支下，关闭钮 fabCloseButtonStyle 跟 FAB 用的是
        // 同一份 bottom/right/56×56——touchend 一开面板，FAB 原地换成关闭钮；随后
        // 浏览器为兼容纯 click 站点补发的合成 click（touchend 后几十~几百 ms，为兼容
        // 而按当前 DOM 重新命中测试，不认原 target）恰好落在刚换上来的关闭钮上，
        // 面板"打开后自动关闭"。桌面鼠标点击不会补发这个合成 click，不受影响。
        // 已用无头浏览器分步复现验证（真实 touchend→click 序列，而非猜测）。
        // onClickClose 据此时间戳吞掉这次尾随点击，见那边注释。
        if (e.type === 'touchend') {
            this._lastFabTouchOpenAt = Date.now();
        }

        // 点击浮动按钮打开对话框
        let stateData = {
            pristine: false,
            isChatOpen: true, // 点击浮动按钮总是打开
        };
        if (!this.wasChatOpened()) {
            this.setCookie();
            stateData.wasChatOpened = true;
        }
        if (this._isMounted) {
            this.setState(stateData);
        }
    }


    setCookie = () => {
        let date = new Date();
        let expirationTime = parseInt(this.props.conf.cookieExpiration);
        date.setTime(date.getTime() + (expirationTime * 24 * 60 * 60 * 1000));
        let expires = "; expires=" + date.toGMTString();
        document.cookie = "chatwasopened=1" + expires + "; path=/";
    }

    getCookie = () => {
        let nameEQ = "chatwasopened=";
        let ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return false;
    }

    wasChatOpened = () => {
        return this.getCookie() !== false;
    }

    // "这个浏览器有没有缓存过一个 chat_id" —— resolveFirstVisitLandingView 的
    // hasCachedChat 输入。跟 chat.js Chat._getCachedChatId() 读的是完全同一个
    // localStorage 键（`lane_chatid_${appId}`，chat.js 在 _applyInitResponse /
    // switchToChat 等处写入）——不新建一个身份/缓存体系，复用现成的。
    // 接收 props 参数（而不是直接读 this.props）：构造函数里调用这个方法时
    // `this.props` 还没被 React 赋值（class field 初始化跑在 super() 之后、
    // 显式构造函数体之前，但 this.props 要等 Component 基类的 super(props)
    // 执行完才有——为了在构造函数里也能同步拿到确定结果，显式传参，不依赖
    // this.props 这时候是否已经就绪）。
    _hasCachedInitialChat = (props) => {
        try {
            const appId = (props || this.props).conf.app_id;
            return !!(appId && localStorage.getItem(`lane_chatid_${appId}`));
        } catch (e) { return false; }
    }

    // 首次访问落地视图的"配置到达后再判一次"消费点——preInitClient 的早期上色
    // （_applyPendingEarlyTheme）和 /usage/start 的完整配置（applyConfig）到达时
    // 都调这同一个方法，跟 mainColor/headerGradient 那套"两条下发路径共用同一份
    // 判定"的纪律一致。nextTheme 是"即将生效"的完整 theme（已经合并了这次更新
    // 的增量），不是 this.state.theme 那份旧值——homePageEnabled/initialView
    // 很可能就是这次调用才第一次有值。
    //
    // _explicitViewTargeted 一旦被标记（navigate-to-chat / native-bridge
    // navigate / teaser 点击，见 switchToChat/switchToHome/navigateHome 顶部
    // 的赋值）就永远不再自动落地——这些都是比"首次访问默认值"优先级更高的显式
    // 意图，配置数据比它们晚到不该把用户已经导向的视图又拽回去。
    //
    // 返回可以直接合进 setState 的增量对象；不需要动 widgetClass 时返回 null。
    _maybeAutoLandOnFirstVisit = (nextTheme) => {
        if (this._explicitViewTargeted) return null;
        if (this.state.widgetClass === 'home-widget') return null; // 已经是 home，不用再判
        const landing = resolveFirstVisitLandingView({
            hasExplicitChatTarget: !!(this.props.initialChatId || this.props.forceChatView),
            hasCachedChat: this._hasCachedInitialChat(),
            homePageEnabled: nextTheme.homePageEnabled,
            initialView: nextTheme.initialView,
        });
        if (landing !== 'home-widget') return null;
        // 自动落地 = 重新确定"起点"，不是一次导航——nav 用 navInit 清空来路，
        // 否则会把配置到达前那一瞬的 chat 视图记成 Home 的来路。
        return { widgetClass: 'home-widget', homeInitialized: true, nav: navInit(NAV_HOME) };
    }

    scrollChatToBottom = () => {
        // 延迟执行确保DOM已更新
        setTimeout(() => {
            if (this.chatFrameRef.current && this.chatFrameRef.current.scrollToBottom) {
                this.chatFrameRef.current.scrollToBottom();
            }
        }, 100);
    }
}
